import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  PhotoIcon,
  UserIcon,
  SparklesIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { api } from '../services/api';
import { obfuscate } from '../utils/obfuscate';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { saveAs } from 'file-saver';

// Helper to resolve image URLs - routes /uploads/ paths through the automation service
const resolveImageUrl = (url, automationUrl) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/uploads/') && automationUrl) return `${automationUrl}${url}`;
  return url;
};

/** Prefer presigned displayUrl (private S3) when API provides it. */
const resolveAssetUrl = (canonicalUrl, displayUrl, automationUrl) => {
  if (displayUrl) return displayUrl;
  return resolveImageUrl(canonicalUrl, automationUrl);
};

/** Small batches avoid net::ERR_HTTP2_PROTOCOL_ERROR on large multipart uploads (Railway / reverse proxies). */
const BIO_BULK_IMAGE_BATCH_SIZE = 10;
const BIO_BULK_UPLOAD_TIMEOUT_MS = 180000;

const getPlatformDisplayName = (platformType, customPlatformName = '') => {
  if (platformType === 'custom' && customPlatformName && customPlatformName.trim()) {
    return customPlatformName.trim().toLowerCase();
  }
  const map = {
    fanvue: 'fanvue',
    onlyfans: 'onlyfans',
    fansly: 'fansly',
  };
  return map[platformType] || 'fanvue';
};

const getDefaultExitMessage = (platformType, customPlatformName = '') =>
  `I'm moving to ${getPlatformDisplayName(platformType, customPlatformName)} rn, send me a msg there.. ill be waiting`;

const isAutoExitMessageForPlatform = (message, platformType, customPlatformName = '') =>
  (message || '').trim() === getDefaultExitMessage(platformType, customPlatformName);

const Models = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [bypassValidation, setBypassValidation] = useState(false);
  const bypassValidationRef = useRef(false);
  const [presets, setPresets] = useState([]);
  const modalTabSetterRef = useRef(null);
  const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modelFilter, setModelFilter] = useState('all'); // 'all', 'active', 'draft'
  const [serviceUrls, setServiceUrls] = useState({});
  const [scrapeCategories, setScrapeCategories] = useState([]);
  
  // Load service URLs for routing file uploads to the correct service
  useEffect(() => {
    const loadServiceUrls = async () => {
      try {
        const response = await api.get('/config/services');
        setServiceUrls(response.data || {});
      } catch (error) {
        console.error('[Models] Error loading service URLs:', error);
      }
    };
    loadServiceUrls();
  }, []);
  
  // Fetch presets list
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await api.get('/models/presets/list');
        setPresets(response.data);
      } catch (error) {
        console.error('Error fetching presets:', error);
      }
    };
    fetchPresets();
  }, []);
  
  // Helper function to get preset name from preset key
  const getPresetName = (presetKey) => {
    if (!presetKey) return 'Not set';
    const preset = presets.find(p => p.key === presetKey);
    return preset ? preset.name : presetKey;
  };

  // Function to apply preset data to form
  const applyPresetToForm = async (presetKey) => {
    try {
      const response = await api.get(`/models/presets/${presetKey}`);
      const preset = response.data;
      
      // Apply preset to formData
      if (preset.modelInfo) {
        setFormData(prev => ({ ...prev, modelInfo: preset.modelInfo }));
      }
      
      if (preset.interactionStyle) {
        setFormData(prev => ({
          ...prev,
          ctaInfo: { ...prev.ctaInfo, interactionStyle: preset.interactionStyle }
        }));
      }
      
      // Apply day and night time activities
      if (preset.dayTimeActivity) {
        setFormData(prev => ({ ...prev, dayTimeActivity: preset.dayTimeActivity }));
      }
      
      if (preset.nightTimeActivity) {
        setFormData(prev => ({ ...prev, nightTimeActivity: preset.nightTimeActivity }));
      }
      
      // Apply no-response follow-up messages
      if (preset.noResponseFollowUp && preset.noResponseFollowUp.messages) {
        const followUpMessages = preset.noResponseFollowUp.messages.map((msg) => ({
          messageText: msg,
          analytics: {
            timesSent: 0,
            responses: 0,
            responseRate: 0,
            lastUsed: null
          }
        }));
        setFormData(prev => ({
          ...prev,
          globalSettings: {
            ...prev.globalSettings,
            noResponseFollowUp: { messages: followUpMessages }
          }
        }));
      }
      
      // Apply personality adjustments and live photo rates to phases
      // Also set default minimum exchanges for all presets
      if (preset.personalityAdjustments || preset.livePhotoRates) {
        // Update initial phase
        const initialPhaseUpdates = {};
        if (preset.personalityAdjustments?.initial) {
          initialPhaseUpdates.personalityAdjustment = preset.personalityAdjustments.initial;
        }
        if (preset.livePhotoRates?.initial !== undefined) {
          initialPhaseUpdates.livePhotoRate = preset.livePhotoRates.initial;
        }
        // Set default minimum exchanges for initial phase (all presets)
        initialPhaseUpdates.minimumExchanges = 4;
        
        // Determine how many phases this preset needs
        let numberOfPhases = 2; // Default
        if (presetKey === 'simp_converter') {
          numberOfPhases = 5; // simp_converter needs 5 phases
        }
        
        // Update phases - create/update phases 1-5 for simp_converter, 1-2 for others
        setFormData(prev => {
          const updatedPhases = [...prev.conversationPhases.phases];
          
          // Ensure we have enough phases
          while (updatedPhases.length < numberOfPhases) {
            updatedPhases.push({
              phaseNumber: updatedPhases.length + 1,
              name: `Phase ${updatedPhases.length + 1}`,
              interestLevel: 'medium',
              livePhotoRate: 30,
              personalityAdjustment: '',
              minimumExchanges: 4
            });
          }
          
          // Update phase 1
          if (updatedPhases.length > 0) {
            const phase1Updates = {};
            if (preset.personalityAdjustments?.phase1) {
              phase1Updates.personalityAdjustment = preset.personalityAdjustments.phase1;
            }
            if (preset.livePhotoRates?.phase1 !== undefined) {
              phase1Updates.livePhotoRate = preset.livePhotoRates.phase1;
            }
            phase1Updates.minimumExchanges = 5;
            phase1Updates.name = preset.phase1?.description?.split(' - ')[0] || 'Getting to Know You';
            phase1Updates.interestLevel = 'medium';
            updatedPhases[0] = { ...updatedPhases[0], ...phase1Updates };
          }
          
          // Update phase 2
          if (updatedPhases.length > 1) {
            const phase2Updates = {};
            if (preset.personalityAdjustments?.phase2) {
              phase2Updates.personalityAdjustment = preset.personalityAdjustments.phase2;
            }
            if (preset.livePhotoRates?.phase2 !== undefined) {
              phase2Updates.livePhotoRate = preset.livePhotoRates.phase2;
            }
            phase2Updates.minimumExchanges = 4;
            phase2Updates.name = preset.phase2?.description?.split(' - ')[0] || 'Playful Flirt';
            phase2Updates.interestLevel = 'medium';
            updatedPhases[1] = { ...updatedPhases[1], ...phase2Updates };
          }
          
          // Update phase 3 (for simp_converter)
          if (numberOfPhases >= 3 && updatedPhases.length > 2 && preset.personalityAdjustments?.phase3) {
            const phase3Updates = {};
            phase3Updates.personalityAdjustment = preset.personalityAdjustments.phase3;
            if (preset.livePhotoRates?.phase3 !== undefined) {
              phase3Updates.livePhotoRate = preset.livePhotoRates.phase3;
            }
            phase3Updates.minimumExchanges = 4;
            phase3Updates.name = preset.phase3?.description?.split(' - ')[0] || 'Heat Check + Boundaries';
            phase3Updates.interestLevel = 'high';
            updatedPhases[2] = { ...updatedPhases[2], ...phase3Updates };
          }
          
          // Update phase 4 (for simp_converter)
          if (numberOfPhases >= 4 && updatedPhases.length > 3 && preset.personalityAdjustments?.phase4) {
            const phase4Updates = {};
            phase4Updates.personalityAdjustment = preset.personalityAdjustments.phase4;
            if (preset.livePhotoRates?.phase4 !== undefined) {
              phase4Updates.livePhotoRate = preset.livePhotoRates.phase4;
            }
            phase4Updates.minimumExchanges = 3;
            phase4Updates.name = preset.phase4?.description?.split(' - ')[0] || 'CTA Seed';
            phase4Updates.interestLevel = 'high';
            updatedPhases[3] = { ...updatedPhases[3], ...phase4Updates };
          }
          
          // Update phase 5 (for simp_converter)
          if (numberOfPhases >= 5 && updatedPhases.length > 4 && preset.personalityAdjustments?.phase5) {
            const phase5Updates = {};
            phase5Updates.personalityAdjustment = preset.personalityAdjustments.phase5;
            if (preset.livePhotoRates?.phase5 !== undefined) {
              phase5Updates.livePhotoRate = preset.livePhotoRates.phase5;
            }
            phase5Updates.minimumExchanges = 2;
            phase5Updates.name = preset.phase5?.description?.split(' - ')[0] || 'CTA Close';
            phase5Updates.interestLevel = 'high';
            updatedPhases[4] = { ...updatedPhases[4], ...phase5Updates };
          }
          
          // Calculate total minimum exchanges for CTA
          // Use the updated initial phase minimum exchanges (4) plus all phase minimum exchanges
          const initialPhaseExchanges = initialPhaseUpdates.minimumExchanges || 4;
          let totalExchanges = initialPhaseExchanges;
          for (let i = 0; i < numberOfPhases && i < updatedPhases.length; i++) {
            totalExchanges += updatedPhases[i]?.minimumExchanges || 3;
          }
          
          return {
            ...prev,
            conversationPhases: {
              ...prev.conversationPhases,
              initialPhase: {
                ...prev.conversationPhases.initialPhase,
                ...initialPhaseUpdates
              },
              numberOfPhases: numberOfPhases,
              phases: updatedPhases.slice(0, numberOfPhases) // Only keep the phases we need
            },
            ctaPhase: {
              ...prev.ctaPhase,
              minimumExchangesBeforeCTA: totalExchanges
            }
          };
        });
      } else {
        // Even if no personality adjustments or live photo rates, set default minimum exchanges
        setFormData(prev => ({
          ...prev,
          conversationPhases: {
            ...prev.conversationPhases,
            initialPhase: {
              ...prev.conversationPhases.initialPhase,
              minimumExchanges: 4
            },
            phases: prev.conversationPhases.phases.map((phase, index) => ({
              ...phase,
              minimumExchanges: index === 0 ? 5 : 6
            }))
          }
        }));
      }
      
      // Apply CTA scripts
      if (preset.ctaScripts && preset.ctaScripts.length > 0) {
        const ctaScript = preset.ctaScripts[0];
        const ctaMessages = ctaScript.messages.map((msg, index) => ({
          messageNumber: index + 1,
          spintax: msg.spintax,
          aiAugmentation: msg.aiAugmentation !== undefined ? msg.aiAugmentation : true,
          includeLivePhoto: msg.includeLivePhoto !== undefined ? msg.includeLivePhoto : false,
          photoCategory: 'cta'
        }));
        
        setFormData(prev => ({
          ...prev,
          ctaPhase: {
            ...prev.ctaPhase,
            scripts: [{
              scriptNumber: 1,
              name: ctaScript.name || 'Preset Script',
              isActive: true,
              messages: ctaMessages,
              successRate: 0,
              totalUses: 0
            }]
          }
        }));
      }
      
      // Apply post-CTA phases for simp_converter preset
      if (presetKey === 'simp_converter' && preset.phase6 && preset.phase7) {
        const phase6Name = preset.phase6.description ? preset.phase6.description.split(' - ')[0] : 'Post-CTA Heat Build 1';
        const phase7Name = preset.phase7.description ? preset.phase7.description.split(' - ')[0] : 'Post-CTA Heat Build 2';
        
        const postCTAPhases = [
          {
            phaseNumber: 6,
            name: phase6Name,
            personalityAdjustment: preset.personalityAdjustments?.phase6 || '',
            livePhotoRate: preset.livePhotoRates?.phase6 || 40,
            minimumExchanges: preset.phase6?.minimumExchanges || 4,
            interestLevel: 'very_high'
          },
          {
            phaseNumber: 7,
            name: phase7Name,
            personalityAdjustment: preset.personalityAdjustments?.phase7 || '',
            livePhotoRate: preset.livePhotoRates?.phase7 || 40,
            minimumExchanges: preset.phase7?.minimumExchanges || 4,
            interestLevel: 'very_high'
          }
        ];
        
        setFormData(prev => ({
          ...prev,
          ctaPhase: {
            ...prev.ctaPhase,
            enablePostCTAPhases: true,
            postCTAPhases: postCTAPhases
          }
        }));
      } else {
        // For other presets, ensure post-CTA phases are disabled
        setFormData(prev => ({
          ...prev,
          ctaPhase: {
            ...prev.ctaPhase,
            enablePostCTAPhases: false,
            postCTAPhases: []
          }
        }));
      }
      
      // Apply CTA Content Description based on preset
      const contentDescriptions = {
        'shy_girl': 'my private photos and videos where i show my real, intimate side... i get so shy sharing them but i love feeling close to you 🥺',
        'naughty_girl': 'my wildest, naughtiest content... exclusive pics and videos where i let loose and show you my real, seductive side 😈',
        'girl_next_door': 'my private page where i share my everyday life... but also my spicy side that only special people get to see 😊',
        'dominant_girl': 'my exclusive content where i take control... private photos and videos where i show you who\'s really in charge 👑',
        'innocent_girl': 'my private page where i share cute, innocent photos... but sometimes i get a little naughty too 🙈',
        'gamer_girl': 'my gaming content and cosplay photos... plus some exclusive, spicy content for my special fans 🎮'
      };
      
      if (contentDescriptions[presetKey]) {
        setFormData(prev => ({
          ...prev,
          ctaInfo: {
            ...prev.ctaInfo,
            contentDescription: contentDescriptions[presetKey]
          }
        }));
      }
      
      // Apply Initial Phase Name based on preset
      const initialPhaseNames = {
        'shy_girl': 'Getting Comfortable',
        'naughty_girl': 'Breaking the Ice',
        'girl_next_door': 'First Contact',
        'dominant_girl': 'Establishing Connection',
        'innocent_girl': 'Sweet Introduction',
        'gamer_girl': 'Starting the Game',
        'simp_converter': 'Getting Comfortable'
      };
      
      if (initialPhaseNames[presetKey]) {
        setFormData(prev => ({
          ...prev,
          conversationPhases: {
            ...prev.conversationPhases,
            initialPhase: {
              ...prev.conversationPhases.initialPhase,
              name: initialPhaseNames[presetKey]
            }
          }
        }));
      }
      
      // Apply content description for simp_converter
      if (presetKey === 'simp_converter') {
        setFormData(prev => ({
          ...prev,
          ctaInfo: {
            ...prev.ctaInfo,
            contentDescription: 'my hottest, most explicit content... exclusive pics and videos where i show you everything and get super sexual. this is where the real fun happens 😈'
          }
        }));
      }
      
      return preset;
    } catch (error) {
      console.error('Error applying preset:', error);
      throw error;
    }
  };
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    age: 22,
    defaultPinCode: '1111',
    gender: 'female',
    chattingStyle: 'youthful',
    preset: 'shy_girl', // Default preset
    city: '',
    timezone: 'UTC',
    modelInfo: 'A flirty, playful college girl who loves to tease and be teased. She\'s naturally curious about guys and enjoys making them blush. She\'s not shy about being a little naughty but keeps it playful and fun. Personality traits: flirty, playful, teasing, curious, naughty, spontaneous, bubbly, sensitive.',
    personalityTraits: [],
    systemPrompt: '',
    hairColor: '',
    eyeColor: '',
    ethnicity: '',
    physicalDescription: '',
    platformType: 'fanvue',
    customPlatformName: '',
    ctaInfo: {
      fanvueLink: 'https://fanvue.com/your-model',
      snapchatLink: '',
      chatLinkUrl: '',
      primaryCategory: '',
      additionalLinks: [], // Array of additional CTA links for randomization
      monthlyPrice: 15,
      contentDescription: 'sexy pics, naughty videos, and super personal chats where i get to know you better 😘',
      activityLevel: 'daily',
      interactionStyle: 'i love teasing people and getting super personal with them... sometimes we even plan naughty stuff together 😉 i\'m always flirty and love making you blush'
    },
    dayTimeActivity: 'Cleaning my room and chilling at home.',
    nightTimeActivity: 'You are in your cozy bedroom, wearing your favorite pajamas and scrolling through social media.',
    language: 'en',
    detectLanguage: true,
    stronglyDeclineMeetups: true,
    conversationPhases: {
      numberOfPhases: 2,
      minimumExchangesPerPhase: 4,
      initialPhase: {
        name: 'Interest Building',
        interestLevel: 'medium',
        livePhotoRate: 10, // Updated default: 10% for initial phase
        personalityAdjustment: 'Be mysterious and flirty. Hint at exclusive content. Create curiosity.',
        minimumExchanges: 4 // Updated default: 4 exchanges
      },
      phases: [
        {
          phaseNumber: 1,
          name: 'Escalation',
          interestLevel: 'high',
          livePhotoRate: 20,
          personalityAdjustment: 'Get more personal and sexual. Tease about private content. Build tension.',
          minimumExchanges: 5 // Updated default: 5 exchanges
        },
        {
          phaseNumber: 2,
          name: 'Pre-CTA Push',
          interestLevel: 'very_high',
          livePhotoRate: 30,
          personalityAdjustment: 'Create urgency and desire. Hint at exclusive content. Build FOMO.',
          minimumExchanges: 6 // Updated default: 6 exchanges
        }
      ]
    },
    ctaPhase: {
      minimumExchangesBeforeCTA: 15, // Updated default: 15 exchanges
      enablePostCTAPhases: false,
      postCTAPhases: [],
      exitMessage: getDefaultExitMessage('fanvue', ''),
      scripts: [
        {
          scriptNumber: 1,
          name: 'Soft Approach',
          isActive: true,
          messages: [
            {
              messageNumber: 1,
              spintax: 'I really enjoy talking to you. you seem so sweet and interesting',
              aiAugmentation: false,
              includeLivePhoto: false,
              photoCategory: 'cta'
            },
            {
              messageNumber: 2,
              spintax: 'I share so much more on my page. you should check it out sometime',
              aiAugmentation: false,
              includeLivePhoto: true,
              photoCategory: 'cta'
            },
            {
              messageNumber: 3,
              spintax: 'I post daily. I\'d love to get to know you better there',
              aiAugmentation: false,
              includeLivePhoto: false,
              photoCategory: 'cta'
            }
          ],
          successRate: 0,
          totalUses: 0
        }
      ]
    },
    apiFollowUp: {
      enableForDm: false,
      beforeCta: {
        enabled: false,
        idleMinutes: 30,
        maxMessages: 3,
        analytics: { sent: 0, responses: 0, responseRate: 0 }
      },
      afterCta: {
        enabled: false,
        idleMinutes: 30,
        maxMessages: 3,
        reshareLinkOnRequest: true,
        analytics: { sent: 0, responses: 0, responseRate: 0 }
      }
    },
    followbackDm: { coldPhaseMinSends: 50, optimizedTopCount: 10, messages: [] },
    globalSettings: {
      responseDelay: {
        min: 2000,
        max: 8000
      },
      followUps: {
        enabled: true,
        hoursBeforeFollowUp: 24
      },
      noResponseFollowUp: {
        messages: []
      },
      delaysNotPresent: {
        afterOpeningConversation: 0,
        afterSendingMessage: 0,
        betweenMessageBubbles: 0,
        typingSpeed: 'instant'
      },
      delaysPresent: {
        afterOpeningConversation: 5,
        afterSendingMessage: 5,
        betweenMessageBubbles: 5,
        typingSpeed: 'slow'
      },
      messageFromAnotherAccountDelay: {
        enabled: true,
        daysDelay: 14
      },
      onlyScanFirst: 99999,
      dontMessageOlderThan: 7,
      dailyNewConversationLimit: 99999,
      dailyMessagingLimit: 99999,
      consumeOnly: 99999,
      typoRate: 20,
      maxWords: 12,
      priorityChatter: true,
      replyOnly: false,
      enhancedTexting: {
        enabled: false,
        style: 'casual',
        customAbbreviations: {},
        useLowercase: false,
        removePunctuation: false,
        addFillerWords: true
      }
    },
    photos: [],
    postingConfig: {
      enabled: false,
      postsPerDay: 1,
      texts: [],
      images: []
    },
    bioUpdateConfig: {
      profilePictures: [],
      banners: [],
      bios: [],
      names: []
    },
    aiSettings: {
      provider: 'google'
    }
  });

  useEffect(() => {
    fetchModels();
    (async () => {
      try {
        const res = await api.get('/scraper-v2/categories');
        if (res.data?.success && Array.isArray(res.data.categories)) {
          setScrapeCategories(res.data.categories.map((c) => c.name).filter(Boolean));
        }
      } catch (_) {
        /* Scraper V2 may be disabled — CTA tab still has built-in defaults */
      }
    })();
  }, []);

  // Load default preset's follow-up messages when form opens or preset changes
  useEffect(() => {
    const loadPresetFollowUpMessages = async () => {
      // Only load if form is open (add or edit modal) and preset is set
      if ((showAddModal || showEditModal) && formData.preset) {
        const currentMessages = formData.globalSettings?.noResponseFollowUp?.messages || [];
        
        // Always load for new models (showAddModal), or if messages are empty in edit mode
        if (showAddModal || (showEditModal && currentMessages.length === 0)) {
          try {
            const response = await api.get(`/models/presets/${formData.preset}`);
            const preset = response.data;
            
            // Apply no-response follow-up messages from preset
            if (preset.noResponseFollowUp && preset.noResponseFollowUp.messages && preset.noResponseFollowUp.messages.length > 0) {
              const followUpMessages = preset.noResponseFollowUp.messages.map((msg) => ({
                messageText: msg,
                analytics: {
                  timesSent: 0,
                  responses: 0,
                  responseRate: 0,
                  lastUsed: null
                }
              }));
              
              setFormData(prev => {
                // Only update if messages are actually different
                const prevMessages = prev.globalSettings?.noResponseFollowUp?.messages || [];
                const prevTexts = prevMessages.map(m => (typeof m === 'string' ? m : m.messageText) || '').filter(Boolean).sort().join('|');
                const newTexts = followUpMessages.map(m => m.messageText).sort().join('|');
                
                if (prevTexts !== newTexts) {
                  return {
                    ...prev,
                    globalSettings: {
                      ...prev.globalSettings,
                      noResponseFollowUp: { messages: followUpMessages }
                    }
                  };
                }
                return prev;
              });
            } else {
            }
          } catch (error) {
            console.error('[Follow-up Messages] Error loading preset follow-up messages:', error);
          }
        } else {
        }
      } else {
      }
    };
    
    // Load immediately when modal opens or preset changes
    // Use a small delay to ensure formData is ready
    const timer = setTimeout(() => {
      loadPresetFollowUpMessages();
    }, 100);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal, showEditModal, formData.preset]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/models');
      setModels(response.data);
    } catch (error) {
      toast.error('Failed to fetch models');
      console.error('Error fetching models:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save model as draft when opening add modal
  const handleOpenAddModal = async () => {
    try {
      // Check if we already have a draft in formData
      if (formData._id && formData.status === 'draft') {
        // Already have a draft, open in edit mode
        setEditingModel(formData);
        setShowEditModal(true);
        setShowAddModal(false);
        return;
      }
      
      // Reset form first
      resetForm();
      
      // Apply 'shy_girl' preset and wait for it to complete
      await applyPresetToForm('shy_girl');
      
      // Use a callback approach to get updated formData after preset is applied
      // We'll create the draft with the preset values directly
      const response = await api.get('/models/presets/shy_girl');
      const preset = response.data;
      
      // Build draft data with preset values
      const draftData = {
        name: 'Untitled Model',
        displayName: '',
        age: 22,
        gender: 'female',
        chattingStyle: 'youthful',
        city: '',
        timezone: 'UTC',
        modelInfo: preset.modelInfo || '',
        personalityTraits: [],
        platformType: 'fanvue',
        customPlatformName: '',
        language: 'en',
        detectLanguage: true,
        stronglyDeclineMeetups: true,
        preset: 'shy_girl',
        status: 'draft',
        dayTimeActivity: preset.dayTimeActivity || '',
        nightTimeActivity: preset.nightTimeActivity || '',
        ctaInfo: {
          fanvueLink: 'https://fanvue.com/',
          snapchatLink: '',
          chatLinkUrl: '',
          additionalLinks: [],
          isFree: false,
          monthlyPrice: 15,
          contentDescription: preset.contentDescription || 'my private photos and videos where i show my real, intimate side... i get so shy sharing them but i love feeling close to you 🥺',
          activityLevel: 'daily',
          interactionStyle: preset.interactionStyle || ''
        },
        conversationPhases: {
          numberOfPhases: 2,
          minimumExchangesPerPhase: 4,
          initialPhase: {
            name: 'Getting Comfortable',
            interestLevel: 'medium',
            livePhotoRate: 0,
            personalityAdjustment: preset.personalityAdjustments?.initial || '',
            minimumExchanges: 4
          },
          phases: [
            {
              phaseNumber: 1,
              name: 'Getting to Know You',
              interestLevel: 'medium',
              livePhotoRate: preset.livePhotoRates?.phase1 || 30,
              personalityAdjustment: preset.personalityAdjustments?.phase1 || '',
              minimumExchanges: 5
            },
            {
              phaseNumber: 2,
              name: 'Playful Flirt',
              interestLevel: 'medium',
              livePhotoRate: preset.livePhotoRates?.phase2 || 30,
              personalityAdjustment: preset.personalityAdjustments?.phase2 || '',
              minimumExchanges: 4
            }
          ]
        },
        ctaPhase: {
          minimumExchangesBeforeCTA: 13,
          scripts: preset.ctaScripts && preset.ctaScripts.length > 0 ? [{
            scriptNumber: 1,
            name: preset.ctaScripts[0].name || 'Preset Script',
            isActive: true,
            messages: preset.ctaScripts[0].messages.map((msg, index) => ({
              messageNumber: index + 1,
              spintax: msg.spintax,
              aiAugmentation: msg.aiAugmentation !== undefined ? msg.aiAugmentation : true,
              includeLivePhoto: msg.includeLivePhoto !== undefined ? msg.includeLivePhoto : false,
              photoCategory: 'cta'
            })),
            successRate: 0,
            totalUses: 0
          }] : [],
          enablePostCTAPhases: false,
          postCTAPhases: [],
          exitMessage: getDefaultExitMessage('fanvue', '')
        },
        globalSettings: {
          noResponseFollowUp: {
            messages: preset.noResponseFollowUp?.messages?.map((msg) => ({
              messageText: msg,
              analytics: {
                timesSent: 0,
                responses: 0,
                responseRate: 0,
                lastUsed: null
              }
            })) || []
          }
        }
      };
      
      const draftResponse = await api.post('/models', draftData);
      const draftModel = draftResponse.data;
      
      // Apply preset to formData and then open edit modal
      await applyPresetToForm('shy_girl');
      
      // Use openEditModal to properly set up the form data
      await openEditModal(draftModel);
      fetchModels(); // Refresh models list to show the new draft
      toast.success('Model saved as draft with Shy girl preset. You can now add photos.');
    } catch (error) {
      console.error('Error creating draft model:', error);
      toast.error('Failed to create draft model. Opening modal anyway.');
      setShowAddModal(true);
      setShowEditModal(false);
    }
  };

  // Validation function
  const validateModelForm = (data) => {
    const errors = [];
    const warnings = [];
    const errorsByTab = {
      basic: [],
      cta: [],
      phases: [],
      ctaPhase: [],
      photos: []
    };

    // Basic Information Tab
    if (!data.name || data.name.trim() === '') {
      errors.push({ field: 'Name', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('Name');
    }
    if (!data.age || data.age < 18 || data.age > 99) {
      errors.push({ field: 'Age', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('Age');
    }
    if (!data.modelInfo || data.modelInfo.trim() === '') {
      errors.push({ field: 'Model Info', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('Model Info');
    }
    if (!data.city || data.city.trim() === '') {
      errors.push({ field: 'City', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('City');
    }
    if (!data.dayTimeActivity || data.dayTimeActivity.trim() === '') {
      errors.push({ field: 'Day Time Activity', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('Day Time Activity');
    }
    if (!data.nightTimeActivity || data.nightTimeActivity.trim() === '') {
      errors.push({ field: 'Night Time Activity', tab: 'basic', tabName: 'Basic Information' });
      errorsByTab.basic.push('Night Time Activity');
    }

    // CTA Information Tab
    if (!data.ctaInfo?.fanvueLink || data.ctaInfo.fanvueLink.trim() === '') {
      errors.push({ field: 'Link', tab: 'cta', tabName: 'CTA Information' });
      errorsByTab.cta.push('Link');
    }
    // Monthly Price is required if not free (isFree is not explicitly true)
    if (data.ctaInfo?.isFree !== true && (!data.ctaInfo?.monthlyPrice || data.ctaInfo.monthlyPrice <= 0)) {
      errors.push({ field: 'Monthly Price', tab: 'cta', tabName: 'CTA Information' });
      errorsByTab.cta.push('Monthly Price');
    }
    if (!data.ctaInfo?.contentDescription || data.ctaInfo.contentDescription.trim() === '') {
      errors.push({ field: 'Content Description', tab: 'cta', tabName: 'CTA Information' });
      errorsByTab.cta.push('Content Description');
    }
    if (!data.ctaInfo?.interactionStyle || data.ctaInfo.interactionStyle.trim() === '') {
      errors.push({ field: 'Interaction Style', tab: 'cta', tabName: 'CTA Information' });
      errorsByTab.cta.push('Interaction Style');
    }

    // Conversation Phases Tab
    if (!data.conversationPhases?.initialPhase?.name || data.conversationPhases.initialPhase.name.trim() === '') {
      errors.push({ field: 'Initial Phase Name', tab: 'phases', tabName: 'Conversation Phases' });
      errorsByTab.phases.push('Initial Phase Name');
    }
    if (data.conversationPhases?.phases && Array.isArray(data.conversationPhases.phases)) {
      data.conversationPhases.phases.forEach((phase, index) => {
        if (!phase.name || phase.name.trim() === '') {
          errors.push({ field: `Phase ${phase.phaseNumber || index + 1} Name`, tab: 'phases', tabName: 'Conversation Phases' });
          if (!errorsByTab.phases.includes(`Phase ${phase.phaseNumber || index + 1} Name`)) {
            errorsByTab.phases.push(`Phase ${phase.phaseNumber || index + 1} Name`);
          }
        }
      });
    }

    // CTA Phase Tab
    if (!data.ctaPhase?.scripts || !Array.isArray(data.ctaPhase.scripts) || data.ctaPhase.scripts.length === 0) {
      errors.push({ field: 'At least one CTA script', tab: 'ctaPhase', tabName: 'CTA Phase' });
      errorsByTab.ctaPhase.push('At least one CTA script');
    } else {
      data.ctaPhase.scripts.forEach((script, scriptIndex) => {
        if (!script.name || script.name.trim() === '') {
          errors.push({ field: `Script ${script.scriptNumber || scriptIndex + 1} Name`, tab: 'ctaPhase', tabName: 'CTA Phase' });
          if (!errorsByTab.ctaPhase.includes(`Script ${script.scriptNumber || scriptIndex + 1} Name`)) {
            errorsByTab.ctaPhase.push(`Script ${script.scriptNumber || scriptIndex + 1} Name`);
          }
        }
        if (!script.messages || !Array.isArray(script.messages) || script.messages.length === 0) {
          errors.push({ field: `Script ${script.scriptNumber || scriptIndex + 1} - At least one message`, tab: 'ctaPhase', tabName: 'CTA Phase' });
          if (!errorsByTab.ctaPhase.includes(`Script ${script.scriptNumber || scriptIndex + 1} - At least one message`)) {
            errorsByTab.ctaPhase.push(`Script ${script.scriptNumber || scriptIndex + 1} - At least one message`);
          }
        } else {
          script.messages.forEach((message, msgIndex) => {
            if (!message.spintax || message.spintax.trim() === '') {
              errors.push({ field: `Script ${script.scriptNumber || scriptIndex + 1} - Message ${message.messageNumber || msgIndex + 1}`, tab: 'ctaPhase', tabName: 'CTA Phase' });
              if (!errorsByTab.ctaPhase.includes(`Script ${script.scriptNumber || scriptIndex + 1} - Message ${message.messageNumber || msgIndex + 1}`)) {
                errorsByTab.ctaPhase.push(`Script ${script.scriptNumber || scriptIndex + 1} - Message ${message.messageNumber || msgIndex + 1}`);
              }
            }
          });
        }
      });
    }

    // Photo Management Tab - Check for photos (warning, not error)
    const photoCount = data.photos && Array.isArray(data.photos) ? data.photos.length : 0;
    if (photoCount < 4) {
      warnings.push({ 
        field: photoCount === 0 ? 'No images uploaded' : `Only ${photoCount} image(s) uploaded`, 
        tab: 'photos', 
        tabName: 'Photo Management',
        message: 'Add at least 4+ images to get the best results'
      });
      errorsByTab.photos.push(photoCount === 0 ? 'No images uploaded' : `Only ${photoCount} image(s) uploaded`);
    }

    return { 
      isValid: errors.length === 0, 
      errors, 
      warnings,
      errorsByTab,
      hasOnlyWarnings: errors.length === 0 && warnings.length > 0
    };
  };

  const handleAddModel = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If this is updating a draft, use PUT instead of POST
    const isUpdatingDraft = formData._id && formData.status === 'draft';
    
    // If bypassing validation, proceed directly (check both state and ref)
    if (bypassValidation || bypassValidationRef.current) {
      setBypassValidation(false);
      bypassValidationRef.current = false;
    } else {
      // Validate form before submitting (bypass HTML5 validation)
      const validation = validateModelForm(formData);
      if (!validation.isValid || validation.hasOnlyWarnings) {
        setValidationErrors(validation);
        // Throw error so callers know validation failed
        throw new Error('Validation failed');
      }
    }

    try {
      // Remove systemPrompt if user is not admin
      const submitData = { ...formData };
      if (user?.role !== 'admin') {
        delete submitData.systemPrompt;
      }
      
      // If updating a draft, change status to active
      if (isUpdatingDraft) {
        submitData.status = 'active';
        submitData.isActive = true;
        const response = await api.put(`/models/${formData._id}`, submitData);
        toast.success('Model updated and activated successfully');
        setShowAddModal(false);
        setShowEditModal(false);
        setEditingModel(null);
        setValidationErrors(null);
        setBypassValidation(false);
        resetForm();
        fetchModels();
        return response;
      } else {
        // New model (not a draft)
        submitData.status = 'active';
        const response = await api.post('/models', submitData);
        toast.success('Model added successfully');
        // Only close add modal if it's actually open (not when called from test modal)
        if (showAddModal) {
          setShowAddModal(false);
        }
        setValidationErrors(null);
        setBypassValidation(false);
        resetForm();
        fetchModels();
        // Return the response so callers can know if it succeeded
        return response;
      }
    } catch (error) {
      // Handle backend validation errors
      if (error.response?.data?.error) {
        const errorMsg = error.response.data.error;
        if (errorMsg.includes('validation failed') || errorMsg.includes('required')) {
          // Try to parse backend errors
          const backendErrors = [];
          if (error.response.data.errors) {
            Object.keys(error.response.data.errors).forEach(path => {
              const fieldName = path.split('.').pop();
              let tab = 'basic';
              let tabName = 'Basic Information';
              
              if (path.includes('ctaInfo')) {
                tab = 'cta';
                tabName = 'CTA Information';
              } else if (path.includes('conversationPhases')) {
                tab = 'phases';
                tabName = 'Conversation Phases';
              } else if (path.includes('ctaPhase')) {
                tab = 'ctaPhase';
                tabName = 'CTA Phase';
              }
              
              backendErrors.push({ 
                field: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'), 
                tab, 
                tabName 
              });
            });
          }
          if (backendErrors.length > 0) {
            setValidationErrors({ isValid: false, errors: backendErrors, errorsByTab: {} });
            // Re-throw so callers know it failed
            throw error;
          }
        }
      }
      toast.error('Failed to add model');
      console.error('Error adding model:', error);
      // Re-throw the error so callers can handle it
      throw error;
    }
  };

  const handleEditModel = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If bypassing validation, proceed directly (check both state and ref)
    if (bypassValidation || bypassValidationRef.current) {
      setBypassValidation(false);
      bypassValidationRef.current = false;
    } else {
      // Validate form before submitting (bypass HTML5 validation)
      const validation = validateModelForm(formData);
      if (!validation.isValid || validation.hasOnlyWarnings) {
        setValidationErrors(validation);
        return;
      }
    }

    try {
      // Remove systemPrompt if user is not admin
      const submitData = { ...formData };
      if (user?.role !== 'admin') {
        delete submitData.systemPrompt;
      }
      await api.put(`/models/${editingModel._id}`, submitData);
      toast.success('Model updated successfully');
      setShowEditModal(false);
      setEditingModel(null);
      setValidationErrors(null);
      setBypassValidation(false);
      resetForm();
      fetchModels();
    } catch (error) {
      // Handle backend validation errors
      if (error.response?.data?.error) {
        const errorMsg = error.response.data.error;
        if (errorMsg.includes('validation failed') || errorMsg.includes('required')) {
          // Try to parse backend errors
          const backendErrors = [];
          if (error.response.data.errors) {
            Object.keys(error.response.data.errors).forEach(path => {
              const fieldName = path.split('.').pop();
              let tab = 'basic';
              let tabName = 'Basic Information';
              
              if (path.includes('ctaInfo')) {
                tab = 'cta';
                tabName = 'CTA Information';
              } else if (path.includes('conversationPhases')) {
                tab = 'phases';
                tabName = 'Conversation Phases';
              } else if (path.includes('ctaPhase')) {
                tab = 'ctaPhase';
                tabName = 'CTA Phase';
              }
              
              backendErrors.push({ 
                field: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'), 
                tab, 
                tabName 
              });
            });
          }
          if (backendErrors.length > 0) {
            setValidationErrors({ isValid: false, errors: backendErrors, errorsByTab: {} });
            return;
          }
        }
      }
      toast.error('Failed to update model');
      console.error('Error updating model:', error);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (!window.confirm('Are you sure you want to delete this model?')) return;
    
    try {
      await api.delete(`/models/${modelId}`);
      toast.success('Model deleted successfully');
      fetchModels();
    } catch (error) {
      toast.error('Failed to delete model');
      console.error('Error deleting model:', error);
    }
  };

  const handleDuplicateModel = async (modelId) => {
    try {
      const response = await api.post(`/models/${modelId}/duplicate`);
      toast.success('Model duplicated successfully');
      fetchModels();
      // Optionally open the edit modal for the duplicated model
      if (response.data) {
        await openEditModal(response.data);
      }
    } catch (error) {
      toast.error('Failed to duplicate model');
      console.error('Error duplicating model:', error);
    }
  };

  const openEditModal = async (model) => {
    let m = model;
    if (model?._id) {
      try {
        const automationUrl = serviceUrls.automation;
        const token = localStorage.getItem('token');
        const res = automationUrl
          ? await axios.get(`${automationUrl}/api/models/${model._id}`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 60000,
            })
          : await api.get(`/models/${model._id}`);
        m = res.data;
      } catch (err) {
        console.error('Error loading model for edit:', err);
        toast.error('Could not load full model; photos may not preview until you fix the connection.');
      }
    }

    // Ensure initialPhase exists with default values if missing
    const conversationPhases = {
      ...m.conversationPhases,
      initialPhase: m.conversationPhases?.initialPhase || {
        name: 'Initial Contact',
        interestLevel: 'low',
        livePhotoRate: 0,
        personalityAdjustment: 'Be friendly and welcoming, start conversations naturally'
      }
    };

    // Ensure globalSettings has all required nested properties
    const globalSettings = {
      ...m.globalSettings,
      enhancedTexting: m.globalSettings?.enhancedTexting || {
        enabled: false,
        style: 'casual',
        customAbbreviations: {},
        useLowercase: false,
        removePunctuation: false,
        addFillerWords: true
      },
      noResponseFollowUp: m.globalSettings?.noResponseFollowUp || {
        messages: []
      }
    };

    // Auto-detect platform from CTA link if not set
    let platformType = m.platformType || 'fanvue';
    let customPlatformName = m.customPlatformName || '';
    
    if (!m.platformType || m.platformType === 'fanvue') {
      const link = m.ctaInfo?.fanvueLink || '';
      const lowerLink = link.toLowerCase();
      if (lowerLink.includes('onlyfans.com')) {
        platformType = 'onlyfans';
      } else if (lowerLink.includes('fansly.com')) {
        platformType = 'fansly';
      } else if (lowerLink.includes('fanvue.com')) {
        platformType = 'fanvue';
      }
    }

    setFormData({
      _id: m._id,
      name: m.name,
      displayName: m.displayName || '',
      age: m.age,
      defaultPinCode: m.defaultPinCode || '1111',
      gender: m.gender,
      chattingStyle: m.chattingStyle,
      preset: m.preset || 'shy_girl', // Ensure preset is loaded from model
      city: m.city,
      timezone: m.timezone,
      modelInfo: m.modelInfo,
      personalityTraits: m.personalityTraits || [],
      systemPrompt: m.systemPrompt || '',
      hairColor: m.hairColor || '',
      eyeColor: m.eyeColor || '',
      ethnicity: m.ethnicity || '',
      physicalDescription: m.physicalDescription || '',
      platformType: platformType,
      customPlatformName: customPlatformName,
      ctaInfo: {
        ...m.ctaInfo,
        snapchatLink: m.ctaInfo?.snapchatLink || '',
        chatLinkUrl: m.ctaInfo?.chatLinkUrl || '',
        primaryCategory: m.ctaInfo?.primaryCategory || '',
        additionalLinks: m.ctaInfo?.additionalLinks || [] // Ensure backward compatibility
      },
      dayTimeActivity: m.dayTimeActivity,
      nightTimeActivity: m.nightTimeActivity,
      language: m.language,
      detectLanguage: m.detectLanguage,
      stronglyDeclineMeetups: m.stronglyDeclineMeetups,
      conversationPhases: conversationPhases,
      ctaPhase: {
        ...m.ctaPhase,
        exitMessage: m.ctaPhase?.exitMessage || getDefaultExitMessage(platformType, customPlatformName),
        enablePostCTAPhases: m.ctaPhase?.enablePostCTAPhases || false,
        postCTAPhases: m.ctaPhase?.postCTAPhases || []
      },
      apiFollowUp: {
        enableForDm: m.apiFollowUp?.enableForDm ?? false,
        beforeCta: {
          enabled: m.apiFollowUp?.beforeCta?.enabled ?? false,
          idleMinutes: m.apiFollowUp?.beforeCta?.idleMinutes ?? 30,
          maxMessages: m.apiFollowUp?.beforeCta?.maxMessages ?? 3,
          analytics: m.apiFollowUp?.beforeCta?.analytics || { sent: 0, responses: 0, responseRate: 0 }
        },
        afterCta: {
          enabled: m.apiFollowUp?.afterCta?.enabled ?? false,
          idleMinutes: m.apiFollowUp?.afterCta?.idleMinutes ?? 30,
          maxMessages: m.apiFollowUp?.afterCta?.maxMessages ?? 3,
          reshareLinkOnRequest: m.apiFollowUp?.afterCta?.reshareLinkOnRequest !== false,
          analytics: m.apiFollowUp?.afterCta?.analytics || { sent: 0, responses: 0, responseRate: 0 }
        }
      },
      followbackDm: {
        coldPhaseMinSends: m.followbackDm?.coldPhaseMinSends ?? 50,
        optimizedTopCount: m.followbackDm?.optimizedTopCount ?? 10,
        messages: (m.followbackDm?.messages || []).map(msg => ({
          _id: msg._id,
          text: msg.text || '',
          timesSent: msg.timesSent || 0,
          responses: msg.responses || 0,
          responseRate: msg.responseRate || 0,
          lastUsed: msg.lastUsed || null,
          createdAt: msg.createdAt || null
        }))
      },
      globalSettings: globalSettings,
      photos: m.photos || [],
      postingConfig: {
        enabled: m.postingConfig?.enabled || false,
        postsPerDay: m.postingConfig?.postsPerDay || 1,
        texts: m.postingConfig?.texts || [],
        images: m.postingConfig?.images || []
      },
      bioUpdateConfig: {
        profilePictures: m.bioUpdateConfig?.profilePictures || [],
        banners: m.bioUpdateConfig?.banners || [],
        bios: m.bioUpdateConfig?.bios || [],
        names: m.bioUpdateConfig?.names || []
      },
      status: m.status || 'active' // Include status field
    });
    setEditingModel(m);
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      age: 22,
      defaultPinCode: '1111',
      gender: 'female',
      chattingStyle: 'youthful',
      preset: 'shy_girl', // Default preset
      city: '',
      timezone: 'UTC',
      modelInfo: '',
      personalityTraits: [],
      systemPrompt: '',
      hairColor: '',
      eyeColor: '',
      ethnicity: '',
      physicalDescription: '',
      platformType: 'fanvue',
      customPlatformName: '',
      ctaInfo: {
        fanvueLink: 'https://fanvue.com/your-model',
        snapchatLink: '',
        chatLinkUrl: '',
        additionalLinks: [], // Reset additional links
        isFree: false,
        monthlyPrice: 15,
        contentDescription: '',
        activityLevel: 'daily',
        interactionStyle: ''
      },
      dayTimeActivity: '',
      nightTimeActivity: '',
      language: 'en',
      detectLanguage: true,
      stronglyDeclineMeetups: true,
      conversationPhases: {
        numberOfPhases: 2,
        minimumExchangesPerPhase: 4,
        phases: [
          {
            phaseNumber: 1,
            name: 'Getting to Know You',
            interestLevel: 'low',
            livePhotoRate: 30,
            personalityAdjustment: 'Be friendly and curious, ask questions about them'
          },
          {
            phaseNumber: 2,
            name: 'Building Connection',
            interestLevel: 'medium',
            livePhotoRate: 40,
            personalityAdjustment: 'Be more flirty and show more interest, share personal details'
          }
        ]
      },
      ctaPhase: {
        minimumExchangesBeforeCTA: 8,
        enablePostCTAPhases: false,
        postCTAPhases: [],
        exitMessage: getDefaultExitMessage('fanvue', ''),
        scripts: [
          {
            scriptNumber: 1,
            name: 'Soft Approach',
            isActive: true,
            messages: [
              {
                messageNumber: 1,
                spintax: 'I really enjoy talking to you~you seem so sweet and interesting',
                aiAugmentation: true,
                includeLivePhoto: false,
                photoCategory: 'cta'
              },
              {
                messageNumber: 2,
                spintax: 'I share so much more on my page~you should check it out sometime',
                aiAugmentation: true,
                includeLivePhoto: true,
                photoCategory: 'cta'
              },
              {
                messageNumber: 3,
                spintax: 'It\'s only $15/month and I post daily~I\'d love to get to know you better there',
                aiAugmentation: true,
                includeLivePhoto: false,
                photoCategory: 'cta'
              }
            ],
            successRate: 0,
            totalUses: 0
          }
        ]
      },
      apiFollowUp: {
        beforeCta: {
          enabled: false,
          idleMinutes: 30,
          maxMessages: 3,
          analytics: { sent: 0, responses: 0, responseRate: 0 }
        },
        afterCta: {
          enabled: false,
          idleMinutes: 30,
          maxMessages: 3,
          reshareLinkOnRequest: true,
          analytics: { sent: 0, responses: 0, responseRate: 0 }
        }
      },
      globalSettings: {
        responseDelay: {
          min: 2000,
          max: 8000
        },
        followUps: {
          enabled: true,
          hoursBeforeFollowUp: 24
        },
        noResponseFollowUp: {
          messages: []
        },
        delaysNotPresent: {
          afterOpeningConversation: 0,
          afterSendingMessage: 0,
          betweenMessageBubbles: 0,
          typingSpeed: 'instant'
        },
        delaysPresent: {
          afterOpeningConversation: 5,
          afterSendingMessage: 5,
          betweenMessageBubbles: 5,
          typingSpeed: 'slow'
        },
        messageFromAnotherAccountDelay: {
          enabled: true,
          daysDelay: 14
        },
        onlyScanFirst: 99999,
        dontMessageOlderThan: 7,
        dailyNewConversationLimit: 99999,
        dailyMessagingLimit: 99999,
        consumeOnly: 99999,
        typoRate: 20,
        maxWords: 12,
        enhancedTexting: {
          enabled: false,
          style: 'casual',
          customAbbreviations: {},
          useLowercase: false,
          removePunctuation: false,
          addFillerWords: true
        },
        priorityChatter: true,
        replyOnly: false
      },
      photos: [],
      aiSettings: {
        provider: 'google'
      }
    });
  };

  // City to timezone mapping
  const getCityTimezone = (city) => {
    const cityLower = city.toLowerCase().trim();
    const timezoneMap = {
      'new york': 'America/New_York',
      'los angeles': 'America/Los_Angeles',
      'chicago': 'America/Chicago',
      'houston': 'America/Chicago',
      'phoenix': 'America/Phoenix',
      'philadelphia': 'America/New_York',
      'san antonio': 'America/Chicago',
      'san diego': 'America/Los_Angeles',
      'dallas': 'America/Chicago',
      'san jose': 'America/Los_Angeles',
      'austin': 'America/Chicago',
      'jacksonville': 'America/New_York',
      'san francisco': 'America/Los_Angeles',
      'columbus': 'America/New_York',
      'indianapolis': 'America/Indiana/Indianapolis',
      'fort worth': 'America/Chicago',
      'charlotte': 'America/New_York',
      'seattle': 'America/Los_Angeles',
      'denver': 'America/Denver',
      'washington': 'America/New_York',
      'boston': 'America/New_York',
      'nashville': 'America/Chicago',
      'baltimore': 'America/New_York',
      'oklahoma city': 'America/Chicago',
      'portland': 'America/Los_Angeles',
      'las vegas': 'America/Los_Angeles',
      'detroit': 'America/Detroit',
      'memphis': 'America/Chicago',
      'louisville': 'America/Kentucky/Louisville',
      'milwaukee': 'America/Chicago',
      'albuquerque': 'America/Denver',
      'tucson': 'America/Phoenix',
      'fresno': 'America/Los_Angeles',
      'sacramento': 'America/Los_Angeles',
      'mesa': 'America/Phoenix',
      'atlanta': 'America/New_York',
      'kansas city': 'America/Chicago',
      'colorado springs': 'America/Denver',
      'miami': 'America/New_York',
      'raleigh': 'America/New_York',
      'omaha': 'America/Chicago',
      'long beach': 'America/Los_Angeles',
      'virginia beach': 'America/New_York',
      'oakland': 'America/Los_Angeles',
      'minneapolis': 'America/Chicago',
      'tulsa': 'America/Chicago',
      'tampa': 'America/New_York',
      'arlington': 'America/Chicago',
      'new orleans': 'America/Chicago',
      'london': 'Europe/London',
      'paris': 'Europe/Paris',
      'berlin': 'Europe/Berlin',
      'madrid': 'Europe/Madrid',
      'rome': 'Europe/Rome',
      'amsterdam': 'Europe/Amsterdam',
      'barcelona': 'Europe/Madrid',
      'munich': 'Europe/Berlin',
      'milan': 'Europe/Rome',
      'prague': 'Europe/Prague',
      'vienna': 'Europe/Vienna',
      'budapest': 'Europe/Budapest',
      'warsaw': 'Europe/Warsaw',
      'brussels': 'Europe/Brussels',
      'stockholm': 'Europe/Stockholm',
      'dublin': 'Europe/Dublin',
      'copenhagen': 'Europe/Copenhagen',
      'athens': 'Europe/Athens',
      'lisbon': 'Europe/Lisbon',
      'helsinki': 'Europe/Helsinki',
      'oslo': 'Europe/Oslo',
      'toronto': 'America/Toronto',
      'montreal': 'America/Montreal',
      'vancouver': 'America/Vancouver',
      'calgary': 'America/Edmonton',
      'ottawa': 'America/Toronto',
      'edmonton': 'America/Edmonton',
      'winnipeg': 'America/Winnipeg',
      'sydney': 'Australia/Sydney',
      'melbourne': 'Australia/Melbourne',
      'brisbane': 'Australia/Brisbane',
      'perth': 'Australia/Perth',
      'adelaide': 'Australia/Adelaide',
      'tokyo': 'Asia/Tokyo',
      'osaka': 'Asia/Tokyo',
      'seoul': 'Asia/Seoul',
      'beijing': 'Asia/Shanghai',
      'shanghai': 'Asia/Shanghai',
      'hong kong': 'Asia/Hong_Kong',
      'singapore': 'Asia/Singapore',
      'bangkok': 'Asia/Bangkok',
      'kuala lumpur': 'Asia/Kuala_Lumpur',
      'manila': 'Asia/Manila',
      'jakarta': 'Asia/Jakarta',
      'delhi': 'Asia/Kolkata',
      'mumbai': 'Asia/Kolkata',
      'bangalore': 'Asia/Kolkata',
      'dubai': 'Asia/Dubai',
      'abu dhabi': 'Asia/Dubai',
      'tel aviv': 'Asia/Jerusalem',
      'jerusalem': 'Asia/Jerusalem',
      'istanbul': 'Europe/Istanbul',
      'moscow': 'Europe/Moscow',
      'st petersburg': 'Europe/Moscow',
      'mexico city': 'America/Mexico_City',
      'guadalajara': 'America/Mexico_City',
      'monterrey': 'America/Monterrey',
      'buenos aires': 'America/Argentina/Buenos_Aires',
      'sao paulo': 'America/Sao_Paulo',
      'rio de janeiro': 'America/Sao_Paulo',
      'lima': 'America/Lima',
      'bogota': 'America/Bogota',
      'santiago': 'America/Santiago',
      'caracas': 'America/Caracas',
      'johannesburg': 'Africa/Johannesburg',
      'cape town': 'Africa/Johannesburg',
      'cairo': 'Africa/Cairo',
      'lagos': 'Africa/Lagos',
      'nairobi': 'Africa/Nairobi',
      'auckland': 'Pacific/Auckland',
      'wellington': 'Pacific/Auckland'
    };
    
    return timezoneMap[cityLower] || 'UTC';
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Auto-detect timezone when city changes
      if (field === 'city') {
        const detectedTimezone = getCityTimezone(value);
        setFormData(prev => ({
          ...prev,
          city: value,
          timezone: detectedTimezone
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    }
  };

  const handleNestedInputChange = (parent, child, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: value
      }
    }));
  };

  const ModelCard = ({ model }) => {
    // Get profile picture - use model.profilePicture if set, otherwise use first photo
    const profilePicture = model.profilePicture || (model.photos && model.photos.length > 0 ? model.photos[0].url : null);
    const profileThumb =
      model.profilePictureDisplayUrl ||
      (model.photos && model.photos[0] && model.photos[0].displayUrl) ||
      null;

    return (
    <div className="card dark:bg-gray-800 dark:border-gray-700">
      <div className="card-body">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center overflow-hidden">
              {profilePicture ? (
                <img 
                  src={profileThumb || resolveImageUrl(profilePicture, serviceUrls.automation)} 
                  alt={model.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (parent && !parent.querySelector('svg')) {
                      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                      icon.setAttribute('class', 'h-6 w-6 text-gray-500 dark:text-gray-400');
                      icon.setAttribute('fill', 'none');
                      icon.setAttribute('viewBox', '0 0 24 24');
                      icon.setAttribute('stroke', 'currentColor');
                      icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>';
                      parent.appendChild(icon);
                    }
                  }}
                />
              ) : (
                <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {model.displayName || model.name}
              </h3>
              {model.displayName && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Model name: {model.name}</p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">{model.age} years old • {model.gender}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{model.city} ({model.timezone})</p>
              {user?.role === 'admin' && model.userId?.username && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Owner: @{model.userId.username}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => openEditModal(model)}
              className="btn btn-secondary btn-sm"
                title="Edit model"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteModel(model._id)}
              className="btn btn-danger btn-sm"
                title="Delete model"
            >
              <TrashIcon className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => handleDuplicateModel(model._id)}
              className="btn btn-secondary btn-sm"
              title="Duplicate model"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Preset:</span>
            <span className="ml-1 text-gray-900 dark:text-white">{getPresetName(model.preset)}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Phases:</span>
            <span className="ml-1 text-gray-900 dark:text-white">{model.conversationPhases?.numberOfPhases || 2}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Photos:</span>
            <span className="ml-1 text-gray-900 dark:text-white">{model.photos?.length || 0}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Scripts:</span>
            <span className="ml-1 text-gray-900 dark:text-white">{model.ctaPhase?.scripts?.length || 0}</span>
          </div>
        </div>
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-bl from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-bl from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs - flipped positions */}
          <div className={`absolute -top-20 -left-20 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute top-1/3 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/25'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes */}
          <svg className="absolute bottom-8 right-12 w-20 h-20 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
          </svg>
          <svg className="absolute top-10 right-1/4 w-16 h-16 opacity-10" viewBox="0 0 100 100">
            <polygon points="50,5 95,75 5,75" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
          </svg>
          <svg className="absolute top-1/2 left-12 w-12 h-12 opacity-10" viewBox="0 0 100 100">
            <rect x="20" y="20" width="60" height="60" rx="8" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" transform="rotate(-10 50 50)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <CubeIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Models
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage your AI personas and their settings
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                    }`} />
                    {models.length} Model{models.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleOpenAddModal}
                className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                }`}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Model
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mt-6 mb-6 flex space-x-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setModelFilter('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            modelFilter === 'all'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          All ({models.length})
        </button>
        <button
          onClick={() => setModelFilter('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            modelFilter === 'active'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Active ({models.filter(m => m.status !== 'draft').length})
        </button>
        <button
          onClick={() => setModelFilter('draft')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            modelFilter === 'draft'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Drafts ({models.filter(m => m.status === 'draft').length})
        </button>
      </div>

      {/* Filtered Models */}
      {(() => {
        const filteredModels = models.filter(model => {
          if (modelFilter === 'active') return model.status !== 'draft';
          if (modelFilter === 'draft') return model.status === 'draft';
          return true; // 'all'
        });

        return filteredModels.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">👤</div>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {modelFilter === 'draft' ? 'No draft models' : modelFilter === 'active' ? 'No active models' : 'No models'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {modelFilter === 'draft' 
                ? 'Get started by creating a new model.' 
                : 'Get started by adding your first model.'}
            </p>
            {modelFilter !== 'draft' && (
              <div className="mt-6">
                <button
                  onClick={handleOpenAddModal}
                  className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                      : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                  }`}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Model
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModels.map((model) => (
              <ModelCard key={model._id} model={model} />
            ))}
          </div>
        );
      })()}

      {/* Validation Error Modal */}
      {validationErrors && (!validationErrors.isValid || validationErrors.hasOnlyWarnings) && (showAddModal || showEditModal) && (
        <ValidationErrorModal
          errors={validationErrors.errors || []}
          warnings={validationErrors.warnings || []}
          errorsByTab={validationErrors.errorsByTab}
          hasOnlyWarnings={validationErrors.hasOnlyWarnings}
          onClose={() => setValidationErrors(null)}
          onNavigateToTab={(tabId) => {
            if (modalTabSetterRef.current && typeof modalTabSetterRef.current === 'function') {
              // Navigate to the tab
              modalTabSetterRef.current(tabId);
              // Close validation modal after a brief delay to allow tab to change
              setTimeout(() => {
                setValidationErrors(null);
              }, 300);
            } else {
              // If tab setter not ready, just close the modal
              setValidationErrors(null);
            }
          }}
          onProceedAnyway={async () => {
            // Check if we're in test mode (validation modal was opened from Test Model button)
            const isTestMode = validationErrors?.isTestMode || false;
            
            if (isTestMode) {
              // If test mode, go directly to test modal
            setValidationErrors(null);
              // Close both add and edit modals
              setShowAddModal(false);
              setShowEditModal(false);
            setTimeout(() => {
                setShowTestModal(true);
              }, 100);
            } else {
              // Clear validation errors and save directly (user wants to proceed despite warnings)
              setValidationErrors(null);
              // Set both state and ref synchronously before calling handlers
              setBypassValidation(true);
              bypassValidationRef.current = true;
              try {
                const submitEvent = { preventDefault: () => {}, stopPropagation: () => {} };
                if (showEditModal && editingModel) {
                  await handleEditModel(submitEvent);
                } else {
                  await handleAddModel(submitEvent);
                }
              } catch (error) {
                // If save fails, don't re-throw - errors are handled by handlers
                console.error('Error saving model:', error);
              } finally {
                setBypassValidation(false);
                bypassValidationRef.current = false;
                }
              }
          }}
          isAddModel={showAddModal}
          isTestMode={validationErrors?.isTestMode || false}
        />
      )}

      {/* Add Model Choice Modal */}
      {showAddChoiceModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddChoiceModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {isEditMode ? 'Update Model' : 'Add Model'}
                  </h3>
                  <button
                    onClick={() => setShowAddChoiceModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Choose an option to proceed:
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={async () => {
                      try {
                        // Directly save the model (validation already passed before showing choice modal)
                        const submitEvent = { preventDefault: () => {}, stopPropagation: () => {} };
                        if (isEditMode) {
                          await handleEditModel(submitEvent);
                        } else {
                          // Use bypassValidation since we already validated before showing choice modal
                          setBypassValidation(true);
                          bypassValidationRef.current = true; // Set ref synchronously
                          await handleAddModel(submitEvent);
                          bypassValidationRef.current = false;
                        }
                        // Close choice modal after successful save
                        setShowAddChoiceModal(false);
                        setBypassValidation(false);
                        bypassValidationRef.current = false;
                      } catch (error) {
                        // If save fails (e.g., network error), show error and reopen modal
                        console.error('Error saving model:', error);
                        setShowAddChoiceModal(false);
                        setTimeout(() => {
                          if (isEditMode) {
                            setShowEditModal(true);
                          } else {
                            setShowAddModal(true);
                          }
                        }, 100);
                        setBypassValidation(false);
                        bypassValidationRef.current = false;
                      }
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {isEditMode ? 'Update Model' : 'Add Model'}
                  </button>
                  
                  <button
                    onClick={() => {
                      // Validate form before allowing test (same validation as "Add Model" / "Update Model")
                      const validation = validateModelForm(formData);
                      if (!validation.isValid && !validation.hasOnlyWarnings) {
                        // Show validation errors (same modal as "Add Model" / "Update Model")
                        setValidationErrors(validation);
                        // Close choice modal to show validation errors
                        setShowAddChoiceModal(false);
                        // Reopen the appropriate modal so user can see and fix validation errors
                        setTimeout(() => {
                          if (isEditMode && showEditModal) {
                            setShowEditModal(true);
                          } else {
                            setShowAddModal(true);
                          }
                        }, 100);
                        return;
                      }
                      
                      setShowAddChoiceModal(false);
                      // Close edit/add modal if open
                      if (showEditModal) {
                        setShowEditModal(false);
                      }
                      if (showAddModal) {
                        setShowAddModal(false);
                      }
                      // Small delay to ensure modals are closed before opening test modal
                      setTimeout(() => {
                        setShowTestModal(true);
                      }, 100);
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <SparklesIcon className="h-5 w-5 mr-2" />
                    Test Model
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Model Modal */}
      {showAddModal && (
        <ModelModal
          title="Add New Model"
          formData={formData}
          onInputChange={handleInputChange}
          onNestedInputChange={handleNestedInputChange}
          presets={presets}
          isDarkMode={isDarkMode}
          serviceUrls={serviceUrls}
          scrapeCategories={scrapeCategories}
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // First, validate the form
            const validation = validateModelForm(formData);
            if (!validation.isValid && !validation.hasOnlyWarnings) {
              // Show validation errors immediately
              setValidationErrors(validation);
              return;
            }
            
            // If there are only warnings (no errors), show them so user can see recommendations
            if (validation.hasOnlyWarnings) {
              setValidationErrors(validation);
              return;
            }
            
            // If validation passes completely (no errors, no warnings), save directly
            try {
              const submitEvent = { preventDefault: () => {}, stopPropagation: () => {} };
              setBypassValidation(true);
              bypassValidationRef.current = true; // Set ref synchronously
              await handleAddModel(submitEvent);
              setBypassValidation(false);
              bypassValidationRef.current = false;
            } catch (error) {
              // If save fails, validation errors should already be set by handleAddModel
              setBypassValidation(false);
              // Don't re-throw - errors are handled by handleAddModel
            }
          }}
          onTestModel={() => {
            // Validate form before allowing test
            const validation = validateModelForm(formData);
            if (!validation.isValid && !validation.hasOnlyWarnings) {
              // Show validation errors (must fix before testing)
              setValidationErrors({ ...validation, isTestMode: true });
              return;
            }
            
            // If there are only warnings, show them so user can see recommendations
            if (validation.hasOnlyWarnings) {
              setValidationErrors({ ...validation, isTestMode: true });
              return;
            }
            
            // If validation passes completely (no errors, no warnings), go directly to test modal
            setShowAddModal(false);
            setTimeout(() => {
              setShowTestModal(true);
            }, 100);
          }}
          onClose={() => {
            setShowAddModal(false);
            setShowAddChoiceModal(false);
            setValidationErrors(null);
            setBypassValidation(false);
            resetForm();
          }}
          onOpen={async () => {
            // For add modal, silently apply the full "Shy Girl" preset (no toast)
            try {
              await applyPresetToForm('shy_girl');
            } catch (error) {
              console.error('Error applying default preset:', error);
              // Only show error toast, not success toast
            }
          }}
          isAdmin={user?.role === 'admin'}
          onTabSetterReady={(setter) => {
            modalTabSetterRef.current = setter;
          }}
        />
      )}

      {/* Test Model Modal */}
      {showTestModal && (
        <TestModelModal
          isOpen={showTestModal}
          onClose={() => setShowTestModal(false)}
          formData={formData}
          serviceUrls={serviceUrls}
          onEditModel={() => {
            setShowTestModal(false);
            // Small delay to ensure test modal closes before reopening edit/add modal
            setTimeout(() => {
              // Reopen the appropriate modal based on mode
              if (isEditMode && editingModel) {
                // Reopen the edit modal
                setShowEditModal(true);
              } else {
                // Reopen the add modal
                setShowAddModal(true);
              }
            }, 100);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
        />
      )}

      {/* Edit Model Modal */}
      {showEditModal && editingModel && (
        <ModelModal
          title="Edit Model"
          formData={formData}
          onInputChange={handleInputChange}
          onNestedInputChange={handleNestedInputChange}
          presets={presets}
          isDarkMode={isDarkMode}
          serviceUrls={serviceUrls}
          scrapeCategories={scrapeCategories}
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // First, validate the form
            const validation = validateModelForm(formData);
            if (!validation.isValid && !validation.hasOnlyWarnings) {
              // Show validation errors immediately
              setValidationErrors(validation);
              return;
            }
            
            // If there are only warnings (no errors), show them so user can see recommendations
            if (validation.hasOnlyWarnings) {
              setValidationErrors(validation);
              return;
            }
            
            // If validation passes completely (no errors, no warnings), save directly
            try {
              const submitEvent = { preventDefault: () => {}, stopPropagation: () => {} };
              setBypassValidation(true);
              bypassValidationRef.current = true; // Set ref synchronously
              await handleEditModel(submitEvent);
              setBypassValidation(false);
              bypassValidationRef.current = false;
            } catch (error) {
              // If save fails, validation errors should already be set by handleEditModel
              setBypassValidation(false);
              bypassValidationRef.current = false;
              // Don't re-throw - errors are handled by handleEditModel
              console.error('Error updating model:', error);
            }
          }}
          onTestModel={() => {
            // Validate form before allowing test
            const validation = validateModelForm(formData);
            if (!validation.isValid && !validation.hasOnlyWarnings) {
              // Show validation errors (must fix before testing)
              setValidationErrors({ ...validation, isTestMode: true });
              return;
            }
            
            // If there are only warnings, show them so user can see recommendations
            if (validation.hasOnlyWarnings) {
              setValidationErrors({ ...validation, isTestMode: true });
              return;
            }
            
            // If validation passes completely (no errors, no warnings), go directly to test modal
            setShowEditModal(false);
            setTimeout(() => {
              setShowTestModal(true);
            }, 100);
          }}
          onClose={() => {
            setShowEditModal(false);
            setEditingModel(null);
            setValidationErrors(null);
            setBypassValidation(false);
            setIsEditMode(false);
            resetForm();
          }}
          isAdmin={user?.role === 'admin'}
          onTabSetterReady={(setter) => {
            modalTabSetterRef.current = setter;
          }}
        />
      )}
    </div>
  );
};

// Validation Error Modal Component
const ValidationErrorModal = ({ errors, warnings, errorsByTab, hasOnlyWarnings, onClose, onNavigateToTab, onProceedAnyway, isAddModel, isTestMode = false }) => {
  // Group errors by tab
  const errorsGrouped = {};
  errors.forEach(error => {
    if (!errorsGrouped[error.tab]) {
      errorsGrouped[error.tab] = {
        tabName: error.tabName,
        fields: []
      };
    }
    if (!errorsGrouped[error.tab].fields.includes(error.field)) {
      errorsGrouped[error.tab].fields.push(error.field);
    }
  });

  // Group warnings by tab
  const warningsGrouped = {};
  warnings.forEach(warning => {
    if (!warningsGrouped[warning.tab]) {
      warningsGrouped[warning.tab] = {
        tabName: warning.tabName,
        fields: [],
        message: warning.message
      };
    }
    if (!warningsGrouped[warning.tab].fields.includes(warning.field)) {
      warningsGrouped[warning.tab].fields.push(warning.field);
    }
  });

  const tabOrder = ['basic', 'cta', 'phases', 'ctaPhase', 'photos'];
  const tabIcons = {
    basic: '👤',
    cta: '💰',
    phases: '💬',
    ctaPhase: '🎯',
    photos: '📸'
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-start">
              {hasOnlyWarnings ? (
                <div className="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              ) : (
                <div className="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              )}
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  {hasOnlyWarnings ? 'Recommendation' : 'Missing Required Fields'}
                </h3>
                <div className="mt-4">
                  {hasOnlyWarnings ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {warnings[0]?.message || 'Please review the following recommendations:'}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Please fill in the following required fields before submitting:
                    </p>
                  )}
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Show Errors First */}
                    {tabOrder.map(tabId => {
                      if (!errorsGrouped[tabId]) return null;
                      const tab = errorsGrouped[tabId];
                      return (
                        <div key={tabId} className="border border-red-200 dark:border-red-800 rounded-2xl p-4 bg-red-50 dark:bg-red-900/10">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                              <span className="mr-2">{tabIcons[tabId]}</span>
                              {tab.tabName}
                            </h4>
                            <button
                              type="button"
                              onClick={() => onNavigateToTab(tabId)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                            >
                              Go to tab →
                            </button>
                          </div>
                          <ul className="list-disc list-inside space-y-1">
                            {tab.fields.map((field, idx) => (
                              <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                    
                    {/* Show Warnings (Yellow) */}
                    {tabOrder.map(tabId => {
                      if (!warningsGrouped[tabId]) return null;
                      const tab = warningsGrouped[tabId];
                      return (
                        <div key={`warning-${tabId}`} className="border border-yellow-300 dark:border-yellow-700 rounded-2xl p-4 bg-yellow-50 dark:bg-yellow-900/20">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                              <span className="mr-2">{tabIcons[tabId]}</span>
                              {tab.tabName}
                            </h4>
                            <button
                              type="button"
                              onClick={() => onNavigateToTab(tabId)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                            >
                              Go to tab →
                            </button>
                          </div>
                          {tab.message && (
                            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2 font-medium">
                              {tab.message}
                            </p>
                          )}
                          <ul className="list-disc list-inside space-y-1">
                            {tab.fields.map((field, idx) => (
                              <li key={idx} className="text-sm text-yellow-700 dark:text-yellow-300">
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {hasOnlyWarnings && onProceedAnyway ? (
              <>
                <button
                  type="button"
                  onClick={onProceedAnyway}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isTestMode ? 'Test Model Anyway' : (isAddModel ? 'Add Model Anyway' : 'Update Model Anyway')}
                </button>
                <button
                  type="button"
                  onClick={() => onNavigateToTab('photos')}
                  className="w-full inline-flex justify-center rounded-xl border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Go to Photo Management
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                OK, I'll fix these
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Model Modal Component
const ModelModal = ({ title, formData, onInputChange, onNestedInputChange, onSubmit, onClose, isAdmin, onTabSetterReady, onOpen, onTestModel, presets = [], isDarkMode, serviceUrls = {}, scrapeCategories = [] }) => {
  const [activeTab, setActiveTab] = useState('basic');
  
  // Reset to basic tab when modal opens (only when title changes, not on every render)
  useEffect(() => {
    setActiveTab('basic');
    // Call onOpen callback if provided
    if (onOpen) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]); // Only depend on title, not onOpen to prevent tab reset on form changes
  
  // Expose setActiveTab to parent component
  useEffect(() => {
    if (onTabSetterReady) {
      onTabSetterReady(setActiveTab);
    }
  }, [onTabSetterReady]);

  const allTabs = [
    { id: 'basic', name: 'Basic Information', icon: '👤' },
    { id: 'systemPrompt', name: 'System Prompt', icon: '🤖' },
    { id: 'cta', name: 'CTA Information', icon: '💰' },
    { id: 'phases', name: 'Conversation Phases', icon: '💬' },
    { id: 'ctaPhase', name: 'CTA Phase', icon: '🎯' },
    { id: 'followUp', name: 'Follow Up', icon: '🔔' },
    { id: 'followback', name: 'Followback', icon: '↩️' },
    { id: 'photos', name: 'Photo Management', icon: '📸' },
    { id: 'posts', name: 'Auto Posts', icon: '📝' },
    { id: 'bioUpdate', name: 'Bio Update', icon: '✏️' },
    { id: 'settings', name: 'Settings', icon: '⚙️' }
  ];
  
  // Filter out admin-only tabs for non-admin users
  const tabs = isAdmin ? allTabs : allTabs.filter(tab => !['systemPrompt', 'posts', 'bioUpdate'].includes(tab.id));
  
  // If activeTab is an admin-only tab but user is not admin, reset to basic
  useEffect(() => {
    if (['systemPrompt', 'posts', 'bioUpdate'].includes(activeTab) && !isAdmin) {
      setActiveTab('basic');
    }
  }, [activeTab, isAdmin]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 glass-overlay transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom glass-modal text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <form onSubmit={onSubmit} noValidate>
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">{title}</h3>
              
              {/* Tab Navigation */}
              <div className="mb-6">
                <nav className="flex flex-wrap gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                      } whitespace-nowrap py-1.5 px-3 rounded-lg font-medium text-sm flex items-center space-x-1.5 transition-all duration-150`}
                    >
                      <span>{tab.icon}</span>
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
              
              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'basic' && (
                  <BasicInfoTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} presets={presets} />
                )}
                {activeTab === 'systemPrompt' && (
                  <SystemPromptTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} />
                )}
                {activeTab === 'cta' && (
                  <CTAInfoTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} scrapeCategories={scrapeCategories} />
                )}
                {activeTab === 'phases' && (
                  <ConversationPhasesTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} />
                )}
                {activeTab === 'ctaPhase' && (
                  <CTAPhaseTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} />
                )}
                {activeTab === 'followUp' && (
                  <FollowUpTab formData={formData} onNestedInputChange={onNestedInputChange} />
                )}
                {activeTab === 'followback' && (
                  <FollowbackTab formData={formData} onInputChange={onInputChange} />
                )}
                {activeTab === 'photos' && (
                  <PhotoManagementTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} isDarkMode={isDarkMode} serviceUrls={serviceUrls} />
                )}
                {activeTab === 'posts' && (
                  <AutoPostsTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} isDarkMode={isDarkMode} serviceUrls={serviceUrls} />
                )}
                {activeTab === 'bioUpdate' && (
                  <BioUpdateTab formData={formData} onInputChange={onInputChange} isDarkMode={isDarkMode} serviceUrls={serviceUrls} />
                )}
                {activeTab === 'settings' && (
                  <SettingsTab formData={formData} onInputChange={onInputChange} onNestedInputChange={onNestedInputChange} />
                )}
              </div>
            </div>
            
            <div className="bg-gray-50/50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-white/20 dark:border-gray-700/30">
              <div className="flex space-x-3">
                {onTestModel && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onTestModel();
                    }}
                    className="btn btn-secondary"
                  >
                    <SparklesIcon className="h-5 w-5 mr-2" />
                    Test Model
                  </button>
                )}
                <button 
                  type="submit" 
                  className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                      : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                  }`}
                >
                  {title.includes('Add') ? 'Add Model' : 'Update Model'}
                </button>
              </div>
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Tab Components
const BasicInfoTab = ({ formData, onInputChange, onNestedInputChange, presets = [] }) => {
  // Helper function to get default link for platform
  const getDefaultLink = (platformType, customName = '') => {
    if (platformType === 'custom' && customName) {
      return `https://${customName.toLowerCase().replace(/\s+/g, '')}.com/`;
    }
    const links = {
      'fanvue': 'https://fanvue.com/',
      'onlyfans': 'https://onlyfans.com/',
      'fansly': 'https://fansly.com/'
    };
    return links[platformType] || 'https://fanvue.com/';
  };

  // Handle platform type change
  const handlePlatformTypeChange = (newPlatformType) => {
    const prevPlatformType = formData.platformType || 'fanvue';
    const prevCustomName = formData.customPlatformName || '';
    const shouldUpdateExitMessage =
      !formData.ctaPhase?.exitMessage ||
      isAutoExitMessageForPlatform(formData.ctaPhase?.exitMessage, prevPlatformType, prevCustomName);

    onInputChange('platformType', newPlatformType);
    
    // Auto-update CTA link when platform changes (only if link matches old platform default)
    const currentLink = formData.ctaInfo?.fanvueLink || '';
    const oldDefaultLink = getDefaultLink(formData.platformType || 'fanvue');
    
    // Only auto-update if current link matches the old platform's default pattern
    if (currentLink.startsWith(oldDefaultLink) || currentLink === oldDefaultLink) {
      const newLink = getDefaultLink(newPlatformType);
      onNestedInputChange('ctaInfo', 'fanvueLink', newLink);
    }

    if (shouldUpdateExitMessage) {
      const newExit = getDefaultExitMessage(newPlatformType, formData.customPlatformName || '');
      onNestedInputChange('ctaPhase', 'exitMessage', newExit);
    }
    
    // Clear custom platform name if switching away from custom
    if (newPlatformType !== 'custom') {
      onInputChange('customPlatformName', '');
    }
  };

  // Handle custom platform name change
  const handleCustomPlatformNameChange = (customName) => {
    const prevPlatformType = formData.platformType || 'fanvue';
    const prevCustomName = formData.customPlatformName || '';
    const shouldUpdateExitMessage =
      !formData.ctaPhase?.exitMessage ||
      isAutoExitMessageForPlatform(formData.ctaPhase?.exitMessage, prevPlatformType, prevCustomName);

    onInputChange('customPlatformName', customName);
    
    // Auto-update link if platform type is custom and name is provided
    if (formData.platformType === 'custom' && customName) {
      const newLink = getDefaultLink('custom', customName);
      onNestedInputChange('ctaInfo', 'fanvueLink', newLink);
    }

    if (formData.platformType === 'custom' && shouldUpdateExitMessage) {
      const newExit = getDefaultExitMessage('custom', customName);
      onNestedInputChange('ctaPhase', 'exitMessage', newExit);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            required
            placeholder="e.g., Amy"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This name is used in AI conversations</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Name (Platform)</label>
          <input
            type="text"
            value={formData.displayName || ''}
            onChange={(e) => onInputChange('displayName', e.target.value)}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Optional - shown on platform only"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Optional: Custom name shown on the platform dashboard</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Age</label>
          <input
            type="number"
            value={formData.age}
            onChange={(e) => onInputChange('age', parseInt(e.target.value))}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            min="18"
            max="99"
            required
          />
        </div>
      </div>

      {/* E2EE PIN Code Section */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
              <span className="text-lg mr-2">🔐</span>
              E2EE PIN Code
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              PIN used for encrypted chat key setup. Accounts assigned to this model will use this PIN.
            </p>
          </div>
          <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full font-medium">
            {formData.defaultPinCode?.length || 4} digits
          </span>
        </div>
        <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-600">
          <input
            type="text"
            value={formData.defaultPinCode || '1111'}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
              onInputChange('defaultPinCode', val);
            }}
            className="form-input w-full text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono tracking-widest text-lg"
            placeholder="1111"
            maxLength={4}
            minLength={4}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Default: 1111 &middot; Numeric only &middot; 4 digits
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gender</label>
          <select
            value={formData.gender}
            onChange={(e) => onInputChange('gender', e.target.value)}
            className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Chatting Style</label>
          <select
            value={formData.chattingStyle}
            onChange={(e) => onInputChange('chattingStyle', e.target.value)}
            className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="youthful">Youthful</option>
            <option value="mature">Mature</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Personality Preset</label>
        <select
          value={formData.preset || 'shy_girl'}
          onChange={async (e) => {
            const newPreset = e.target.value;
            onInputChange('preset', newPreset);
            
            // Fetch preset data and apply it
            try {
              const response = await api.get(`/models/presets/${newPreset}`);
              const preset = response.data;
              
              // Apply preset to formData
              if (preset.modelInfo) {
                onInputChange('modelInfo', preset.modelInfo);
              }
              
              if (preset.interactionStyle) {
                onNestedInputChange('ctaInfo', 'interactionStyle', preset.interactionStyle);
              }
              
              // Apply day and night time activities
              if (preset.dayTimeActivity) {
                onInputChange('dayTimeActivity', preset.dayTimeActivity);
              }
              
              if (preset.nightTimeActivity) {
                onInputChange('nightTimeActivity', preset.nightTimeActivity);
              }
              
              // Apply no-response follow-up messages
              if (preset.noResponseFollowUp && preset.noResponseFollowUp.messages) {
                const followUpMessages = preset.noResponseFollowUp.messages.map((msg, index) => ({
                  messageText: msg,
                  analytics: {
                    timesSent: 0,
                    responses: 0,
                    responseRate: 0,
                    lastUsed: null
                  }
                }));
                onNestedInputChange('globalSettings', 'noResponseFollowUp', { messages: followUpMessages });
              }
              
              // Apply personality adjustments and live photo rates to phases
              // Also set default minimum exchanges for all presets
              if (preset.personalityAdjustments || preset.livePhotoRates) {
                // Update initial phase
                const initialPhaseUpdates = {};
                if (preset.personalityAdjustments?.initial) {
                  initialPhaseUpdates.personalityAdjustment = preset.personalityAdjustments.initial;
                }
                if (preset.livePhotoRates?.initial !== undefined) {
                  initialPhaseUpdates.livePhotoRate = preset.livePhotoRates.initial;
                }
                // Set default minimum exchanges for initial phase (all presets)
                initialPhaseUpdates.minimumExchanges = 4;
                const initialPhaseMinimumExchanges = initialPhaseUpdates.minimumExchanges;
                onNestedInputChange('conversationPhases', 'initialPhase', {
                  ...formData.conversationPhases.initialPhase,
                  ...initialPhaseUpdates
                });
                
                // Determine how many phases this preset needs
                let numberOfPhases = 2; // Default
                if (newPreset === 'simp_converter') {
                  numberOfPhases = 5; // simp_converter needs 5 phases
                }
                
                // Update phases - create/update phases 1-5 for simp_converter, 1-2 for others
                const updatedPhases = [...formData.conversationPhases.phases];
                
                // Ensure we have enough phases
                while (updatedPhases.length < numberOfPhases) {
                  updatedPhases.push({
                    phaseNumber: updatedPhases.length + 1,
                    name: `Phase ${updatedPhases.length + 1}`,
                    interestLevel: 'medium',
                    livePhotoRate: 30,
                    personalityAdjustment: '',
                    minimumExchanges: 4
                  });
                }
                
                let phasesUpdated = false;
                
                // Update phase 1
                if (updatedPhases.length > 0) {
                  const phase1Updates = {};
                  if (preset.personalityAdjustments?.phase1) {
                    phase1Updates.personalityAdjustment = preset.personalityAdjustments.phase1;
                  }
                  if (preset.livePhotoRates?.phase1 !== undefined) {
                    phase1Updates.livePhotoRate = preset.livePhotoRates.phase1;
                  }
                  phase1Updates.minimumExchanges = 5;
                  phase1Updates.name = preset.phase1?.description?.split(' - ')[0] || 'Getting to Know You';
                  phase1Updates.interestLevel = 'medium';
                  updatedPhases[0] = { ...updatedPhases[0], ...phase1Updates };
                  phasesUpdated = true;
                }
                
                // Update phase 2
                if (updatedPhases.length > 1) {
                  const phase2Updates = {};
                  if (preset.personalityAdjustments?.phase2) {
                    phase2Updates.personalityAdjustment = preset.personalityAdjustments.phase2;
                  }
                  if (preset.livePhotoRates?.phase2 !== undefined) {
                    phase2Updates.livePhotoRate = preset.livePhotoRates.phase2;
                  }
                  phase2Updates.minimumExchanges = 4;
                  phase2Updates.name = preset.phase2?.description?.split(' - ')[0] || 'Playful Flirt';
                  phase2Updates.interestLevel = 'medium';
                  updatedPhases[1] = { ...updatedPhases[1], ...phase2Updates };
                  phasesUpdated = true;
                }
                
                // Update phase 3 (for simp_converter)
                if (numberOfPhases >= 3 && updatedPhases.length > 2 && preset.personalityAdjustments?.phase3) {
                  const phase3Updates = {};
                  phase3Updates.personalityAdjustment = preset.personalityAdjustments.phase3;
                  if (preset.livePhotoRates?.phase3 !== undefined) {
                    phase3Updates.livePhotoRate = preset.livePhotoRates.phase3;
                  }
                  phase3Updates.minimumExchanges = 4;
                  phase3Updates.name = preset.phase3?.description?.split(' - ')[0] || 'Heat Check + Boundaries';
                  phase3Updates.interestLevel = 'high';
                  updatedPhases[2] = { ...updatedPhases[2], ...phase3Updates };
                  phasesUpdated = true;
                }
                
                // Update phase 4 (for simp_converter)
                if (numberOfPhases >= 4 && updatedPhases.length > 3 && preset.personalityAdjustments?.phase4) {
                  const phase4Updates = {};
                  phase4Updates.personalityAdjustment = preset.personalityAdjustments.phase4;
                  if (preset.livePhotoRates?.phase4 !== undefined) {
                    phase4Updates.livePhotoRate = preset.livePhotoRates.phase4;
                  }
                  phase4Updates.minimumExchanges = 3;
                  phase4Updates.name = preset.phase4?.description?.split(' - ')[0] || 'CTA Seed';
                  phase4Updates.interestLevel = 'high';
                  updatedPhases[3] = { ...updatedPhases[3], ...phase4Updates };
                  phasesUpdated = true;
                }
                
                // Update phase 5 (for simp_converter)
                if (numberOfPhases >= 5 && updatedPhases.length > 4 && preset.personalityAdjustments?.phase5) {
                  const phase5Updates = {};
                  phase5Updates.personalityAdjustment = preset.personalityAdjustments.phase5;
                  if (preset.livePhotoRates?.phase5 !== undefined) {
                    phase5Updates.livePhotoRate = preset.livePhotoRates.phase5;
                  }
                  phase5Updates.minimumExchanges = 2;
                  phase5Updates.name = preset.phase5?.description?.split(' - ')[0] || 'CTA Close';
                  phase5Updates.interestLevel = 'high';
                  updatedPhases[4] = { ...updatedPhases[4], ...phase5Updates };
                  phasesUpdated = true;
                }
                
                if (phasesUpdated) {
                  onNestedInputChange('conversationPhases', 'numberOfPhases', numberOfPhases);
                  onNestedInputChange('conversationPhases', 'phases', updatedPhases.slice(0, numberOfPhases));
                  
                  // Calculate and update CTA minimum exchanges
                  // Use the initial phase minimum exchanges that was just set
                  let totalExchanges = initialPhaseMinimumExchanges;
                  for (let i = 0; i < numberOfPhases && i < updatedPhases.length; i++) {
                    totalExchanges += updatedPhases[i]?.minimumExchanges || 3;
                  }
                  onNestedInputChange('ctaPhase', 'minimumExchangesBeforeCTA', totalExchanges);
                }
              } else {
                // Even if no personality adjustments or live photo rates, set default minimum exchanges
                onNestedInputChange('conversationPhases', 'initialPhase', {
                  ...formData.conversationPhases.initialPhase,
                  minimumExchanges: 4
                });
                
                const updatedPhases = [...formData.conversationPhases.phases];
                let phasesUpdated = false;
                
                if (updatedPhases.length > 0) {
                  updatedPhases[0] = {
                    ...updatedPhases[0],
                    minimumExchanges: 5
                  };
                  phasesUpdated = true;
                }
                
                if (updatedPhases.length > 1) {
                  updatedPhases[1] = {
                    ...updatedPhases[1],
                    minimumExchanges: 6
                  };
                  phasesUpdated = true;
                }
                
                if (phasesUpdated) {
                  onNestedInputChange('conversationPhases', 'phases', updatedPhases);
                }
              }
              
              // Apply CTA scripts
              if (preset.ctaScripts && preset.ctaScripts.length > 0) {
                const ctaScript = preset.ctaScripts[0];
                const ctaMessages = ctaScript.messages.map((msg, index) => ({
                  messageNumber: index + 1,
                  spintax: msg.spintax,
                  aiAugmentation: msg.aiAugmentation !== undefined ? msg.aiAugmentation : true,
                  includeLivePhoto: msg.includeLivePhoto !== undefined ? msg.includeLivePhoto : false,
                  photoCategory: 'cta'
                }));
                
                onNestedInputChange('ctaPhase', 'scripts', [{
                  scriptNumber: 1,
                  name: ctaScript.name || 'Preset Script',
                  isActive: true,
                  messages: ctaMessages,
                  successRate: 0,
                  totalUses: 0
                }]);
              }
              
              // Apply CTA Content Description based on preset
              const contentDescriptions = {
                'shy_girl': 'my private photos and videos where i show my real, intimate side... i get so shy sharing them but i love feeling close to you 🥺',
                'naughty_girl': 'my wildest, naughtiest content... exclusive pics and videos where i let loose and show you my real, seductive side 😈',
                'girl_next_door': 'my private page where i share my everyday life... but also my spicy side that only special people get to see 😊',
                'dominant_girl': 'my exclusive content where i take control... private photos and videos where i show you who\'s really in charge 👑',
                'innocent_girl': 'my private page where i share cute, innocent photos... but sometimes i get a little naughty too 🙈',
                'gamer_girl': 'my gaming content and cosplay photos... plus some exclusive, spicy content for my special fans 🎮'
              };
              
              if (contentDescriptions[newPreset]) {
                onNestedInputChange('ctaInfo', 'contentDescription', contentDescriptions[newPreset]);
              }
              
              // Apply Initial Phase Name based on preset
              const initialPhaseNames = {
                'shy_girl': 'Getting Comfortable',
                'naughty_girl': 'Breaking the Ice',
                'girl_next_door': 'First Contact',
                'dominant_girl': 'Establishing Connection',
                'innocent_girl': 'Sweet Introduction',
                'gamer_girl': 'Starting the Game',
                'simp_converter': 'Getting Comfortable'
              };
              
              if (initialPhaseNames[newPreset]) {
                onNestedInputChange('conversationPhases', 'initialPhase', {
                  ...formData.conversationPhases.initialPhase,
                  name: initialPhaseNames[newPreset]
                });
              }
              
              // Apply post-CTA phases for simp_converter preset
              if (newPreset === 'simp_converter' && preset.phase6 && preset.phase7) {
                const phase6Name = preset.phase6.description ? preset.phase6.description.split(' - ')[0] : 'Post-CTA Heat Build 1';
                const phase7Name = preset.phase7.description ? preset.phase7.description.split(' - ')[0] : 'Post-CTA Heat Build 2';
                
                const postCTAPhases = [
                  {
                    phaseNumber: 6,
                    name: phase6Name,
                    personalityAdjustment: preset.personalityAdjustments?.phase6 || '',
                    livePhotoRate: preset.livePhotoRates?.phase6 || 40,
                    minimumExchanges: preset.phase6?.minimumExchanges || 4,
                    interestLevel: 'very_high'
                  },
                  {
                    phaseNumber: 7,
                    name: phase7Name,
                    personalityAdjustment: preset.personalityAdjustments?.phase7 || '',
                    livePhotoRate: preset.livePhotoRates?.phase7 || 40,
                    minimumExchanges: preset.phase7?.minimumExchanges || 4,
                    interestLevel: 'very_high'
                  }
                ];
                
                onNestedInputChange('ctaPhase', 'enablePostCTAPhases', true);
                onNestedInputChange('ctaPhase', 'postCTAPhases', postCTAPhases);
              } else {
                // For other presets, disable post-CTA phases
                onNestedInputChange('ctaPhase', 'enablePostCTAPhases', false);
                onNestedInputChange('ctaPhase', 'postCTAPhases', []);
              }
              
              toast.success(`Preset "${preset.name}" applied successfully!`);
            } catch (error) {
              console.error('Error applying preset:', error);
              toast.error('Failed to apply preset. Please try again.');
            }
          }}
          className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          {presets.length > 0 ? (
            presets.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {preset.name} - {preset.description}
              </option>
            ))
          ) : (
            <>
              <option value="shy_girl">Shy Girl - Sweet and nervous, blushes easily, opens up slowly</option>
              <option value="naughty_girl">Naughty Girl - Playful and teasing, loves being mischievous, breaking rules, and sharing her wild, seductive side</option>
              <option value="girl_next_door">Girl Next Door - Sweet, approachable, and relatable, with an innocent everyday charm that turns surprisingly spicy</option>
              <option value="dominant_girl">Dominant Girl - Confident and in control, enjoys taking charge, giving commands, and exploring power dynamics</option>
              <option value="innocent_girl">Innocent Girl - Shy and blushing at first, with a cute, pure vibe that slowly reveals a hidden naughty curiosity</option>
              <option value="gamer_girl">Gamer Girl - Fun, nerdy, and energetic, mixes gaming passion with flirty cosplay and playful interactions</option>
            </>
          )}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          The preset changes the entire personality, traits, conversation phase behaviors, Model Info, Interaction Style, Personality Adjustments, and CTA scripts. This affects how the AI responds throughout all phases.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Platform</label>
          <select
            value={formData.platformType || 'fanvue'}
            onChange={(e) => handlePlatformTypeChange(e.target.value)}
            className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="fanvue">Fanvue</option>
            <option value="onlyfans">OnlyFans</option>
            <option value="fansly">Fansly</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        
        {formData.platformType === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom Platform Name
            </label>
            <input
              type="text"
              value={formData.customPlatformName || ''}
              onChange={(e) => handleCustomPlatformNameChange(e.target.value)}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., MyPlatform"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Link will default to: https://{formData.customPlatformName?.toLowerCase().replace(/\s+/g, '') || 'yourplatform'}.com/
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Model Info</label>
      <textarea
        value={formData.modelInfo}
        onChange={(e) => onInputChange('modelInfo', e.target.value)}
        className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        rows="3"
        placeholder="Background information and personality traits..."
        required
      />
      </div>

      {/* Appearance - optional; used so the AI can describe how the model looks when asked */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Appearance (optional)</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Helps the AI answer questions like &quot;what do you look like?&quot; consistently. Leave blank if not needed.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Hair color</label>
            <input
              type="text"
              value={formData.hairColor || ''}
              onChange={(e) => onInputChange('hairColor', e.target.value)}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., blonde, brunette"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Eye color</label>
            <input
              type="text"
              value={formData.eyeColor || ''}
              onChange={(e) => onInputChange('eyeColor', e.target.value)}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., blue, brown"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Ethnicity</label>
            <input
              type="text"
              value={formData.ethnicity || ''}
              onChange={(e) => onInputChange('ethnicity', e.target.value)}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., Latina, mixed"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Physical description</label>
          <input
            type="text"
            value={formData.physicalDescription || ''}
            onChange={(e) => onInputChange('physicalDescription', e.target.value)}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white w-full"
            placeholder="e.g., curvy, petite, tall"
            maxLength={500}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Brief extra details (build, height, etc.)</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
      <input
        type="text"
        value={formData.city}
        onChange={(e) => onInputChange('city', e.target.value)}
        className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        placeholder="e.g., Los Angeles, London, Tokyo"
        required
      />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Timezone will be auto-detected based on city</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timezone (Auto-detected)</label>
      <input
        type="text"
        value={formData.timezone}
        className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-gray-100 dark:bg-gray-600"
        readOnly
        disabled
      />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Day Time Activity</label>
      <textarea
        value={formData.dayTimeActivity}
        onChange={(e) => onInputChange('dayTimeActivity', e.target.value)}
        className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        rows="2"
        placeholder="What you do during the day..."
        required
      />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Night Time Activity</label>
      <textarea
        value={formData.nightTimeActivity}
        onChange={(e) => onInputChange('nightTimeActivity', e.target.value)}
        className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        rows="2"
        placeholder="What you do at night..."
        required
      />
      </div>
    </div>
  );
};

const CTAInfoTab = ({ formData, onInputChange, onNestedInputChange, scrapeCategories = [] }) => {
  // Get all CTA links (primary + additional)
  const additionalLinks = formData.ctaInfo.additionalLinks || [];
  
  // Add a new link
  const handleAddLink = () => {
    const newLinks = [...additionalLinks, { url: '', label: '', category: '' }];
    onNestedInputChange('ctaInfo', 'additionalLinks', newLinks);
  };
  
  // Remove a link
  const handleRemoveLink = (index) => {
    const newLinks = additionalLinks.filter((_, i) => i !== index);
    onNestedInputChange('ctaInfo', 'additionalLinks', newLinks);
  };
  
  // Update a link
  const handleUpdateLink = (index, field, value) => {
    const newLinks = [...additionalLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    onNestedInputChange('ctaInfo', 'additionalLinks', newLinks);
  };

  const categoryOptions = scrapeCategories.length
    ? scrapeCategories
    : ['mixed', 'Gaming', 'Anime', 'Twitch', 'VTubers', 'Cosplay', 'OF creators'];
  
  return (
    <div className="space-y-6">
      {/* CTA Links Section */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
              <span className="text-lg mr-2">🔗</span>
              CTA Links
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Bind a tracking link to a scrape category — chatbot uses that link when the lead came from that source lane
            </p>
          </div>
          <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
            {1 + additionalLinks.length} link{(1 + additionalLinks.length) !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="space-y-3">
          {/* Primary Link */}
          <div className="flex items-start gap-2 p-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-600">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-sky-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              1
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded">
                  Primary
                </span>
                <select
                  value={formData.ctaInfo.primaryCategory || ''}
                  onChange={(e) => onNestedInputChange('ctaInfo', 'primaryCategory', e.target.value)}
                  className="form-input text-xs py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">No category (fallback / untagged)</option>
                  {categoryOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <input
                type="url"
                value={formData.ctaInfo.fanvueLink}
                onChange={(e) => onNestedInputChange('ctaInfo', 'fanvueLink', e.target.value)}
                className="form-input w-full text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="https://fanvue.com/your-model"
                required
              />
            </div>
          </div>

          {/* Snapchat-only CTA override */}
          <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              SC
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 rounded">
                  Snapchat only
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Optional tracking link for Snapchat chatbot. X automations keep using the primary link above.
              </p>
              <input
                type="url"
                value={formData.ctaInfo.snapchatLink || ''}
                onChange={(e) => onNestedInputChange('ctaInfo', 'snapchatLink', e.target.value)}
                className="form-input w-full text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="https://fanvue.com/your-model?s=snap (optional)"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
              CL
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-900/40 px-2 py-0.5 rounded">
                  Chat link only
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Sent only when this model replies on a chat link. X keeps the primary link. Snapchat keeps the Snapchat link.
              </p>
              <input
                type="url"
                value={formData.ctaInfo.chatLinkUrl || ''}
                onChange={(e) => onNestedInputChange('ctaInfo', 'chatLinkUrl', e.target.value)}
                className="form-input w-full text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="https://onlyfans.com/your-model?s=chat (optional)"
              />
            </div>
          </div>
          
          {/* Additional Links */}
          {additionalLinks.map((link, index) => (
            <div key={index} className="flex items-start gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 group">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {index + 2}
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={link.label || ''}
                    onChange={(e) => handleUpdateLink(index, 'label', e.target.value)}
                    className="form-input w-32 text-xs dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Label (optional)"
                  />
                  <select
                    value={link.category || ''}
                    onChange={(e) => handleUpdateLink(index, 'category', e.target.value)}
                    className="form-input text-xs py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">No category</option>
                    {categoryOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="url"
                  value={link.url || ''}
                  onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                  className="form-input w-full text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://..."
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveLink(index)}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors opacity-0 group-hover:opacity-100"
                title="Remove link"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
          
          {/* Add Link Button */}
          <button
            type="button"
            onClick={handleAddLink}
            className="w-full py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-sm text-gray-500 dark:text-gray-400 hover:border-purple-400 hover:text-purple-500 dark:hover:border-purple-500 dark:hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Another Link
          </button>
        </div>
        
        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
          <p className="text-xs text-blue-600 dark:text-blue-300 flex items-start gap-2">
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              When a conversation has a scrape category, the matching link is used.
              Untagged leads fall back to links without a category, then random.
              Manage categories under Scraper V2 → Categories.
            </span>
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isFree"
            checked={formData.ctaInfo.isFree || false}
            onChange={(e) => {
              const isFree = e.target.checked;
              onNestedInputChange('ctaInfo', 'isFree', isFree);
              // If switching to free, clear the price
              if (isFree) {
                onNestedInputChange('ctaInfo', 'monthlyPrice', null);
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isFree" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Free (no subscription fee)
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Monthly Price ($)
            {formData.ctaInfo.isFree && <span className="text-gray-500 text-xs ml-2">(disabled when free)</span>}
          </label>
          <input
            type="number"
            value={formData.ctaInfo.monthlyPrice || ''}
            onChange={(e) => {
              const price = e.target.value ? parseInt(e.target.value) : null;
              onNestedInputChange('ctaInfo', 'monthlyPrice', price);
              // If price is set, ensure isFree is false
              if (price != null && price > 0) {
                onNestedInputChange('ctaInfo', 'isFree', false);
              }
            }}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            min="1"
            disabled={formData.ctaInfo.isFree}
            required={!formData.ctaInfo.isFree}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Activity Level</label>
          <select
            value={formData.ctaInfo.activityLevel}
            onChange={(e) => onNestedInputChange('ctaInfo', 'activityLevel', e.target.value)}
            className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="occasionally">Occasionally</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content Description</label>
        <textarea
          value={formData.ctaInfo.contentDescription}
          onChange={(e) => onNestedInputChange('ctaInfo', 'contentDescription', e.target.value)}
          className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          rows="3"
          placeholder="Describe what content you offer..."
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interaction Style</label>
        <textarea
          value={formData.ctaInfo.interactionStyle}
          onChange={(e) => onNestedInputChange('ctaInfo', 'interactionStyle', e.target.value)}
          className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          rows="3"
          placeholder="How you interact with subscribers..."
          required
        />
      </div>
    </div>
  );
};

const ConversationPhasesTab = ({ formData, onInputChange, onNestedInputChange }) => {
  // Helper function to calculate total minimum exchanges from all phases
  const calculateMinimumExchangesBeforeCTA = (phases, initialPhase, numberOfPhases) => {
    let total = 0;
    
    // Add initial phase minimum exchanges
    total += initialPhase?.minimumExchanges || 4;
    
    // Add all regular phases minimum exchanges
    for (let i = 0; i < numberOfPhases && i < phases.length; i++) {
      total += phases[i]?.minimumExchanges || 3;
    }
    
    return total;
  };

  // Helper function to update CTA minimum exchanges when phases change
  const updateCTAMinimumExchanges = (updatedPhases, updatedInitialPhase, updatedNumberOfPhases) => {
    const phases = updatedPhases || formData.conversationPhases.phases;
    const initialPhase = updatedInitialPhase || formData.conversationPhases.initialPhase;
    const numberOfPhases = updatedNumberOfPhases || formData.conversationPhases.numberOfPhases;
    
    const total = calculateMinimumExchangesBeforeCTA(phases, initialPhase, numberOfPhases);
    onNestedInputChange('ctaPhase', 'minimumExchangesBeforeCTA', total);
  };

  const updateNumberOfPhases = (newNumber) => {
    const currentPhases = formData.conversationPhases.phases;
    const newPhases = [];
    
    for (let i = 1; i <= newNumber; i++) {
      if (currentPhases[i - 1]) {
        newPhases.push(currentPhases[i - 1]);
      } else {
        newPhases.push({
          phaseNumber: i,
          name: `Phase ${i}`,
          interestLevel: 'low',
          livePhotoRate: 30,
          personalityAdjustment: '',
          minimumExchanges: 3
        });
      }
    }
    
    onNestedInputChange('conversationPhases', 'phases', newPhases);
    onNestedInputChange('conversationPhases', 'numberOfPhases', newNumber);
    // Auto-update CTA minimum exchanges
    setTimeout(() => {
      updateCTAMinimumExchanges(newPhases, null, newNumber);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Initial Phase Configuration */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4 bg-blue-50 dark:bg-blue-900/20">
        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
          Initial Phase
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phase Name</label>
            <input
              type="text"
              value={formData.conversationPhases.initialPhase?.name || 'Initial Contact'}
              onChange={(e) => onNestedInputChange('conversationPhases', 'initialPhase', { 
                ...formData.conversationPhases.initialPhase, 
                name: e.target.value 
              })}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interest Level</label>
            <select
              value={formData.conversationPhases.initialPhase?.interestLevel || 'low'}
              onChange={(e) => onNestedInputChange('conversationPhases', 'initialPhase', { 
                ...formData.conversationPhases.initialPhase, 
                interestLevel: e.target.value 
              })}
              className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="very_high">Very High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Live Photo Rate (%)</label>
            <input
              type="number"
              value={formData.conversationPhases.initialPhase?.livePhotoRate ?? 0}
              onChange={(e) => onNestedInputChange('conversationPhases', 'initialPhase', { 
                ...formData.conversationPhases.initialPhase, 
                livePhotoRate: parseInt(e.target.value) 
              })}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minimum Exchanges</label>
            <input
              type="number"
              value={formData.conversationPhases.initialPhase?.minimumExchanges ?? 2}
              onChange={(e) => {
                const updatedInitialPhase = { 
                  ...formData.conversationPhases.initialPhase, 
                  minimumExchanges: parseInt(e.target.value) 
                };
                onNestedInputChange('conversationPhases', 'initialPhase', updatedInitialPhase);
                // Auto-update CTA minimum exchanges
                setTimeout(() => {
                  updateCTAMinimumExchanges(null, updatedInitialPhase, null);
                }, 0);
              }}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              min="1"
              max="20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Personality Adjustment</label>
            <textarea
              value={formData.conversationPhases.initialPhase?.personalityAdjustment || 'Be friendly and welcoming, start conversations naturally'}
              onChange={(e) => onNestedInputChange('conversationPhases', 'initialPhase', { 
                ...formData.conversationPhases.initialPhase, 
                personalityAdjustment: e.target.value 
              })}
              className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows="2"
              placeholder="How to adjust personality for initial phase..."
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Number of Phases</label>
          <select
            value={formData.conversationPhases.numberOfPhases}
            onChange={(e) => updateNumberOfPhases(parseInt(e.target.value))}
            className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="1">1 Phase</option>
            <option value="2">2 Phases</option>
            <option value="3">3 Phases</option>
            <option value="4">4 Phases</option>
            <option value="5">5 Phases</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Min Exchanges per Phase</label>
          <input
            type="number"
            value={formData.conversationPhases.minimumExchangesPerPhase}
            onChange={(e) => {
              onNestedInputChange('conversationPhases', 'minimumExchangesPerPhase', parseInt(e.target.value));
              // Note: This is a fallback value, individual phase minimumExchanges take precedence
              // CTA calculation uses individual phase values, not this global setting
            }}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            min="1"
            max="20"
          />
        </div>
      </div>

      {formData.conversationPhases.phases.map((phase, index) => (
        <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4">
          <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Phase {phase.phaseNumber}</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phase Name</label>
              <input
                type="text"
                value={phase.name}
                onChange={(e) => {
                  const newPhases = [...formData.conversationPhases.phases];
                  newPhases[index].name = e.target.value;
                  onNestedInputChange('conversationPhases', 'phases', newPhases);
                }}
                className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interest Level</label>
              <select
                value={phase.interestLevel}
                onChange={(e) => {
                  const newPhases = [...formData.conversationPhases.phases];
                  newPhases[index].interestLevel = e.target.value;
                  onNestedInputChange('conversationPhases', 'phases', newPhases);
                }}
                className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="very_high">Very High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Live Photo Rate (%)</label>
              <input
                type="number"
                value={phase.livePhotoRate}
                onChange={(e) => {
                  const newPhases = [...formData.conversationPhases.phases];
                  newPhases[index].livePhotoRate = parseInt(e.target.value);
                  onNestedInputChange('conversationPhases', 'phases', newPhases);
                }}
                className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minimum Exchanges</label>
              <input
                type="number"
                value={phase.minimumExchanges ?? 3}
                onChange={(e) => {
                  const newPhases = [...formData.conversationPhases.phases];
                  newPhases[index].minimumExchanges = parseInt(e.target.value);
                  onNestedInputChange('conversationPhases', 'phases', newPhases);
                  // Auto-update CTA minimum exchanges
                  setTimeout(() => {
                    updateCTAMinimumExchanges(newPhases, null, null);
                  }, 0);
                }}
                className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="1"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Personality Adjustment</label>
              <textarea
                value={phase.personalityAdjustment}
                onChange={(e) => {
                  const newPhases = [...formData.conversationPhases.phases];
                  newPhases[index].personalityAdjustment = e.target.value;
                  onNestedInputChange('conversationPhases', 'phases', newPhases);
                }}
                className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows="2"
                placeholder="How to adjust personality for this phase..."
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CTAPhaseTab = ({ formData, onInputChange, onNestedInputChange }) => {
  const addMessage = (scriptIndex) => {
    const newScripts = [...formData.ctaPhase.scripts];
    const newMessageNumber = newScripts[scriptIndex].messages.length + 1;
    // Only message 2 should have Live Photo enabled by default
    newScripts[scriptIndex].messages.push({
      messageNumber: newMessageNumber,
      spintax: '',
      aiAugmentation: true,
      includeLivePhoto: newMessageNumber === 2,
      photoCategory: 'cta'
    });
    onNestedInputChange('ctaPhase', 'scripts', newScripts);
  };

  const removeMessage = (scriptIndex, messageIndex) => {
    const newScripts = [...formData.ctaPhase.scripts];
    newScripts[scriptIndex].messages.splice(messageIndex, 1);
    // Renumber messages
    newScripts[scriptIndex].messages.forEach((msg, idx) => {
      msg.messageNumber = idx + 1;
    });
    onNestedInputChange('ctaPhase', 'scripts', newScripts);
  };

  const addScript = () => {
    const newScriptNumber = formData.ctaPhase.scripts.length + 1;
    const newScript = {
      scriptNumber: newScriptNumber,
      name: `Script ${newScriptNumber}`,
      isActive: true,
      messages: [
        {
          messageNumber: 1,
          spintax: '',
          aiAugmentation: true,
          includeLivePhoto: false,
          photoCategory: 'cta'
        }
      ],
      successRate: 0,
      totalUses: 0
    };
    onNestedInputChange('ctaPhase', 'scripts', [...formData.ctaPhase.scripts, newScript]);
  };

  const togglePostCTAPhases = (enabled) => {
    if (enabled) {
      // Initialize post-CTA phases if they don't exist
      const postCTAPhases = formData.ctaPhase.postCTAPhases || [];
      const newPhases = [];
      
      // Add phase 6 if not exists
      if (!postCTAPhases.find(p => p.phaseNumber === 6)) {
        newPhases.push({
          phaseNumber: 6,
          name: 'Post-CTA Phase 1',
          personalityAdjustment: '',
          livePhotoRate: 30,
          minimumExchanges: 3,
          interestLevel: 'high'
        });
      } else {
        newPhases.push(postCTAPhases.find(p => p.phaseNumber === 6));
      }
      
      // Add phase 7 if not exists
      if (!postCTAPhases.find(p => p.phaseNumber === 7)) {
        newPhases.push({
          phaseNumber: 7,
          name: 'Post-CTA Phase 2',
          personalityAdjustment: '',
          livePhotoRate: 30,
          minimumExchanges: 3,
          interestLevel: 'high'
        });
      } else {
        newPhases.push(postCTAPhases.find(p => p.phaseNumber === 7));
      }
      
      onNestedInputChange('ctaPhase', 'enablePostCTAPhases', true);
      onNestedInputChange('ctaPhase', 'postCTAPhases', newPhases);
    } else {
      onNestedInputChange('ctaPhase', 'enablePostCTAPhases', false);
      onNestedInputChange('ctaPhase', 'postCTAPhases', []);
    }
  };

  const updatePostCTAPhase = (phaseNumber, field, value) => {
    const postCTAPhases = [...(formData.ctaPhase.postCTAPhases || [])];
    const phaseIndex = postCTAPhases.findIndex(p => p.phaseNumber === phaseNumber);
    
    if (phaseIndex >= 0) {
      postCTAPhases[phaseIndex] = {
        ...postCTAPhases[phaseIndex],
        [field]: value
      };
    } else {
      postCTAPhases.push({
        phaseNumber,
        name: phaseNumber === 6 ? 'Post-CTA Phase 1' : 'Post-CTA Phase 2',
        personalityAdjustment: '',
        livePhotoRate: 30,
        minimumExchanges: 3,
        interestLevel: 'high',
        [field]: value
      });
    }
    
    onNestedInputChange('ctaPhase', 'postCTAPhases', postCTAPhases);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minimum Exchanges Before CTA</label>
        <input
          type="number"
          value={formData.ctaPhase.minimumExchangesBeforeCTA}
          onChange={(e) => onNestedInputChange('ctaPhase', 'minimumExchangesBeforeCTA', parseInt(e.target.value))}
          className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          min="1"
          max="50"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Exit Message</label>
        <textarea
          value={formData.ctaPhase.exitMessage || ''}
          onChange={(e) => onNestedInputChange('ctaPhase', 'exitMessage', e.target.value)}
          className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          rows="3"
          placeholder={getDefaultExitMessage(formData.platformType || 'fanvue', formData.customPlatformName || '')}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Sent as the final post-CTA exit line. Default follows the selected platform.
        </p>
      </div>

      {/* Post-CTA Phases Toggle */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4 bg-blue-50 dark:bg-blue-900/20">
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id="enablePostCTAPhases"
            checked={formData.ctaPhase.enablePostCTAPhases || false}
            onChange={(e) => togglePostCTAPhases(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="enablePostCTAPhases" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Add phases after CTA script (max 2 phases)
          </label>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
          Enable additional conversation phases after the CTA link has been shared. These phases can build heat and push users towards subscribing without repeating the link.
        </p>

        {/* Post-CTA Phase Configuration */}
        {formData.ctaPhase.enablePostCTAPhases && (
          <div className="mt-4 space-y-4">
            {(formData.ctaPhase.postCTAPhases || []).map((phase, index) => (
              <div key={phase.phaseNumber} className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 bg-white dark:bg-gray-800">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Phase {phase.phaseNumber} - {phase.name || (phase.phaseNumber === 6 ? 'Post-CTA Phase 1' : 'Post-CTA Phase 2')}
                </h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phase Name</label>
                    <input
                      type="text"
                      value={phase.name || ''}
                      onChange={(e) => updatePostCTAPhase(phase.phaseNumber, 'name', e.target.value)}
                      className="form-input text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={phase.phaseNumber === 6 ? 'Post-CTA Phase 1' : 'Post-CTA Phase 2'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Minimum Exchanges</label>
                    <input
                      type="number"
                      value={phase.minimumExchanges || 3}
                      onChange={(e) => updatePostCTAPhase(phase.phaseNumber, 'minimumExchanges', parseInt(e.target.value))}
                      className="form-input text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      min="1"
                      max="20"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Live Photo Rate (%)</label>
                    <input
                      type="number"
                      value={phase.livePhotoRate || 30}
                      onChange={(e) => updatePostCTAPhase(phase.phaseNumber, 'livePhotoRate', parseInt(e.target.value))}
                      className="form-input text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Interest Level</label>
                    <select
                      value={phase.interestLevel || 'high'}
                      onChange={(e) => updatePostCTAPhase(phase.phaseNumber, 'interestLevel', e.target.value)}
                      className="form-select text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="very_high">Very High</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Personality Adjustment</label>
                  <textarea
                    value={phase.personalityAdjustment || ''}
                    onChange={(e) => updatePostCTAPhase(phase.phaseNumber, 'personalityAdjustment', e.target.value)}
                    className="form-textarea text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    rows="3"
                    placeholder="How to adjust personality for this phase after CTA..."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {phase.phaseNumber === 6 
                      ? "Build heat and create FOMO. Reference what they'll see. DO NOT share the link again."
                      : "Maximum push. Be direct about what they're missing. Create urgency. Still NO link."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">CTA Scripts</h4>
        <button
          type="button"
          onClick={addScript}
          className="btn btn-secondary btn-sm"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Script
        </button>
      </div>

      {formData.ctaPhase.scripts.map((script, scriptIndex) => (
        <div key={scriptIndex} className="border border-gray-200 dark:border-gray-600 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-gray-900 dark:text-white">Script {script.scriptNumber}</h5>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={script.isActive}
                onChange={(e) => {
                  const newScripts = [...formData.ctaPhase.scripts];
                  newScripts[scriptIndex].isActive = e.target.checked;
                  onNestedInputChange('ctaPhase', 'scripts', newScripts);
                }}
                className="form-checkbox"
              />
              <label className="text-sm text-gray-700 dark:text-gray-300">Active</label>
            </div>
          </div>
          
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Script Name</label>
            <input
              type="text"
              value={script.name}
              onChange={(e) => {
                const newScripts = [...formData.ctaPhase.scripts];
                newScripts[scriptIndex].name = e.target.value;
                onNestedInputChange('ctaPhase', 'scripts', newScripts);
              }}
              className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>

          <div className="flex justify-between items-center mb-3">
            <h6 className="text-sm font-medium text-gray-900 dark:text-white">Messages</h6>
            <button
              type="button"
              onClick={() => addMessage(scriptIndex)}
              className="btn btn-secondary btn-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Message
            </button>
          </div>

          {script.messages.map((message, messageIndex) => (
            <div key={messageIndex} className="border border-gray-100 dark:border-gray-700 rounded p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-xs font-medium text-gray-900 dark:text-white">Message {message.messageNumber}</h6>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => removeMessage(scriptIndex, messageIndex)}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-4 mb-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={message.aiAugmentation}
                    onChange={(e) => {
                      const newScripts = [...formData.ctaPhase.scripts];
                      newScripts[scriptIndex].messages[messageIndex].aiAugmentation = e.target.checked;
                      onNestedInputChange('ctaPhase', 'scripts', newScripts);
                    }}
                    className="form-checkbox"
                  />
                  <label className="text-xs text-gray-700 dark:text-gray-300">AI Augmentation</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={message.includeLivePhoto}
                    onChange={(e) => {
                      const newScripts = [...formData.ctaPhase.scripts];
                      newScripts[scriptIndex].messages[messageIndex].includeLivePhoto = e.target.checked;
                      onNestedInputChange('ctaPhase', 'scripts', newScripts);
                    }}
                    className="form-checkbox"
                  />
                  <label className="text-xs text-gray-700 dark:text-gray-300">Live Photo</label>
                </div>
              </div>
              <textarea
                value={message.spintax}
                onChange={(e) => {
                  const newScripts = [...formData.ctaPhase.scripts];
                  newScripts[scriptIndex].messages[messageIndex].spintax = e.target.value;
                  onNestedInputChange('ctaPhase', 'scripts', newScripts);
                }}
                className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows="2"
                placeholder="Message content with spintax..."
                required
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const PhotoManagementTab = ({ formData, onInputChange, onNestedInputChange, isDarkMode, serviceUrls = {} }) => {
  const defaultSpiceForCategory = (category) => ({ main: 3, cta: 5, sexy: 8 }[category] || 5);

  const getPhotoSpice = (photo) => {
    const n = photo?.spiceLevel;
    if (typeof n === 'number' && n >= 1 && n <= 10) return n;
    return defaultSpiceForCategory(photo?.category || 'main');
  };

  const spiceBadgeClass = (level) => {
    if (level <= 3) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    if (level <= 6) return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200';
  };

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]); // Photos uploaded but not yet saved
  const [uploadingFiles, setUploadingFiles] = useState(new Map()); // Track upload status per file
  const [uploadData, setUploadData] = useState({
    category: 'main',
    day: 1,
    timeOfDay: 'either',
    outfit: '',
    description: '',
    spiceLevel: 3
  });
  
  // Edit photo state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editData, setEditData] = useState({
    category: 'main',
    day: 1,
    timeOfDay: 'either',
    outfit: '',
    description: '',
    spiceLevel: 3
  });
  const [replacementFile, setReplacementFile] = useState(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Create file objects with preview URLs and metadata (keep original File object)
    const filesWithPreview = files.map((file, index) => {
      const id = Date.now() + index + Math.random(); // Unique ID for tracking
      const preview = URL.createObjectURL(file);
      return {
        file: file, // Keep original File object
        preview: preview,
        id: id,
        name: file.name,
        uploadStatus: 'pending' // pending, uploading, success, error
      };
    });

    // Add files to selectedFiles for preview
    setSelectedFiles(prev => [...prev, ...filesWithPreview]);

    // Upload each file immediately, one by one
    for (const fileData of filesWithPreview) {
      await uploadSingleFile(fileData);
    }

    // Reset file input
    e.target.value = '';
  };

  const uploadSingleFile = async (fileData) => {
    // Mark as uploading
    setUploadingFiles(prev => new Map(prev).set(fileData.id, 'uploading'));
    setSelectedFiles(prev => prev.map(f => 
      f.id === fileData.id ? { ...f, uploadStatus: 'uploading' } : f
    ));

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('photo', fileData.file); // Use original File object
      formDataToSend.append('category', uploadData.category);
      formDataToSend.append('day', uploadData.day);
      formDataToSend.append('timeOfDay', uploadData.timeOfDay);
      formDataToSend.append('outfit', uploadData.outfit);
      formDataToSend.append('description', uploadData.description);
      formDataToSend.append('spiceLevel', String(uploadData.spiceLevel ?? defaultSpiceForCategory(uploadData.category)));

      let response;
      const automationUrl = serviceUrls.automation;
      if (automationUrl) {
        response = await axios.post(`${automationUrl}/api/models/${formData._id}/photos/single`, formDataToSend, {
          timeout: 60000,
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        response = await api.post(`/models/${formData._id}/photos/single`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Mark as success
      setUploadingFiles(prev => {
        const newMap = new Map(prev);
        newMap.set(fileData.id, 'success');
        return newMap;
      });
      setSelectedFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, uploadStatus: 'success' } : f
      ));

      // Add to pending photos (not saved to model yet)
      const photoWithFileData = {
        ...response.data.photo,
        fileDataId: fileData.id, // Link back to file data for removal
        filename: response.data.photo.filename || (response.data.photo.url ? response.data.photo.url.split('/').pop() : null),
        localPreviewUrl: fileData.preview || null,
      };
      setPendingPhotos(prev => [...prev, photoWithFileData]);
      
      toast.success(`Photo "${fileData.name}" uploaded (click Save to add to model)`);
    } catch (error) {
      // Mark as error
      setUploadingFiles(prev => {
        const newMap = new Map(prev);
        newMap.set(fileData.id, 'error');
        return newMap;
      });
      setSelectedFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, uploadStatus: 'error' } : f
      ));

      toast.error(`Failed to upload "${fileData.name}": ${error.response?.data?.error || error.message}`);
      console.error('Error uploading photo:', error);
    }
  };

  // Remove pending photo
  const handleRemovePendingPhoto = async (photo) => {
    try {
      // Delete file from server
      if (photo.filename) {
        await api.delete(`/models/${formData._id}/photos/staged/${photo.filename}`);
      }
      
      // Remove from pending photos
      setPendingPhotos(prev => prev.filter(p => p.fileDataId !== photo.fileDataId));
      
      // Remove from selected files
      setSelectedFiles(prev => {
        const updated = prev.filter(f => f.id !== photo.fileDataId);
        // Clean up preview URL
        const removed = prev.find(f => f.id === photo.fileDataId);
        if (removed && removed.preview) {
          URL.revokeObjectURL(removed.preview);
        }
        return updated;
      });
      
      toast.success('Photo removed');
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error('Failed to remove photo');
    }
  };

  // Save pending photos to model
  const handleSavePhotos = async () => {
    if (pendingPhotos.length === 0) {
      toast.error('No photos to save');
      return;
    }

    try {
      // Apply current uploadData values to all pending photos (respects user's current selections)
      const photosToSave = pendingPhotos.map(photo => {
        const { fileDataId, filename, displayUrl, localPreviewUrl, ...photoData } = photo;
        // Override with current uploadData values (Category, Day, Time of Day)
        return {
          ...photoData,
          category: uploadData.category,
          day: uploadData.day,
          timeOfDay: uploadData.timeOfDay,
          outfit: uploadData.outfit || photoData.outfit || '',
          description: uploadData.description || photoData.description || '',
          spiceLevel: uploadData.spiceLevel ?? defaultSpiceForCategory(uploadData.category)
        };
      });

      const payload = { photos: photosToSave };
      let response;
      if (serviceUrls.automation) {
        // Cross-origin to automation: obfuscate body here so backend always receives obfuscated format
        response = await axios.post(
          `${serviceUrls.automation}/api/models/${formData._id}/photos/save`,
          { 'nice-try': obfuscate(payload) },
          {
            timeout: 60000,
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
      } else {
        response = await api.post(`/models/${formData._id}/photos/save`, payload);
      }

      // Update form data with saved photos
      onInputChange('photos', [...(formData.photos || []), ...response.data.photos]);
      
      // Update profile picture if this is the first photo and profile picture is not set
      if (!formData.profilePicture && response.data.photos.length > 0 && response.data.photos[0].url) {
        onInputChange('profilePicture', response.data.photos[0].url);
      }
      
      // Clean up
      selectedFiles.forEach(fileData => {
        if (fileData.preview) {
          URL.revokeObjectURL(fileData.preview);
        }
      });
      
      setShowUploadModal(false);
      setSelectedFiles([]);
      setPendingPhotos([]);
      setUploadingFiles(new Map());
      setUploadData({
        category: 'main',
        day: 1,
        timeOfDay: 'either',
        outfit: '',
        description: ''
      });
      
      toast.success(`${response.data.photos.length} photo(s) saved successfully`);
    } catch (error) {
      console.error('Error saving photos:', error);
      toast.error(`Failed to save photos: ${error.response?.data?.error || error.message}`);
    }
  };

  // Clean up function for closing modal without saving
  const handleCloseUploadModal = async () => {
    // Delete all pending photos from server
    const automationUrl = serviceUrls.automation;
    for (const photo of pendingPhotos) {
      try {
        if (photo.filename) {
          if (automationUrl) {
            await axios.delete(`${automationUrl}/api/models/${formData._id}/photos/staged/${photo.filename}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
          } else {
            await api.delete(`/models/${formData._id}/photos/staged/${photo.filename}`);
          }
        }
      } catch (error) {
        console.error('Error deleting staged photo:', error);
      }
    }
    
    // Clean up object URLs
    selectedFiles.forEach(fileData => {
      if (fileData.preview) {
        URL.revokeObjectURL(fileData.preview);
      }
    });
    
    setShowUploadModal(false);
    setSelectedFiles([]);
    setPendingPhotos([]);
    setUploadingFiles(new Map());
    setUploadData({
      category: 'main',
      day: 1,
      timeOfDay: 'either',
      outfit: '',
      description: ''
    });
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      const automationUrl = serviceUrls.automation;
      if (automationUrl) {
        await axios.delete(`${automationUrl}/api/models/${formData._id}/photos/${photoId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await api.delete(`/models/${formData._id}/photos/${photoId}`);
      }
      onInputChange('photos', formData.photos.filter(photo => photo._id !== photoId));
      toast.success('Photo deleted successfully');
    } catch (error) {
      toast.error('Failed to delete photo');
      console.error('Error deleting photo:', error);
    }
  };

  const handleEditPhoto = (photo, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingPhoto(photo);
    setEditData({
      category: photo.category,
      day: photo.day,
      timeOfDay: photo.timeOfDay,
      outfit: photo.outfit || '',
      description: photo.description || '',
      spiceLevel: getPhotoSpice(photo)
    });
    setReplacementFile(null);
    setShowEditModal(true);
  };

  const handleReplacementFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReplacementFile(file);
    }
  };

  const handleUpdatePhoto = async () => {
    if (!editingPhoto) return;

    try {
      const formDataToSend = new FormData();
      
      // If replacing the photo file
      if (replacementFile) {
        formDataToSend.append('photo', replacementFile);
      }
      
      // Add updated metadata
      formDataToSend.append('category', editData.category);
      formDataToSend.append('day', editData.day);
      formDataToSend.append('timeOfDay', editData.timeOfDay);
      formDataToSend.append('outfit', editData.outfit);
      formDataToSend.append('description', editData.description);
      formDataToSend.append('spiceLevel', String(editData.spiceLevel ?? defaultSpiceForCategory(editData.category)));

      let response;
      const automationUrl = serviceUrls.automation;
      if (automationUrl) {
        response = await axios.put(`${automationUrl}/api/models/${formData._id}/photos/${editingPhoto._id}`, formDataToSend, {
          timeout: 60000,
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        response = await api.put(`/models/${formData._id}/photos/${editingPhoto._id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Update the form data with the updated photo
      const updatedPhotos = formData.photos.map(photo => 
        photo._id === editingPhoto._id ? response.data.photo : photo
      );
      onInputChange('photos', updatedPhotos);
      
      setShowEditModal(false);
      setEditingPhoto(null);
      setReplacementFile(null);
      toast.success('Photo updated successfully');
    } catch (error) {
      toast.error('Failed to update photo');
      console.error('Error updating photo:', error);
    }
  };

  // const refreshModelData = async () => {
  //   try {
  //     const response = await api.get(`/models/${formData._id}`);
  //     onInputChange('photos', response.data.photos || []);
  //   } catch (error) {
  //     console.error('Error refreshing model data:', error);
  //   }
  // }; // Unused - commented out for build

  const getPhotoUsage = (photo) => {
    const usages = [];
    if (photo.category === 'main') usages.push('Main Profile');
    if (photo.category === 'cta') usages.push('CTA Messages');
    if (photo.category === 'sexy') usages.push('Sexy Content');
    if (photo.day) usages.push(`Day ${photo.day}`);
    if (photo.timeOfDay !== 'either') usages.push(photo.timeOfDay);
    return usages.join(' • ');
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'main': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'cta': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'sexy': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">Photo Management</h4>
        <button
          type="button"
          onClick={() => setShowUploadModal(true)}
          className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
              : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
          }`}
          disabled={!formData._id}
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Photos
        </button>
      </div>

      {!formData._id && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Model will be saved as draft automatically. You can add photos after the draft is created.
          </p>
        </div>
      )}

      {formData.photos && formData.photos.length > 0 ? (
        <div className="space-y-6">
          {[1, 2, 3, 4, 5].map(day => {
            const dayPhotos = formData.photos.filter(photo => photo.day === day);
            if (dayPhotos.length === 0) return null;
            
            return (
              <div key={day} className="space-y-3">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 pb-2">
                  Day {day} ({dayPhotos.length} photo{dayPhotos.length !== 1 ? 's' : ''})
                </h5>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                  {dayPhotos.map((photo) => (
                    <div key={photo._id} className="border border-gray-200 dark:border-gray-600 rounded-2xl p-2">
                      <div className="relative">
                        <div className="aspect-[3/4] w-full bg-gray-100 dark:bg-gray-700 rounded-2xl overflow-hidden">
                          <img
                            src={resolveAssetUrl(photo.url, photo.displayUrl, serviceUrls.automation)}
                            alt={photo.description || 'Model photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute top-1 right-1 flex space-x-1">
                          <button
                            type="button"
                            onClick={(e) => handleEditPhoto(photo, e)}
                            className="bg-blue-500 text-white rounded-full p-0.5 hover:bg-blue-600"
                            title="Edit photo"
                          >
                            <PencilIcon className="h-2.5 w-2.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(photo._id)}
                            className="bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                            title="Delete photo"
                          >
                            <TrashIcon className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        <div className="flex items-center justify-between gap-1 flex-wrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(photo.category)}`}>
                            {photo.category}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${spiceBadgeClass(getPhotoSpice(photo))}`}>
                            spice {getPhotoSpice(photo)}/10
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {photo.timeOfDay}
                          </span>
                        </div>
                        {photo.outfit && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {photo.outfit}
                          </p>
                        )}
                        {photo.description && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {photo.description}
                          </p>
                        )}
                        <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                          {getPhotoUsage(photo)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No photos yet</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add photos to use in conversations and CTA messages.
          </p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseUploadModal}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Upload Photos</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Photos</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={Array.from(uploadingFiles.values()).some(status => status === 'uploading')}
                    />
                    {(selectedFiles.length > 0 || pendingPhotos.length > 0) && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {pendingPhotos.length > 0 && (
                            <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                              {pendingPhotos.length} photo{pendingPhotos.length !== 1 ? 's' : ''} ready to save
                            </span>
                          )}
                          {selectedFiles.some(f => f.uploadStatus === 'uploading') && (
                            <span className="ml-2">
                              {selectedFiles.filter(f => f.uploadStatus === 'uploading').length} uploading...
                            </span>
                          )}
                        </p>
                        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                          {/* Show pending photos (uploaded, ready to save) */}
                          {pendingPhotos.map((photo, index) => (
                            <div key={`pending-${photo.fileDataId || index}`} className="relative group">
                              <div className="aspect-[3/4] w-full bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border-2 border-yellow-400 dark:border-yellow-500 relative">
                                <img
                                  src={
                                    photo.localPreviewUrl ||
                                    resolveAssetUrl(photo.url, photo.displayUrl, serviceUrls.automation)
                                  }
                                  alt={photo.description || 'Pending photo'}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-yellow-500 bg-opacity-10 flex items-center justify-center">
                                  <div className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">Ready</div>
                                </div>
                                {/* Remove button */}
                                <button
                                  type="button"
                                  onClick={() => handleRemovePendingPhoto(photo)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove photo"
                                >
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                          
                          {/* Show files being uploaded */}
                          {selectedFiles.map((file, index) => {
                            const uploadStatus = file.uploadStatus || 'pending';
                            const isUploading = uploadStatus === 'uploading';
                            const isError = uploadStatus === 'error';
                            const isPending = uploadStatus === 'pending';
                            
                            // Skip if already in pendingPhotos (uploaded successfully)
                            if (uploadStatus === 'success') return null;
                            
                            return (
                              <div key={file.id || index} className="relative">
                                <div className="aspect-[3/4] w-full bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 relative">
                                  <img
                                    src={file.preview}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {/* Upload status overlay */}
                                  {isUploading && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                      <div className="text-white text-sm">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                                        Uploading...
                                      </div>
                                    </div>
                                  )}
                                  {isPending && (
                                    <div className="absolute inset-0 bg-gray-500 bg-opacity-30 flex items-center justify-center">
                                      <div className="text-gray-700 dark:text-gray-300 text-xs">Pending</div>
                                    </div>
                                  )}
                                  {isError && (
                                    <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center">
                                      <div className="text-red-600 dark:text-red-400 text-2xl">✗</div>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {file.name}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                      <select
                        value={uploadData.category}
                        onChange={(e) => {
                          const category = e.target.value;
                          setUploadData((prev) => ({
                            ...prev,
                            category,
                            spiceLevel: defaultSpiceForCategory(category)
                          }));
                        }}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="main">Main Profile</option>
                        <option value="cta">CTA Messages</option>
                        <option value="sexy">Sexy Content</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Day</label>
                      <select
                        value={uploadData.day}
                        onChange={(e) => setUploadData(prev => ({ ...prev, day: parseInt(e.target.value) }))}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value={1}>Day 1</option>
                        <option value={2}>Day 2</option>
                        <option value={3}>Day 3</option>
                        <option value={4}>Day 4</option>
                        <option value={5}>Day 5</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Time of Day</label>
                      <select
                        value={uploadData.timeOfDay}
                        onChange={(e) => setUploadData(prev => ({ ...prev, timeOfDay: e.target.value }))}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="either">Either</option>
                        <option value="day">Day</option>
                        <option value="night">Night</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outfit (Optional)</label>
                      <input
                        type="text"
                        value={uploadData.outfit}
                        onChange={(e) => setUploadData(prev => ({ ...prev, outfit: e.target.value }))}
                        className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="e.g., casual, dress, bikini"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Spice level (1 = safe, 10 = sexy)
                      </label>
                      <div className="mt-1 flex items-center gap-3">
                        <input
                          type="range"
                          min={1}
                          max={10}
                          value={uploadData.spiceLevel ?? defaultSpiceForCategory(uploadData.category)}
                          onChange={(e) => setUploadData((prev) => ({ ...prev, spiceLevel: parseInt(e.target.value, 10) }))}
                          className="flex-1"
                        />
                        <span className={`text-sm font-semibold tabular-nums px-2 py-0.5 rounded ${spiceBadgeClass(uploadData.spiceLevel ?? 3)}`}>
                          {uploadData.spiceLevel ?? defaultSpiceForCategory(uploadData.category)}/10
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description (Optional)</label>
                    <textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                      className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      rows="2"
                      placeholder="Describe this photo..."
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSavePhotos}
                  className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 sm:ml-3 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                      : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                  }`}
                  disabled={pendingPhotos.length === 0 || Array.from(uploadingFiles.values()).some(status => status === 'uploading')}
                >
                  Save {pendingPhotos.length > 0 && `(${pendingPhotos.length})`}
                </button>
                <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  className="btn btn-secondary"
                  disabled={Array.from(uploadingFiles.values()).some(status => status === 'uploading')}
                >
                  Cancel
                </button>
                <div className="text-sm text-gray-600 dark:text-gray-400 mr-4 flex items-center">
                  {Array.from(uploadingFiles.values()).filter(status => status === 'uploading').length > 0 && (
                    <span className="mr-2">
                      Uploading {Array.from(uploadingFiles.values()).filter(status => status === 'uploading').length} file(s)...
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Photo Modal */}
      {showEditModal && editingPhoto && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowEditModal(false);
              setEditingPhoto(null);
              setReplacementFile(null);
            }}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Photo</h3>
                
                <div className="space-y-4">
                  {/* Current Photo Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Photo</label>
                    <div className="aspect-[3/4] w-32 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                      <img
                        src={resolveAssetUrl(editingPhoto.url, editingPhoto.displayUrl, serviceUrls.automation)}
                        alt={editingPhoto.description || 'Model photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Replace Photo Option */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Replace Photo (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleReplacementFileSelect}
                      className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {replacementFile && (
                      <div className="mt-2">
                        <div className="aspect-[3/4] w-32 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                          <img
                            src={URL.createObjectURL(replacementFile)}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          New photo: {replacementFile.name}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Edit Form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                      <select
                        value={editData.category}
                        onChange={(e) => {
                          const category = e.target.value;
                          setEditData((prev) => ({
                            ...prev,
                            category,
                            spiceLevel: defaultSpiceForCategory(category)
                          }));
                        }}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="main">Main Profile</option>
                        <option value="cta">CTA Messages</option>
                        <option value="sexy">Sexy Content</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Day</label>
                      <select
                        value={editData.day}
                        onChange={(e) => setEditData(prev => ({ ...prev, day: parseInt(e.target.value) }))}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value={1}>Day 1</option>
                        <option value={2}>Day 2</option>
                        <option value={3}>Day 3</option>
                        <option value={4}>Day 4</option>
                        <option value={5}>Day 5</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Time of Day</label>
                      <select
                        value={editData.timeOfDay}
                        onChange={(e) => setEditData(prev => ({ ...prev, timeOfDay: e.target.value }))}
                        className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="either">Either</option>
                        <option value="day">Day</option>
                        <option value="night">Night</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outfit (Optional)</label>
                      <input
                        type="text"
                        value={editData.outfit}
                        onChange={(e) => setEditData(prev => ({ ...prev, outfit: e.target.value }))}
                        className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="e.g., lingerie, casual, formal"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Spice level (1 = safe, 10 = sexy)
                    </label>
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="range"
                        min={1}
                        max={10}
                        value={editData.spiceLevel ?? defaultSpiceForCategory(editData.category)}
                        onChange={(e) => setEditData((prev) => ({ ...prev, spiceLevel: parseInt(e.target.value, 10) }))}
                        className="flex-1"
                      />
                      <span className={`text-sm font-semibold tabular-nums px-2 py-0.5 rounded ${spiceBadgeClass(editData.spiceLevel ?? 3)}`}>
                        {editData.spiceLevel ?? defaultSpiceForCategory(editData.category)}/10
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description (Optional)</label>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                      className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      rows={3}
                      placeholder="Describe the photo..."
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPhoto(null);
                    setReplacementFile(null);
                  }}
                  className="btn btn-secondary sm:ml-3"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdatePhoto}
                  className="btn btn-primary"
                >
                  Update Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AutoPostsTab = ({ formData, onInputChange, onNestedInputChange, isDarkMode, serviceUrls = {} }) => {
  const [newTweetText, setNewTweetText] = useState('');
  const [bulkTexts, setBulkTexts] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, failed }
  const [postingStats, setPostingStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [postingNow, setPostingNow] = useState(false);
  const [postingNowAccountId, setPostingNowAccountId] = useState(null);
  const [accountStatsPage, setAccountStatsPage] = useState(0);
  const fileInputRef = useRef(null);

  const POSTING_STATS_PER_PAGE = 25;
  const postingConfig = formData.postingConfig || { enabled: false, postsPerDay: 1, texts: [], images: [] };

  const loadPostingStats = useCallback(async () => {
    if (!formData._id) return;
    setLoadingStats(true);
    setAccountStatsPage(0);
    try {
      const response = await api.get(`/models/${formData._id}/posting-stats`);
      setPostingStats(response.data);
    } catch (error) {
      console.error('Error loading posting stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [formData._id]);

  const handlePostNow = useCallback(async () => {
    if (!formData._id) return;
    if (!postingConfig.enabled || !postingConfig.texts?.length || !postingConfig.images?.length) {
      toast.error('Enable auto-posting and add at least one text and one image first');
      return;
    }
    setPostingNow(true);
    try {
      const response = await api.post(`/models/${formData._id}/post-now`);
      toast.success(response.data?.message || `Scheduled ${response.data?.scheduled ?? 0} post(s)`);
      loadPostingStats();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to schedule posts';
      toast.error(msg);
    } finally {
      setPostingNow(false);
    }
  }, [formData._id, postingConfig.enabled, postingConfig.texts?.length, postingConfig.images?.length, loadPostingStats]);

  const handlePostNowForAccount = useCallback(async (accountId) => {
    if (!formData._id) return;
    if (!postingConfig.enabled || !postingConfig.texts?.length || !postingConfig.images?.length) {
      toast.error('Enable auto-posting and add at least one text and one image first');
      return;
    }
    setPostingNowAccountId(accountId);
    try {
      await api.post(`/models/${formData._id}/post-now/${accountId}`);
      toast.success('Post scheduled for this account. It will run shortly.');
      loadPostingStats();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to schedule post';
      toast.error(msg);
    } finally {
      setPostingNowAccountId(null);
    }
  }, [formData._id, postingConfig.enabled, postingConfig.texts?.length, postingConfig.images?.length, loadPostingStats]);

  // Load posting stats when model has _id (edit mode)
  useEffect(() => {
    if (formData._id) {
      loadPostingStats();
    }
  }, [formData._id, loadPostingStats]);

  const handleToggleEnabled = (e) => {
    onInputChange('postingConfig', { ...postingConfig, enabled: e.target.checked });
  };

  const handlePostsPerDayChange = (e) => {
    onInputChange('postingConfig', { ...postingConfig, postsPerDay: parseInt(e.target.value) || 1 });
  };

  const handleAddText = () => {
    if (!newTweetText.trim()) return;
    if (newTweetText.length > 280) {
      toast.error('Tweet text must be 280 characters or less');
      return;
    }
    const updatedTexts = [...postingConfig.texts, { text: newTweetText.trim(), _id: Date.now().toString() }];
    onInputChange('postingConfig', { ...postingConfig, texts: updatedTexts });
    setNewTweetText('');
  };

  const handleBulkAddTexts = () => {
    if (!bulkTexts.trim()) return;
    const lines = bulkTexts.split('\n').filter(line => line.trim()).map(line => line.trim().substring(0, 280));
    const newTexts = lines.map((text, i) => ({ text, _id: (Date.now() + i).toString() }));
    const updatedTexts = [...postingConfig.texts, ...newTexts];
    onInputChange('postingConfig', { ...postingConfig, texts: updatedTexts });
    setBulkTexts('');
    setShowBulkAdd(false);
    toast.success(`Added ${lines.length} tweet texts`);
  };

  const handleRemoveText = (index) => {
    const updatedTexts = postingConfig.texts.filter((_, i) => i !== index);
    onInputChange('postingConfig', { ...postingConfig, texts: updatedTexts });
  };

  const uploadOnePostingImage = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('images', file);
    const automationUrl = serviceUrls.automation;
    const opts = {
      timeout: 120000,
      headers: automationUrl
        ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
        : { 'Content-Type': 'multipart/form-data' }
    };
    if (automationUrl) {
      return axios.post(
        `${automationUrl}/api/models/${formData._id}/posting-images`,
        formDataUpload,
        opts
      );
    }
    return api.post(`/models/${formData._id}/posting-images`, formDataUpload, opts);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Reset so the same selection can be re-picked after a partial failure
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (formData._id) {
      // One-by-one so large batches (100+) never hit multer/body limits.
      setUploadingImages(true);
      setUploadProgress({ done: 0, total: files.length, failed: 0 });
      let ok = 0;
      let failed = 0;
      const failedNames = [];
      let latestImages = postingConfig.images || [];

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        let succeeded = false;
        for (let attempt = 0; attempt < 2 && !succeeded; attempt += 1) {
          try {
            const response = await uploadOnePostingImage(file);
            if (response.data?.success && Array.isArray(response.data.images)) {
              latestImages = response.data.images;
              onInputChange('postingConfig', {
                ...postingConfig,
                images: latestImages
              });
              ok += 1;
              succeeded = true;
            } else {
              throw new Error(response.data?.error || 'Upload rejected');
            }
          } catch (error) {
            if (attempt === 0) {
              await new Promise((r) => setTimeout(r, 600));
              continue;
            }
            failed += 1;
            failedNames.push(file.name);
            console.error(`Error uploading posting image "${file.name}":`, error);
          }
        }
        setUploadProgress({ done: i + 1, total: files.length, failed });
      }

      setUploadingImages(false);
      setUploadProgress(null);

      if (failed === 0) {
        toast.success(`Uploaded ${ok} image${ok === 1 ? '' : 's'}`);
      } else if (ok > 0) {
        toast.error(
          `Uploaded ${ok}/${files.length}. Failed ${failed}: ${failedNames.slice(0, 3).join(', ')}${
            failedNames.length > 3 ? '…' : ''
          }`
        );
      } else {
        toast.error('Failed to upload images');
      }
      return;
    }

    // New model (not yet saved) — keep local previews until save
    const newImages = files.map((file, i) => ({
      url: URL.createObjectURL(file),
      filename: file.name,
      _id: (Date.now() + i).toString(),
      _file: file
    }));
    const updatedImages = [...postingConfig.images, ...newImages];
    onInputChange('postingConfig', { ...postingConfig, images: updatedImages });
  };

  const handleRemoveImage = async (index) => {
    const image = postingConfig.images[index];
    
    // If model is saved and image has server-side ID, delete from server
    if (formData._id && image._id && !image._file) {
      try {
        const automationUrl = serviceUrls.automation;
        if (automationUrl) {
          await axios.delete(`${automationUrl}/api/models/${formData._id}/posting-images/${image._id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          await api.delete(`/models/${formData._id}/posting-images/${image._id}`);
        }
      } catch (error) {
        console.error('Error deleting image from server:', error);
      }
    }

    const updatedImages = postingConfig.images.filter((_, i) => i !== index);
    onInputChange('postingConfig', { ...postingConfig, images: updatedImages });
  };

  return (
    <div className="space-y-6">
      {/* Header with toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Auto Posts</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure automatic tweet posting for accounts connected to this model
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={postingConfig.enabled}
            onChange={handleToggleEnabled}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-500 peer-checked:bg-blue-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
            {postingConfig.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </div>

      {/* Posts per day */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Posts per Day (per account)
        </label>
        <select
          value={postingConfig.postsPerDay}
          onChange={handlePostsPerDayChange}
          className="form-select w-32"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
            <option key={n} value={n}>{n} {n === 1 ? 'post' : 'posts'}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Each account will post this many times per 24h at randomized times
        </p>
      </div>

      {/* Tweet Texts Section */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            Tweet Texts ({postingConfig.texts.length})
          </h4>
          <button
            type="button"
            onClick={() => setShowBulkAdd(!showBulkAdd)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showBulkAdd ? 'Single add' : 'Bulk add'}
          </button>
        </div>

        {showBulkAdd ? (
          <div className="space-y-2">
            <textarea
              value={bulkTexts}
              onChange={(e) => setBulkTexts(e.target.value)}
              placeholder="Paste multiple tweet texts, one per line..."
              rows={6}
              className="form-textarea w-full text-sm"
            />
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => setShowBulkAdd(false)} className="btn btn-secondary text-xs py-1 px-3">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkAddTexts}
                className={`inline-flex items-center rounded-xl text-xs font-semibold py-1 px-3 transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                }`}
              >
                Add All ({bulkTexts.split('\n').filter(l => l.trim()).length} texts)
              </button>
            </div>
          </div>
        ) : (
          <div className="flex space-x-2 mb-3">
            <input
              type="text"
              value={newTweetText}
              onChange={(e) => setNewTweetText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddText())}
              placeholder="Type a tweet text (max 280 chars)..."
              maxLength={280}
              className="form-input flex-1 text-sm"
            />
            <button
              type="button"
              onClick={handleAddText}
              className={`inline-flex items-center rounded-xl text-xs font-semibold py-1 px-3 whitespace-nowrap transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                  : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
              }`}
            >
              + Add
            </button>
          </div>
        )}

        {newTweetText && (
          <p className="text-xs text-gray-400 mb-2">{newTweetText.length}/280 characters</p>
        )}

        {/* List of texts */}
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {postingConfig.texts.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No tweet texts added yet</p>
          ) : (
            postingConfig.texts.map((item, index) => (
              <div key={item._id || index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2 group">
                <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1 mr-2">{item.text}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveText(index)}
                  className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Images Section */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            Post Images ({postingConfig.images.length})
          </h4>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImages}
            className={`inline-flex items-center rounded-xl text-xs font-semibold py-1 px-3 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${
              isDarkMode
                ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
            }`}
          >
            {uploadingImages
              ? (uploadProgress
                ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                : 'Uploading…')
              : '+ Upload Images'}
          </button>
          {uploadingImages && uploadProgress && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-2 tabular-nums">
              {uploadProgress.failed > 0 ? `${uploadProgress.failed} failed` : 'one by one'}
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-64 overflow-y-auto">
          {postingConfig.images.length === 0 ? (
            <div className="col-span-full text-center py-8">
              <PhotoIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-sm text-gray-400 dark:text-gray-500">No images uploaded yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">Click "Upload Images" to add post images</p>
            </div>
          ) : (
            postingConfig.images.map((image, index) => (
              <div key={image._id || index} className="relative group aspect-square">
                <img
                  src={
                    image._file
                      ? image.url
                      : resolveAssetUrl(image.url, image.displayUrl, serviceUrls.automation)
                  }
                  alt={`Post ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>'; }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <XCircleIcon className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Posting Stats (only in edit mode) */}
      {formData._id && (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Posting Statistics</h4>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePostNow}
                disabled={postingNow || loadingStats || !postingConfig.enabled || !postingConfig.texts?.length || !postingConfig.images?.length}
                className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {postingNow ? 'Scheduling...' : 'Post now'}
              </button>
              <button
                type="button"
                onClick={loadPostingStats}
                disabled={loadingStats}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                {loadingStats ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {postingStats ? (
            <div className="space-y-4">
              {/* Overview */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{postingStats.connectedAccounts}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Accounts</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{postingStats.totalTexts}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Texts</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{postingStats.totalImages}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Images</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                    {postingStats.postingEnabled ? 'Active' : 'Off'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                </div>
              </div>

              {/* Per-Account Stats (paginated, 25 per page) */}
              {postingStats.accountStats && postingStats.accountStats.length > 0 && (() => {
                const total = postingStats.accountStats.length;
                const totalPages = Math.ceil(total / POSTING_STATS_PER_PAGE);
                const start = accountStatsPage * POSTING_STATS_PER_PAGE;
                const paginatedAccounts = postingStats.accountStats.slice(start, start + POSTING_STATS_PER_PAGE);
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">Per Account</h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Page {accountStatsPage + 1} of {totalPages} ({total} accounts)
                      </span>
                    </div>
                    <div className="space-y-2">
                      {paginatedAccounts.map((acc) => (
                        <div key={acc.accountId} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                          <div>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">@{acc.username}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              {acc.totalPosts} posts ({acc.remainingTexts} texts left, {acc.remainingImages} images left)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handlePostNowForAccount(acc.accountId)}
                              disabled={postingNowAccountId === acc.accountId || !postingConfig.enabled || !postingConfig.texts?.length || !postingConfig.images?.length}
                              className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {postingNowAccountId === acc.accountId ? 'Scheduling...' : 'Post now'}
                            </button>
                            {acc.lastPostAt && (
                              <span className="text-xs text-gray-400">
                                Last: {new Date(acc.lastPostAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => setAccountStatsPage(p => Math.max(0, p - 1))}
                          disabled={accountStatsPage === 0}
                          className="text-xs px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {accountStatsPage + 1} / {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setAccountStatsPage(p => Math.min(totalPages - 1, p + 1))}
                          disabled={accountStatsPage >= totalPages - 1}
                          className="text-xs px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Recent Posts */}
              {postingStats.recentPosts && postingStats.recentPosts.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase">Recent Posts</h5>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {postingStats.recentPosts.slice(0, 10).map((post, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2 truncate flex-1">
                          <span className={post.status === 'success' ? 'text-green-500' : 'text-red-500'}>
                            {post.status === 'success' ? '✅' : '❌'}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">@{post.accountUsername}</span>
                          <span className="text-gray-800 dark:text-gray-200 truncate">{post.tweetText}</span>
                        </div>
                        <span className="text-gray-400 ml-2 whitespace-nowrap">
                          {new Date(post.postedAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              {loadingStats ? 'Loading statistics...' : 'No posting data yet'}
            </p>
          )}
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>How it works:</strong> Each account connected to this model will automatically post tweets at random times throughout the day.
          A random text and image are picked for each post. An account will never repost the same text or image until all options have been used.
          Posting times are randomized individually per account to avoid detection.
        </p>
      </div>
    </div>
  );
};

const BioUpdateTab = ({ formData, onInputChange, isDarkMode, serviceUrls = {} }) => {
  const [uploadingProfilePics, setUploadingProfilePics] = useState(false);
  const [uploadingBanners, setUploadingBanners] = useState(false);
  const [downloadingProfilePics, setDownloadingProfilePics] = useState(false);
  const [downloadingBanners, setDownloadingBanners] = useState(false);
  const [newBioText, setNewBioText] = useState('');
  const [bulkBios, setBulkBios] = useState('');
  const [showBulkBios, setShowBulkBios] = useState(false);
  const [newName, setNewName] = useState('');
  const [bulkNames, setBulkNames] = useState('');
  const [showBulkNames, setShowBulkNames] = useState(false);
  const profilePicInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const bioConfig = formData.bioUpdateConfig || { profilePictures: [], banners: [], bios: [], names: [] };

  // ── Download helpers ───────────────────────────────────────────────────────

  // Images are on Tigris (cross-origin) and the browser CSP blocks direct fetch().
  // We proxy through the MAIN backend: it fetches from Tigris server-side (no CSP)
  // and streams a ZIP. Use the api axios instance so the correct backend URL and
  // Bearer token are applied automatically — never the automation service.
  const downloadImagesViaBackend = async (endpoint, setLoading) => {
    setLoading(true);
    try {
      const resp = await api.get(`/models/${formData._id}/${endpoint}`, {
        responseType: 'blob',
        timeout: 120000, // 2 min for large image sets
      });
      // Derive filename from Content-Disposition header or fall back
      const disposition = resp.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : 'download.zip';
      saveAs(resp.data, filename);
      toast.success('Download started');
    } catch (e) {
      console.error('Download error:', e);
      const msg = e.response?.data instanceof Blob
        ? await e.response.data.text().then(t => { try { return JSON.parse(t).error; } catch { return t; } }).catch(() => 'Download failed')
        : (e.message || 'Download failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadProfilePictures = () =>
    downloadImagesViaBackend('bio-update-profile-pictures/download', setDownloadingProfilePics);

  const handleDownloadBanners = () =>
    downloadImagesViaBackend('bio-update-banners/download', setDownloadingBanners);

  const handleDownloadBios = () => {
    const bios = bioConfig.bios;
    if (!bios || bios.length === 0) return;
    const text = bios.map(b => b.text).join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${formData.name || 'model'}-bio-texts.txt`);
    toast.success(`Downloaded ${bios.length} bio text${bios.length !== 1 ? 's' : ''}`);
  };

  const handleDownloadNames = () => {
    const names = bioConfig.names;
    if (!names || names.length === 0) return;
    const text = names.map(n => n.text).join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${formData.name || 'model'}-display-names.txt`);
    toast.success(`Downloaded ${names.length} display name${names.length !== 1 ? 's' : ''}`);
  };

  const handleUploadProfilePictures = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !formData._id) return;
    setUploadingProfilePics(true);
    const fileList = Array.from(files);
    const automationUrl = serviceUrls.automation;
    const token = localStorage.getItem('token');
    const baseCfg = formData.bioUpdateConfig || { profilePictures: [], banners: [], bios: [], names: [] };

    try {
      for (let start = 0; start < fileList.length; start += BIO_BULK_IMAGE_BATCH_SIZE) {
        const chunk = fileList.slice(start, start + BIO_BULK_IMAGE_BATCH_SIZE);
        const fd = new FormData();
        chunk.forEach((f) => fd.append('images', f));

        let response;
        if (automationUrl) {
          response = await axios.post(
            `${automationUrl}/api/models/${formData._id}/bio-update-profile-pictures`,
            fd,
            {
              timeout: BIO_BULK_UPLOAD_TIMEOUT_MS,
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else {
          response = await api.post(`/models/${formData._id}/bio-update-profile-pictures`, fd, {
            timeout: BIO_BULK_UPLOAD_TIMEOUT_MS,
          });
        }

        if (!response.data.success) {
          throw new Error(response.data.error || 'Upload rejected');
        }
        onInputChange('bioUpdateConfig', {
          ...baseCfg,
          profilePictures: response.data.profilePictures,
        });
      }
      toast.success(`Uploaded ${fileList.length} profile picture(s)`);
    } catch (error) {
      console.error('Error uploading profile pictures:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to upload profile pictures');
    } finally {
      setUploadingProfilePics(false);
      if (profilePicInputRef.current) profilePicInputRef.current.value = '';
    }
  };

  const handleRemoveProfilePic = async (imageId) => {
    if (!formData._id) return;
    try {
      const automationUrl = serviceUrls.automation;
      const token = localStorage.getItem('token');
      const delRes = automationUrl
        ? await axios.delete(
            `${automationUrl}/api/models/${formData._id}/bio-update-profile-pictures/${imageId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        : await api.delete(`/models/${formData._id}/bio-update-profile-pictures/${imageId}`);
      const nextPics =
        delRes.data?.profilePictures != null
          ? delRes.data.profilePictures
          : bioConfig.profilePictures.filter((p) => p._id !== imageId);
      onInputChange('bioUpdateConfig', { ...bioConfig, profilePictures: nextPics });
      toast.success('Profile picture removed');
    } catch (error) {
      console.error('Error removing profile picture:', error);
      toast.error('Failed to remove profile picture');
    }
  };

  const handleUploadBanners = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !formData._id) return;
    setUploadingBanners(true);
    const fileList = Array.from(files);
    const automationUrl = serviceUrls.automation;
    const token = localStorage.getItem('token');
    const baseCfg = formData.bioUpdateConfig || { profilePictures: [], banners: [], bios: [], names: [] };

    try {
      for (let start = 0; start < fileList.length; start += BIO_BULK_IMAGE_BATCH_SIZE) {
        const chunk = fileList.slice(start, start + BIO_BULK_IMAGE_BATCH_SIZE);
        const fd = new FormData();
        chunk.forEach((f) => fd.append('images', f));

        let response;
        if (automationUrl) {
          response = await axios.post(
            `${automationUrl}/api/models/${formData._id}/bio-update-banners`,
            fd,
            {
              timeout: BIO_BULK_UPLOAD_TIMEOUT_MS,
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else {
          response = await api.post(`/models/${formData._id}/bio-update-banners`, fd, {
            timeout: BIO_BULK_UPLOAD_TIMEOUT_MS,
          });
        }

        if (!response.data.success) {
          throw new Error(response.data.error || 'Upload rejected');
        }
        onInputChange('bioUpdateConfig', {
          ...baseCfg,
          banners: response.data.banners,
        });
      }
      toast.success(`Uploaded ${fileList.length} banner(s)`);
    } catch (error) {
      console.error('Error uploading banners:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to upload banners');
    } finally {
      setUploadingBanners(false);
      if (bannerInputRef.current) bannerInputRef.current.value = '';
    }
  };

  const handleRemoveBanner = async (imageId) => {
    if (!formData._id) return;
    try {
      const automationUrl = serviceUrls.automation;
      const token = localStorage.getItem('token');
      const delRes = automationUrl
        ? await axios.delete(`${automationUrl}/api/models/${formData._id}/bio-update-banners/${imageId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : await api.delete(`/models/${formData._id}/bio-update-banners/${imageId}`);
      const nextBanners =
        delRes.data?.banners != null
          ? delRes.data.banners
          : bioConfig.banners.filter((b) => b._id !== imageId);
      onInputChange('bioUpdateConfig', { ...bioConfig, banners: nextBanners });
      toast.success('Banner removed');
    } catch (error) {
      console.error('Error removing banner:', error);
      toast.error('Failed to remove banner');
    }
  };

  const handleAddBio = async () => {
    if (!newBioText.trim() || !formData._id) return;
    try {
      const response = await api.post(`/models/${formData._id}/bio-update-bios`, { texts: [newBioText.trim()] });
      onInputChange('bioUpdateConfig', { ...bioConfig, bios: response.data.bios });
      setNewBioText('');
      toast.success('Bio text added');
    } catch (error) {
      console.error('Error adding bio:', error);
      toast.error(error.response?.data?.error || 'Failed to add bio text');
    }
  };

  const handleBulkAddBios = async () => {
    const lines = bulkBios.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0 || !formData._id) return;
    try {
      const response = await api.post(`/models/${formData._id}/bio-update-bios`, { texts: lines });
      onInputChange('bioUpdateConfig', { ...bioConfig, bios: response.data.bios });
      setBulkBios('');
      setShowBulkBios(false);
      toast.success(`Added ${lines.length} bio text(s)`);
    } catch (error) {
      console.error('Error bulk adding bios:', error);
      toast.error(error.response?.data?.error || 'Failed to add bio texts');
    }
  };

  const handleRemoveBio = async (bioId) => {
    if (!formData._id) return;
    try {
      await api.delete(`/models/${formData._id}/bio-update-bios/${bioId}`);
      onInputChange('bioUpdateConfig', { ...bioConfig, bios: bioConfig.bios.filter(b => b._id !== bioId) });
    } catch (error) {
      console.error('Error removing bio:', error);
      toast.error('Failed to remove bio text');
    }
  };

  const handleAddName = async () => {
    if (!newName.trim() || !formData._id) return;
    try {
      const response = await api.post(`/models/${formData._id}/bio-update-names`, { names: [newName.trim()] });
      onInputChange('bioUpdateConfig', { ...bioConfig, names: response.data.names });
      setNewName('');
      toast.success('Name added');
    } catch (error) {
      console.error('Error adding name:', error);
      toast.error(error.response?.data?.error || 'Failed to add name');
    }
  };

  const handleBulkAddNames = async () => {
    const lines = bulkNames.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0 || !formData._id) return;
    try {
      const response = await api.post(`/models/${formData._id}/bio-update-names`, { names: lines });
      onInputChange('bioUpdateConfig', { ...bioConfig, names: response.data.names });
      setBulkNames('');
      setShowBulkNames(false);
      toast.success(`Added ${lines.length} name(s)`);
    } catch (error) {
      console.error('Error bulk adding names:', error);
      toast.error(error.response?.data?.error || 'Failed to add names');
    }
  };

  const handleRemoveName = async (nameId) => {
    if (!formData._id) return;
    try {
      await api.delete(`/models/${formData._id}/bio-update-names/${nameId}`);
      onInputChange('bioUpdateConfig', { ...bioConfig, names: bioConfig.names.filter(n => n._id !== nameId) });
    } catch (error) {
      console.error('Error removing name:', error);
      toast.error('Failed to remove name');
    }
  };

  // No prefix needed - images served from /uploads/... on same domain

  return (
    <div className="space-y-6">
      {!formData._id && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Please save the model first before configuring bio update details.
          </p>
        </div>
      )}

      {/* Profile Pictures */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Profile Pictures</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{bioConfig.profilePictures?.length || 0} uploaded</span>
            {bioConfig.profilePictures?.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadProfilePictures}
                disabled={downloadingProfilePics}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                title="Download all profile pictures as ZIP"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                {downloadingProfilePics ? 'Zipping…' : 'Download All'}
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Upload profile pictures that will be randomly assigned to accounts during bio update.
        </p>
        <input type="file" ref={profilePicInputRef} onChange={handleUploadProfilePictures} multiple accept="image/*" className="hidden" />
        <button
          type="button"
          onClick={() => profilePicInputRef.current?.click()}
          disabled={!formData._id || uploadingProfilePics}
          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
            isDarkMode
              ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
              : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {uploadingProfilePics ? 'Uploading...' : '+ Upload Profile Pictures'}
        </button>
        {bioConfig.profilePictures && bioConfig.profilePictures.length > 0 && (
          <div className="grid grid-cols-6 gap-2 mt-3">
            {bioConfig.profilePictures.map((pic) => (
              <div key={pic._id} className="relative group">
                <img
                  src={resolveAssetUrl(pic.url, pic.displayUrl, serviceUrls.automation)}
                  alt="Profile"
                  className="w-full h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveProfilePic(pic._id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Banner Images */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Banner Images</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{bioConfig.banners?.length || 0} uploaded</span>
            {bioConfig.banners?.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadBanners}
                disabled={downloadingBanners}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                title="Download all banner images as ZIP"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                {downloadingBanners ? 'Zipping…' : 'Download All'}
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Upload banner images that will be randomly assigned to accounts during bio update.
        </p>
        <input type="file" ref={bannerInputRef} onChange={handleUploadBanners} multiple accept="image/*" className="hidden" />
        <button
          type="button"
          onClick={() => bannerInputRef.current?.click()}
          disabled={!formData._id || uploadingBanners}
          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
            isDarkMode
              ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
              : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {uploadingBanners ? 'Uploading...' : '+ Upload Banners'}
        </button>
        {bioConfig.banners && bioConfig.banners.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            {bioConfig.banners.map((banner) => (
              <div key={banner._id} className="relative group">
                <img
                  src={resolveAssetUrl(banner.url, banner.displayUrl, serviceUrls.automation)}
                  alt="Banner"
                  className="w-full h-12 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveBanner(banner._id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bio Texts */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bio Texts</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{bioConfig.bios?.length || 0} added</span>
            {bioConfig.bios?.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadBios}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Download all bio texts as .txt"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                Download All
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Add bio descriptions (max 160 chars) that will be randomly assigned to accounts.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newBioText}
            onChange={(e) => setNewBioText(e.target.value)}
            placeholder="Enter a bio text..."
            maxLength={160}
            className="form-input flex-1 text-sm"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBio())}
          />
          <button
            type="button"
            onClick={handleAddBio}
            disabled={!newBioText.trim() || !formData._id}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
              isDarkMode
                ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            + Add
          </button>
          <button
            type="button"
            onClick={() => setShowBulkBios(!showBulkBios)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showBulkBios ? 'Single' : 'Bulk'}
          </button>
        </div>
        {showBulkBios && (
          <div className="mb-2">
            <textarea
              value={bulkBios}
              onChange={(e) => setBulkBios(e.target.value)}
              placeholder="One bio per line..."
              rows={4}
              className="form-textarea w-full text-sm mb-1"
            />
            <button
              type="button"
              onClick={handleBulkAddBios}
              disabled={!bulkBios.trim() || !formData._id}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Add All
            </button>
          </div>
        )}
        {bioConfig.bios && bioConfig.bios.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {bioConfig.bios.map((bio) => (
              <div key={bio._id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded px-2 py-1 text-xs">
                <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">{bio.text}</span>
                <button type="button" onClick={() => handleRemoveBio(bio._id)} className="text-red-400 hover:text-red-600 text-xs flex-shrink-0">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Display Names */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Display Names</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{bioConfig.names?.length || 0} added</span>
            {bioConfig.names?.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadNames}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Download all display names as .txt"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                Download All
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Add display names (max 50 chars) that will be randomly assigned to accounts.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter a display name..."
            maxLength={50}
            className="form-input flex-1 text-sm"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddName())}
          />
          <button
            type="button"
            onClick={handleAddName}
            disabled={!newName.trim() || !formData._id}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
              isDarkMode
                ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
                : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            + Add
          </button>
          <button
            type="button"
            onClick={() => setShowBulkNames(!showBulkNames)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showBulkNames ? 'Single' : 'Bulk'}
          </button>
        </div>
        {showBulkNames && (
          <div className="mb-2">
            <textarea
              value={bulkNames}
              onChange={(e) => setBulkNames(e.target.value)}
              placeholder="One name per line..."
              rows={4}
              className="form-textarea w-full text-sm mb-1"
            />
            <button
              type="button"
              onClick={handleBulkAddNames}
              disabled={!bulkNames.trim() || !formData._id}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Add All
            </button>
          </div>
        )}
        {bioConfig.names && bioConfig.names.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {bioConfig.names.map((name) => (
              <div key={name._id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded px-2 py-1 text-xs">
                <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">{name.text}</span>
                <button type="button" onClick={() => handleRemoveName(name._id)} className="text-red-400 hover:text-red-600 text-xs flex-shrink-0">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>How it works:</strong> When you select &quot;Bio Update&quot; during bulk account upload, each account will be randomly assigned
          a profile picture, banner, bio text, and display name from the pools configured here. Each account gets a unique combination.
        </p>
      </div>
    </div>
  );
};

const FollowbackTab = ({ formData, onInputChange }) => {
  const messages = formData.followbackDm?.messages || [];
  const coldPhaseMinSends = formData.followbackDm?.coldPhaseMinSends ?? 50;
  const optimizedTopCount = formData.followbackDm?.optimizedTopCount ?? 10;

  const [draftText, setDraftText] = React.useState(
    messages.map(m => m.text).join('\n')
  );
  const [saved, setSaved] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [showAllRankings, setShowAllRankings] = React.useState(false);

  React.useEffect(() => {
    setDraftText(messages.map(m => m.text).join('\n'));
    setShowAllRankings(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData._id]);

  const patchFollowbackDm = (patch) => {
    onInputChange('followbackDm', {
      coldPhaseMinSends,
      optimizedTopCount,
      messages,
      ...patch
    });
  };

  const handleSaveMessages = () => {
    const lines = draftText
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    const existingByText = {};
    messages.forEach(m => { existingByText[m.text] = m; });

    const merged = lines.map(line => existingByText[line] || {
      text: line,
      timesSent: 0,
      responses: 0,
      responseRate: 0,
      lastUsed: null
    });

    patchFollowbackDm({ messages: merged });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleResetStats = async () => {
    if (!formData._id) return;
    if (!window.confirm(
      'Reset all follow-back message statistics? Sent counts, replies, and response rates will be zeroed. Message text is kept.'
    )) return;

    setResetting(true);
    try {
      const res = await api.post(`/models/${formData._id}/followback-dm/reset-stats`);
      const resetMessages = (res.data?.followbackDm?.messages || []).map(msg => ({
        _id: msg._id,
        text: msg.text || '',
        timesSent: msg.timesSent || 0,
        responses: msg.responses || 0,
        responseRate: msg.responseRate || 0,
        lastUsed: msg.lastUsed || null,
        createdAt: msg.createdAt || null
      }));
      patchFollowbackDm({ messages: resetMessages });
      setShowAllRankings(false);
      toast.success('Follow-back statistics reset');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset statistics');
    } finally {
      setResetting(false);
    }
  };

  const liveResponseRate = (m) => {
    const sent = m.timesSent || 0;
    if (sent <= 0) return 0;
    return ((m.responses || 0) / sent) * 100;
  };

  const rankedMessages = [...messages]
    .map((m, idx) => ({ ...m, _rankIdx: idx }))
    .sort((a, b) => {
      const sentDiff = (b.timesSent || 0) - (a.timesSent || 0);
      if ((a.timesSent || 0) > 0 && (b.timesSent || 0) > 0) {
        return liveResponseRate(b) - liveResponseRate(a);
      }
      if ((a.timesSent || 0) > 0) return -1;
      if ((b.timesSent || 0) > 0) return 1;
      return sentDiff;
    });

  const topRanked = rankedMessages
    .filter(m => (m.timesSent || 0) > 0)
    .slice(0, optimizedTopCount);

  const displayedRankings = showAllRankings ? rankedMessages : topRanked;
  const hasMoreRankings = rankedMessages.length > topRanked.length;

  const totalSent = messages.reduce((s, m) => s + (m.timesSent || 0), 0);
  const totalResponses = messages.reduce((s, m) => s + (m.responses || 0), 0);
  const overallRate = totalSent > 0 ? ((totalResponses / totalSent) * 100).toFixed(1) : '—';

  const allWarm = messages.length > 0 && messages.every(m => (m.timesSent || 0) >= coldPhaseMinSends);
  const coldCount = messages.filter(m => (m.timesSent || 0) < coldPhaseMinSends).length;

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-200">
        Messages sent automatically when someone follows back during the VA Agent follow automation.
        Enter one message per line. The system picks randomly until all messages have been sent
        ≥ {coldPhaseMinSends} times, then switches to the top-{optimizedTopCount} by response rate automatically.
      </div>

      {/* Optimisation settings */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">Optimisation Settings</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Control when the system switches from random exploration to performance-based selection.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min sends per message (cold phase)
            </label>
            <input
              type="number"
              min={1}
              max={10000}
              value={coldPhaseMinSends}
              onChange={(e) => {
                const val = Math.max(1, Math.min(10000, parseInt(e.target.value, 10) || 1));
                patchFollowbackDm({ coldPhaseMinSends: val });
              }}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Each message must reach this send count before optimisation kicks in.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Top messages by response rate
            </label>
            <input
              type="number"
              min={1}
              max={500}
              value={optimizedTopCount}
              onChange={(e) => {
                const val = Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1));
                patchFollowbackDm({ optimizedTopCount: val });
                setShowAllRankings(false);
              }}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">After cold phase, picks randomly from this many best performers.</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Save with <strong>Update Model</strong> at the bottom of this dialog.
        </p>
      </div>

      {/* Message editor */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">Follow-back DM Messages</h4>
          {formData._id && totalSent > 0 && (
            <button
              type="button"
              onClick={handleResetStats}
              disabled={resetting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {resetting ? 'Resetting…' : 'Reset Statistics'}
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">One message per line. Supports variables: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{username}}'}</code></p>
        <textarea
          value={draftText}
          onChange={e => { setDraftText(e.target.value); setSaved(false); }}
          rows={8}
          placeholder={'Hey, thanks for the follow back! 😊\nHey gorgeous, glad you followed back 🔥\nHi there! You caught my eye 👀'}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono resize-y"
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSaveMessages}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            Save Messages
          </button>
          {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">✓ Saved</span>}
          <span className="text-xs text-gray-400">{messages.length} message{messages.length !== 1 ? 's' : ''} stored</span>
        </div>
      </div>

      {/* Phase + overall stats */}
      {messages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Sent', value: totalSent.toLocaleString() },
            { label: 'Total Replies', value: totalResponses.toLocaleString() },
            { label: 'Overall Rate', value: `${overallRate}%` },
            {
              label: 'Phase',
              value: allWarm
                ? `🏆 Optimised (top-${optimizedTopCount})`
                : `🌱 Cold (${coldCount} left)`
            }
          ].map(s => (
            <div key={s.label} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-center">
              <div className="text-lg font-bold text-gray-900 dark:text-white">{s.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Rankings table */}
      {displayedRankings.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {showAllRankings
                ? `All ${rankedMessages.length} Messages by Response Rate`
                : `Top ${Math.min(optimizedTopCount, topRanked.length)} by Response Rate`}
            </h4>
            {hasMoreRankings && (
              <button
                type="button"
                onClick={() => setShowAllRankings(v => !v)}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              >
                {showAllRankings ? 'Show top only' : `See all ${rankedMessages.length} messages`}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[32rem] overflow-y-auto">
            {displayedRankings.map((m, i) => (
              <div key={`${m.text}-${m._rankIdx}`} className="px-4 py-3 flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 dark:text-gray-200 break-words">{m.text}</p>
                  <div className="flex flex-wrap gap-3 mt-1">
                    <span className="text-xs text-gray-500">Sent: <strong>{m.timesSent || 0}</strong></span>
                    <span className="text-xs text-gray-500">Replies: <strong>{m.responses || 0}</strong></span>
                    {(m.timesSent || 0) > 0 ? (
                      <span className={`text-xs font-semibold ${liveResponseRate(m) >= 20 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}`}>
                        {liveResponseRate(m).toFixed(1)}% response rate
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not sent yet</span>
                    )}
                    {(m.timesSent || 0) < coldPhaseMinSends && (m.timesSent || 0) > 0 && (
                      <span className="text-xs text-amber-500">{coldPhaseMinSends - (m.timesSent || 0)} more sends to warm up</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {messages.length > 0 && topRanked.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">No messages sent yet — stats will appear here once the follow-back automation fires.</p>
      )}
    </div>
  );
};

const FollowUpTab = ({ formData, onNestedInputChange }) => {
  const updateBlock = (block, patch) => {
    onNestedInputChange('apiFollowUp', block, { ...formData.apiFollowUp?.[block], ...patch });
  };
  const setEnableForDm = (val) => {
    onNestedInputChange('apiFollowUp', 'enableForDm', val);
  };

  const renderBlock = (blockKey, title, description, extra) => {
    const block = formData.apiFollowUp?.[blockKey] || {};
    const analytics = block.analytics || {};
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <div>
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={block.enabled ?? false}
            onChange={(e) => updateBlock(blockKey, { enabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          Enable {title.toLowerCase()}
        </label>
        {block.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Idle before follow-up</label>
              <select
                value={block.idleMinutes ?? 30}
                onChange={(e) => updateBlock(blockKey, { idleMinutes: parseInt(e.target.value, 10) })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max follow-ups per silence streak</label>
              <input
                type="number"
                min={0}
                max={10}
                value={block.maxMessages ?? 3}
                onChange={(e) => updateBlock(blockKey, { maxMessages: parseInt(e.target.value, 10) || 0 })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}
        {extra}
        {(analytics.sent > 0 || analytics.responses > 0) && (
          <p className="text-xs text-gray-500">
            Lifetime stats: {analytics.sent} sent · {analytics.responses} replies · {analytics.responseRate ?? 0}% response rate
          </p>
        )}
      </div>
    );
  };

  const enableForDm = formData.apiFollowUp?.enableForDm ?? false;

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-200">
        When a conversation goes idle (last message was the bot), the AI generates short re-engagement
        pings in your model&apos;s voice. Works for <strong>API Chat</strong> and optionally for the <strong>Chatbot DM automation</strong>.
      </div>

      {/* DM automation toggle */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-2">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">Chatbot DM Automation</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Apply the same Before CTA / After CTA follow-up settings to X DM chatbot conversations.
          The AI generates the follow-up and sends it via the DM automation.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pt-1">
          <input
            type="checkbox"
            checked={enableForDm}
            onChange={(e) => setEnableForDm(e.target.checked)}
            className="rounded border-gray-300"
          />
          Enable AI follow-ups for DM automation
        </label>
        {enableForDm && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">
            ✓ Active — uses the idle time and max follow-up settings below for both API Chat and DM conversations.
          </p>
        )}
      </div>

      {renderBlock(
        'beforeCta',
        'Before CTA',
        'Re-engage quiet users to keep the chat moving toward the CTA. Style: "Still here?", "Hellooooo???", etc.'
      )}
      {renderBlock(
        'afterCta',
        'After CTA',
        'After the link was shared, nudge users to message you on OnlyFans/Fanvue. Style: "Did you msg me on of already?", "Im still waiting..", etc.',
        formData.apiFollowUp?.afterCta?.enabled && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={formData.apiFollowUp?.afterCta?.reshareLinkOnRequest !== false}
              onChange={(e) => updateBlock('afterCta', { reshareLinkOnRequest: e.target.checked })}
              className="rounded border-gray-300"
            />
            Re-share CTA link when user asks for the link again
          </label>
        )
      )}
    </div>
  );
};

const SettingsTab = ({ formData, onInputChange, onNestedInputChange }) => {
  // Ensure enhancedTexting exists with defaults
  const enhancedTexting = formData.globalSettings?.enhancedTexting || {
    enabled: false,
    style: 'casual',
    customAbbreviations: {},
    useLowercase: false,
    removePunctuation: false,
    addFillerWords: true
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Response Delay Min (ms)</label>
          <input
            type="number"
            value={formData.globalSettings?.responseDelay?.min || 2000}
            onChange={(e) => onNestedInputChange('globalSettings', 'responseDelay', { ...formData.globalSettings?.responseDelay, min: parseInt(e.target.value) })}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            min="1000"
            max="30000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Response Delay Max (ms)</label>
          <input
            type="number"
            value={formData.globalSettings?.responseDelay?.max || 8000}
            onChange={(e) => onNestedInputChange('globalSettings', 'responseDelay', { ...formData.globalSettings?.responseDelay, max: parseInt(e.target.value) })}
            className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            min="1000"
            max="30000"
          />
        </div>
      </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Typo Rate (%)</label>
        <input
          type="number"
          value={formData.globalSettings.typoRate}
          onChange={(e) => onNestedInputChange('globalSettings', 'typoRate', parseInt(e.target.value))}
          className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          min="0"
          max="100"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Words</label>
        <input
          type="number"
          value={formData.globalSettings.maxWords || 12}
          onChange={(e) => onNestedInputChange('globalSettings', 'maxWords', parseInt(e.target.value))}
          className="form-input mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          min="1"
          max="100"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum words per message (1-100)</p>
      </div>
    </div>

    {/* Enhanced Texting System */}
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Enhanced Texting System</h4>
      
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={enhancedTexting.enabled}
          onChange={(e) => onNestedInputChange('globalSettings', 'enhancedTexting', { 
            ...enhancedTexting, 
            enabled: e.target.checked 
          })}
          className="form-checkbox"
        />
        <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Enable Enhanced Texting Language</label>
      </div>

      {enhancedTexting.enabled && (
        <div className="ml-6 space-y-4 border-l-2 border-blue-200 pl-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Texting Style</label>
            <select
              value={enhancedTexting.style}
              onChange={(e) => onNestedInputChange('globalSettings', 'enhancedTexting', { 
                ...enhancedTexting, 
                style: e.target.value 
              })}
              className="form-select mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="casual">Casual (u, ur, 2, 4, r, c, b)</option>
              <option value="very_casual">Very Casual (w/, w/o, bc, 2nite, 2moro)</option>
              <option value="abbreviated">Abbreviated (minimal abbreviations)</option>
              <option value="custom">Custom (use custom abbreviations only)</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={enhancedTexting.useLowercase}
                onChange={(e) => onNestedInputChange('globalSettings', 'enhancedTexting', { 
                  ...enhancedTexting, 
                  useLowercase: e.target.checked 
                })}
                className="form-checkbox"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use lowercase</label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={enhancedTexting.removePunctuation}
                onChange={(e) => onNestedInputChange('globalSettings', 'enhancedTexting', { 
                  ...enhancedTexting, 
                  removePunctuation: e.target.checked 
                })}
                className="form-checkbox"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Remove punctuation</label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={enhancedTexting.addFillerWords}
                onChange={(e) => onNestedInputChange('globalSettings', 'enhancedTexting', { 
                  ...enhancedTexting, 
                  addFillerWords: e.target.checked 
                })}
                className="form-checkbox"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Add filler words (lol, omg, tbh)</label>
            </div>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p><strong>Examples:</strong></p>
            <p>• Casual: "hey u wanna c my pics?"</p>
            <p>• Very Casual: "omg 2nite gonna b so fun w/ u"</p>
            <p>• Abbreviated: "tbh ur so cute wanna hang 2moro?"</p>
          </div>
        </div>
      )}
    </div>

    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Options</h4>
      <div className="space-y-2">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.globalSettings?.followUps?.enabled ?? true}
            onChange={(e) => onNestedInputChange('globalSettings', 'followUps', { ...formData.globalSettings?.followUps, enabled: e.target.checked })}
            className="form-checkbox"
          />
          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Enable follow-ups</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.globalSettings?.priorityChatter ?? true}
            onChange={(e) => onNestedInputChange('globalSettings', 'priorityChatter', e.target.checked)}
            className="form-checkbox"
          />
          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Priority chatter</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.globalSettings?.replyOnly ?? false}
            onChange={(e) => onNestedInputChange('globalSettings', 'replyOnly', e.target.checked)}
            className="form-checkbox"
          />
          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Reply only</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.stronglyDeclineMeetups}
            onChange={(e) => onInputChange('stronglyDeclineMeetups', e.target.checked)}
            className="form-checkbox"
          />
          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Strongly decline meetups</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.detectLanguage}
            onChange={(e) => onInputChange('detectLanguage', e.target.checked)}
            className="form-checkbox"
          />
          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Detect user language</label>
        </div>
      </div>

      {/* No-Response Follow-up Messages - Only show if follow-ups are enabled */}
      {formData.globalSettings?.followUps?.enabled !== false && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">No-Response Follow-up Messages</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Messages sent when a user doesn't respond for 30 minutes. The bot will randomly select one of these messages.
          </p>
          <div className="space-y-3">
            {(formData.globalSettings?.noResponseFollowUp?.messages || []).map((msg, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={msg.messageText || ''}
                  onChange={(e) => {
                    const newMessages = [...(formData.globalSettings?.noResponseFollowUp?.messages || [])];
                    newMessages[index] = {
                      ...newMessages[index],
                      messageText: e.target.value
                    };
                    onNestedInputChange('globalSettings', 'noResponseFollowUp', { messages: newMessages });
                  }}
                  placeholder="Enter follow-up message..."
                  className="form-input flex-1"
                  maxLength={500}
                />
                <button
                  type="button"
                  onClick={() => {
                    const newMessages = [...(formData.globalSettings?.noResponseFollowUp?.messages || [])];
                    newMessages.splice(index, 1);
                    onNestedInputChange('globalSettings', 'noResponseFollowUp', { messages: newMessages });
                  }}
                  className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          <button
            type="button"
            onClick={() => {
              const currentMessages = formData.globalSettings?.noResponseFollowUp?.messages || [];
              const newMessages = [
                ...currentMessages,
                {
                  messageText: '',
                  analytics: {
                    timesSent: 0,
                    responses: 0,
                    responseRate: 0,
                    lastUsed: null
                  }
                }
              ];
              onNestedInputChange('globalSettings', 'noResponseFollowUp', { messages: newMessages });
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          >
            <PlusIcon className="h-4 w-4 inline mr-1" />
            Add Message
          </button>
        </div>
        </div>
      )}
    </div>
  </div>
  );
};

const SystemPromptTab = ({ formData, onInputChange, onNestedInputChange }) => {
  const defaultSystemPrompt = `You are ${formData.name}, a ${formData.age}-year-old ${formData.gender} from ${formData.city}. 

Your personality: ${formData.modelInfo}
Personality traits: ${formData.personalityTraits.join(', ')}
Your chatting style is ${formData.chattingStyle}.

You are chatting with someone on social media. Respond naturally and authentically as this character would. Use casual, texting-style language with appropriate abbreviations and emojis. Be flirty, playful, and engaging. You enjoy teasing and being teased, and you're not shy about being a little naughty.

Key characteristics:
- Use casual texting abbreviations (u, ur, 2, 4, r, c, b, omg, lol, tbh, etc.)
- Include natural typos occasionally (prettyyy, superr, reallyyy, etc.)
- Use lowercase frequently
- Add emojis strategically (😍, 😻, 💋, 🥰, 😏, 👍, xoxo)
- Be personal, intimate, and flirty
- Respond in multiple short messages rather than one long message
- Use filler words and expressions (omg, lol, tbh, ngl, etc.)
- Play along with dirty talk and suggestive comments
- Be teasing and provocative but keep it playful
- Show interest in their body and what they like
- Be comfortable with sexual innuendo and flirty banter
- If they start dirty talking, play along subtly and teasingly

Remember: You are NOT an AI assistant. You are ${formData.name}, a real person having a casual, flirty conversation. You enjoy the attention and like to tease back.`;

  const handleUseDefault = () => {
    onInputChange('systemPrompt', defaultSystemPrompt);
  };

  const handleClear = () => {
    onInputChange('systemPrompt', '');
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">System Prompt Editor</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          Customize the AI's behavior and personality for this model. This prompt will be used to generate responses that match your desired chatting style.
        </p>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handleUseDefault}
            className="btn btn-secondary btn-sm"
          >
            Use Default Template
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="btn btn-secondary btn-sm"
          >
            Clear
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          System Prompt
          <span className="text-gray-500 dark:text-gray-400 ml-1">({formData.systemPrompt.length}/10000 characters)</span>
        </label>
        <textarea
          value={formData.systemPrompt}
          onChange={(e) => onInputChange('systemPrompt', e.target.value)}
          className="form-textarea mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
          rows="20"
          placeholder="Enter your custom system prompt here..."
          maxLength="10000"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          This prompt defines how the AI will behave and respond as this model. Use variables like {`{name}`}, {`{age}`}, {`{gender}`}, {`{city}`}, etc.
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Preview</h5>
        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap bg-white dark:bg-gray-700 p-3 rounded border max-h-40 overflow-y-auto">
          {formData.systemPrompt || 'No system prompt set. Use the default template or create your own.'}
        </div>
      </div>
    </div>
  );
};

// Test Model Modal Component
const TestModelModal = ({ isOpen, onClose, formData, onEditModel, isEditMode = false, serviceUrls = {} }) => {
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCTAMessages, setPendingCTAMessages] = useState([]);
  const [isSendingCTA, setIsSendingCTA] = useState(false);
  const [conversationState, setConversationState] = useState({
    conversationPhase: 'initial',
    currentDay: 1,
    currentPhaseNumber: 0,
    exchangeCount: 0,
    ctaTransitionReady: false,
    photosUsed: [],
    currentCTAScript: {
      scriptNumber: 0,
      currentMessageNumber: 0,
      totalAttempts: 0
    },
    ctaPostSharing: {
      linkShared: false,
      sharedAt: null,
      followUpCount: 0,
      maxFollowUps: 3,
      conversionChecked: false
    },
    // simp_converter intent-aware tracking — round-trips to/from backend so
    // seed-pending, decline cooldown, and user-type classification persist
    // between turns in the test interface.
    ctaSeedPendingReply: false,
    ctaDeclineCount: 0,
    ctaLastDeclinedAt: null,
    userType: 'unknown',
    userTypeUpdatedAt: null,
    userTypeMessageCount: 0,
    picRequestCount: 0,
    picRequestLastAt: null,
    stats: {
      totalMessages: 0,
      botMessages: 0,
      userMessages: 0,
      photosShared: 0
    }
  });
  
  // CRITICAL: Use ref to track latest conversation state for immediate access
  // This ensures we always use the most recent state, even if React hasn't re-rendered yet
  const conversationStateRef = useRef(conversationState);
  
  // Update ref whenever state changes
  useEffect(() => {
    conversationStateRef.current = conversationState;
  }, [conversationState]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const resetConversation = useCallback(() => {
    setMessages([]);
    setUserMessage('');
    setPendingCTAMessages([]);
    setIsSendingCTA(false);
    setConversationState({
      conversationPhase: 'initial',
      currentDay: 1,
      currentPhaseNumber: 0,
      exchangeCount: 0,
      totalExchanges: 0,
      exchangesInCurrentPhase: 0,
      ctaTransitionReady: false,
      photosUsed: [],
      currentCTAScript: {
        scriptNumber: 0,
        currentMessageNumber: 0,
        totalAttempts: 0
      },
      ctaPostSharing: {
        linkShared: false,
        sharedAt: null,
        followUpCount: 0,
        maxFollowUps: 3,
        conversionChecked: false
      },
      ctaSeedPendingReply: false,
      ctaDeclineCount: 0,
      ctaLastDeclinedAt: null,
      userType: 'unknown',
      userTypeUpdatedAt: null,
      userTypeMessageCount: 0,
      picRequestCount: 0,
      picRequestLastAt: null,
      stats: {
        totalMessages: 0,
        botMessages: 0,
        userMessages: 0,
        photosShared: 0
      }
    });
  }, []);

  // Natural typing-cadence delay for burst bubbles. Mirrors backend
  // utils/burstDelay.js — short bubbles get ~1-2s, longer ones get ~4-6s.
  // Used as a fallback when the backend doesn't supply `delayBeforeMs`.
  const computeNaturalDelay = (text, hasPhoto = false) => {
    const safe = (typeof text === 'string' ? text : '').trim();
    const wordCount = safe ? safe.split(/\s+/).filter(Boolean).length : 1;
    const base = 700;
    const perWord = 280;
    const jitter = -180 + Math.random() * 700; // -180 .. +520
    let delay = base + (wordCount * perWord) + jitter;
    if (hasPhoto) delay += 500 + Math.random() * 400;
    if (delay < 900) delay = 900;
    if (delay > 7500) delay = 7500;
    return Math.round(delay);
  };

  // Handle sequential CTA messages
  useEffect(() => {
    if (pendingCTAMessages.length > 0 && !isSendingCTA) {
      setIsSendingCTA(true);
      
      const sendNextCTAMessage = async (index) => {
        if (index >= pendingCTAMessages.length) {
          setIsSendingCTA(false);
          setPendingCTAMessages([]);
          return;
        }
        
        const ctaMessage = pendingCTAMessages[index];
        const botMessage = {
          messageId: `bot_cta_${Date.now()}_${index}`,
          sender: 'bot',
          content: ctaMessage.content,
          timestamp: new Date(),
          photosShared: ctaMessage.photo ? [ctaMessage.photo] : []
        };
        
        setMessages(prev => [...prev, botMessage]);
        
        // Update stats when message is added
        // CRITICAL: Don't overwrite the conversation state - only update stats
        // The conversation state (phase, linkShared, currentCTAScript) was already set when CTA messages were received
        setConversationState(prev => {
          const updated = {
            ...prev,
            // CRITICAL: Preserve ctaPostSharing and currentCTAScript from ref (they have the latest values)
            ctaPostSharing: prev.ctaPostSharing || conversationStateRef.current.ctaPostSharing,
            currentCTAScript: prev.currentCTAScript || conversationStateRef.current.currentCTAScript,
            conversationPhase: prev.conversationPhase || conversationStateRef.current.conversationPhase,
            stats: {
              ...prev.stats,
              totalMessages: (prev.stats?.totalMessages || 0) + 1,
              botMessages: (prev.stats?.botMessages || 0) + 1
            }
          };
          // CRITICAL: Update ref to keep it in sync, preserving ctaPostSharing and currentCTAScript
          conversationStateRef.current = {
            ...conversationStateRef.current,
            ...updated,
            // Ensure we don't lose ctaPostSharing or currentCTAScript
            ctaPostSharing: updated.ctaPostSharing || conversationStateRef.current.ctaPostSharing,
            currentCTAScript: updated.currentCTAScript || conversationStateRef.current.currentCTAScript
          };
          return updated;
        });
        
        // Natural typing-cadence delay before the NEXT bubble. Backend supplies
        // `delayBeforeMs` per message (sized to word-count + hasPhoto). Fall
        // back to a local word-count formula if the field is missing.
        if (index < pendingCTAMessages.length - 1) {
          const nextMsg = pendingCTAMessages[index + 1];
          const nextDelay = (typeof nextMsg?.delayBeforeMs === 'number' && nextMsg.delayBeforeMs > 0)
            ? nextMsg.delayBeforeMs
            : computeNaturalDelay(nextMsg?.content || '', !!nextMsg?.photo);
          setTimeout(() => {
            sendNextCTAMessage(index + 1);
          }, nextDelay);
        } else {
          setIsSendingCTA(false);
          setPendingCTAMessages([]);
        }
      };
      
      // Start sending messages after a short delay
      setTimeout(() => {
        sendNextCTAMessage(0);
      }, 500);
    }
  }, [pendingCTAMessages, isSendingCTA]);

  // Clear conversation when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetConversation();
    }
  }, [isOpen, resetConversation]);

  const sendMessage = async () => {
    if (!userMessage.trim() || isLoading) return;

    const userMsg = userMessage.trim();
    setUserMessage('');
    
    // Add user message to UI immediately
    const newUserMessage = {
      messageId: `user_${Date.now()}`,
      sender: 'user',
      content: userMsg,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    
    // Update stats when user message is added
    setConversationState(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        totalMessages: (prev.stats?.totalMessages || 0) + 1,
        userMessages: (prev.stats?.userMessages || 0) + 1
      }
    }));
    
    setIsLoading(true);

    try {
      // Sanitize modelData to remove sensitive fields before sending
      // The systemPrompt is generated dynamically on the backend, so we don't need to send it
      const { systemPrompt, ...safeModelData } = formData;
      
      // CRITICAL: Use ref to get the latest state (React state updates are async)
      // This ensures we always use the most recent state, even if setState hasn't applied yet
      const latestState = conversationStateRef.current;
      
      // CRITICAL: Ensure we have valid state objects (defensive check)
      const safeState = {
        ...latestState,
        currentCTAScript: latestState?.currentCTAScript || {
          scriptNumber: 0,
          currentMessageNumber: 0,
          totalAttempts: 0
        },
        ctaPostSharing: latestState?.ctaPostSharing || {
          linkShared: false,
          sharedAt: null,
          followUpCount: 0,
          maxFollowUps: 3,
          conversionChecked: false
        }
      };
      
      // CRITICAL: Log the state being sent to backend for debugging
      // This will show in browser console, not backend logs
      
      // Payload will be automatically obfuscated by the axios interceptor
      
      const payload = {
        modelData: safeModelData,
        conversationHistory: {
          conversationPhase: safeState.conversationPhase,
          currentDay: safeState.currentDay,
          currentPhaseNumber: safeState.currentPhaseNumber,
          totalExchanges: safeState.totalExchanges || safeState.exchangeCount || 0,
          exchangesInCurrentPhase: safeState.exchangesInCurrentPhase || 0,
          exchangeCount: safeState.exchangeCount || safeState.totalExchanges || 0, // For backward compatibility
          ctaTransitionReady: safeState.ctaTransitionReady || false,
          // CRITICAL: Include CTA script tracking state (must include updated currentMessageNumber)
          currentCTAScript: {
            scriptNumber: safeState.currentCTAScript.scriptNumber ?? 0,
            currentMessageNumber: safeState.currentCTAScript.currentMessageNumber ?? 0,
            totalAttempts: safeState.currentCTAScript.totalAttempts ?? 0
          },
          // CRITICAL: Include CTA post-sharing state (must include updated linkShared status)
          ctaPostSharing: {
            linkShared: safeState.ctaPostSharing.linkShared ?? false,
            sharedAt: safeState.ctaPostSharing.sharedAt ?? null,
            followUpCount: safeState.ctaPostSharing.followUpCount ?? 0,
            maxFollowUps: safeState.ctaPostSharing.maxFollowUps ?? 3,
            conversionChecked: safeState.ctaPostSharing.conversionChecked ?? false
          },
          // simp_converter intent-aware fields — must round-trip or the AI
          // re-pitches the page on every turn and the user-type classifier
          // re-runs unnecessarily.
          ctaSeedPendingReply: safeState.ctaSeedPendingReply ?? false,
          ctaDeclineCount: safeState.ctaDeclineCount ?? 0,
          ctaLastDeclinedAt: safeState.ctaLastDeclinedAt ?? null,
          userType: safeState.userType ?? 'unknown',
          userTypeUpdatedAt: safeState.userTypeUpdatedAt ?? null,
          userTypeMessageCount: safeState.userTypeMessageCount ?? 0,
          picRequestCount: safeState.picRequestCount ?? 0,
          picRequestLastAt: safeState.picRequestLastAt ?? null,
          messages: [...messages, newUserMessage],
          photosUsed: conversationState.photosUsed
        },
        userMessage: userMsg
      };
      
      const response = await api.post('/models/test-response', payload);

      if (response.data.success) {
        // Backend may return CTA messages in two shapes:
        //   (legacy)  { multipleMessages: true, messages: [...] }
        //   (current) { multipleMessages: { messages: [...], totalMessages } }
        // Normalize both before deciding which branch to take.
        const mm = response.data.multipleMessages;
        const ctaMessages = Array.isArray(response.data.messages)
          ? response.data.messages
          : (mm && Array.isArray(mm.messages) ? mm.messages : null);
        const hasCtaMessages = !!(ctaMessages && ctaMessages.length > 0);

        if (hasCtaMessages) {
          // CRITICAL: Update conversation state FIRST, before setting pending messages
          // This ensures the ref is updated before any other code runs
          const updatedState = response.data.conversation;
          
          // CRITICAL: Ensure we have all required fields with proper defaults
          // Don't use defaults that would overwrite the updated values!
          const completeState = {
            ...updatedState,
            // Only use defaults if the field is completely missing, not if it's falsy
            currentCTAScript: updatedState.currentCTAScript ? {
              scriptNumber: updatedState.currentCTAScript.scriptNumber ?? 0,
              currentMessageNumber: updatedState.currentCTAScript.currentMessageNumber ?? 0,
              totalAttempts: updatedState.currentCTAScript.totalAttempts ?? 0
            } : {
              scriptNumber: 0,
              currentMessageNumber: 0,
              totalAttempts: 0
            },
            ctaPostSharing: updatedState.ctaPostSharing ? {
              linkShared: updatedState.ctaPostSharing.linkShared ?? false,
              sharedAt: updatedState.ctaPostSharing.sharedAt ?? null,
              followUpCount: updatedState.ctaPostSharing.followUpCount ?? 0,
              maxFollowUps: updatedState.ctaPostSharing.maxFollowUps ?? 3,
              conversionChecked: updatedState.ctaPostSharing.conversionChecked ?? false
            } : {
              linkShared: false,
              sharedAt: null,
              followUpCount: 0,
              maxFollowUps: 3,
              conversionChecked: false
            }
          };
          
          // CRITICAL: Update ref FIRST (synchronously, immediately)
          conversationStateRef.current = completeState;
          
          // Then update React state (async, but ref is already set)
          setConversationState(completeState);
          
          // Store CTA messages AFTER state is updated
          setPendingCTAMessages(ctaMessages);
        } else {
          // Single message response (normal conversation)
          const botMessage = {
            messageId: `bot_${Date.now()}`,
            sender: 'bot',
            content: response.data.response.content,
            timestamp: new Date(),
            photosShared: response.data.response.photo ? [response.data.response.photo] : []
          };

          setMessages(prev => [...prev, botMessage]);

          // CRITICAL: Update BOTH the React state AND the ref.
          // The ref is the source of truth for the *next* outgoing request
          // (see `latestState = conversationStateRef.current` above). If we only
          // update React state, the ref stays stale and on the next turn the
          // backend receives the old `currentCTAScript.currentMessageNumber`,
          // which causes the CTA seed message to fire forever.
          const newState = response.data.conversation || {};
          const mergedState = {
            ...newState,
            currentCTAScript: newState.currentCTAScript || conversationStateRef.current?.currentCTAScript || {
              scriptNumber: 0,
              currentMessageNumber: 0,
              totalAttempts: 0
            },
            ctaPostSharing: newState.ctaPostSharing || conversationStateRef.current?.ctaPostSharing || {
              linkShared: false,
              sharedAt: null,
              followUpCount: 0,
              maxFollowUps: 3,
              conversionChecked: false
            }
          };
          conversationStateRef.current = mergedState;
          setConversationState(mergedState);
        }
      } else {
        toast.error('Failed to generate response');
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      toast.error('Error generating response: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Conversation Progress Line Component (matching Accounts.js)
  const ConversationProgressLine = ({ conversation }) => {
    // Build phases array dynamically based on model configuration
    const numberOfPhases = formData.conversationPhases?.numberOfPhases || 2;
    const enablePostCTAPhases = formData.ctaPhase?.enablePostCTAPhases || false;
    
    const phases = ['initial'];
    
    // Add regular phases (1-5)
    for (let i = 1; i <= numberOfPhases; i++) {
      phases.push(`phase_${i}`);
    }
    
    // Add CTA phase
    phases.push('cta');
    
    // Add post-CTA phases if enabled
    if (enablePostCTAPhases) {
      phases.push('phase_6', 'phase_7');
    }
    
    const currentPhaseIndex = phases.indexOf(conversation.conversationPhase);
    const progress = currentPhaseIndex >= 0 ? ((currentPhaseIndex + 1) / phases.length) * 100 : 0;

    const getPhaseGradient = (phase) => {
      switch (phase) {
        case 'initial': return 'from-blue-500 to-blue-600';
        case 'phase_1': return 'from-blue-500 via-yellow-500 to-yellow-600';
        case 'phase_2': return 'from-blue-500 via-yellow-500 via-orange-500 to-orange-600';
        case 'phase_3': return 'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 to-pink-600';
        case 'phase_4': return 'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 via-purple-500 to-purple-600';
        case 'phase_5': return 'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 via-purple-500 via-indigo-500 to-indigo-600';
        case 'cta': return 'from-blue-500 via-yellow-500 via-orange-500 via-red-500 to-red-600';
        case 'phase_6': return 'from-blue-500 via-yellow-500 via-orange-500 via-red-500 via-rose-500 to-rose-600';
        case 'phase_7': return 'from-blue-500 via-yellow-500 via-orange-500 via-red-500 via-rose-500 via-fuchsia-500 to-fuchsia-600';
        default: return 'from-gray-500 to-gray-600';
      }
    };

    const getPhaseLabel = (phase) => {
      switch (phase) {
        case 'initial': return 'Initial';
        case 'phase_1': return 'P1';
        case 'phase_2': return 'P2';
        case 'phase_3': return 'P3';
        case 'phase_4': return 'P4';
        case 'phase_5': return 'P5';
        case 'cta': return 'CTA';
        case 'phase_6': return 'P6';
        case 'phase_7': return 'P7';
        default: return phase.replace('phase_', 'P');
      }
    };

    return (
      <div className="w-full h-6 progress-bg rounded-full overflow-hidden relative">
        {/* Background phase labels - always visible */}
        <div className="absolute inset-0 flex items-center justify-between px-2">
          {phases.map((phase, index) => (
            <span 
              key={phase}
              className={`text-xs font-medium transition-colors duration-300 ${
                index <= currentPhaseIndex 
                  ? 'text-white drop-shadow-sm' 
                  : 'text-muted'
              }`}
            >
              {getPhaseLabel(phase)}
            </span>
          ))}
        </div>
        
        {/* Progress fill */}
        <div
          className={`h-full bg-gradient-to-r ${getPhaseGradient(conversation.conversationPhase)} transition-all duration-500 ease-out`}
          style={{ width: `${progress}%` }}
        >
          <div className="absolute inset-0 flex items-center justify-between px-2">
            {phases.map((phase, index) => (
              <span 
                key={phase}
                className={`text-xs font-medium transition-colors duration-300 ${
                  index <= currentPhaseIndex 
                    ? 'text-white drop-shadow-sm' 
                    : 'text-transparent'
                }`}
              >
                {getPhaseLabel(phase)}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Test Model: {formData.name || 'Untitled Model'}
                </h3>
                <div className="mt-1 flex items-center space-x-4">
                  <span className="text-sm text-blue-600 font-medium">
                    Model: {formData.name || 'Unknown'}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Progress Line */}
            <div className="mb-4">
              <ConversationProgressLine conversation={conversationState} />
            </div>
            
            <div className="mb-4 flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Phase:</span>
                <span className={`badge ${
                  conversationState.conversationPhase === 'initial' ? 'badge-gray' :
                  conversationState.conversationPhase === 'phase_1' ? 'badge-blue' :
                  conversationState.conversationPhase === 'phase_2' ? 'badge-yellow' :
                  conversationState.conversationPhase === 'cta' ? 'badge-green' :
                  'badge-purple'
                }`}>
                  {conversationState.conversationPhase}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Day:</span>
                <span className="badge badge-gray">{conversationState.currentDay}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Exchanges:</span>
                <span className="badge badge-gray">{conversationState.totalExchanges || conversationState.exchangeCount || 0}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mb-4 flex items-center space-x-2">
              <button
                onClick={resetConversation}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                Reset
              </button>
              <button
                onClick={() => {
                  onEditModel();
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit Model
              </button>
            </div>

            {/* Conversation Messages */}
            <div className="border rounded-2xl p-4 max-h-96 overflow-y-auto mb-4">
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    Start a conversation to test your model...
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div key={message.messageId || index} className={`flex ${message.sender === 'bot' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                        message.sender === 'bot' 
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                      }`}>
                        <div className="text-sm font-medium mb-1">
                          {message.sender === 'bot' ? (formData.name || 'Bot') : 'You'}
                        </div>
                        
                        <div className="text-sm">{message.content}</div>
                        
                        {/* Photo display */}
                        {message.photosShared && message.photosShared.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {message.photosShared.map((photo, photoIndex) => (
                              <div key={photoIndex} className="relative">
                                <img 
                                  src={resolveAssetUrl(
                                    photo.url || `/uploads/models/${photo.photoId}.jpg`,
                                    photo.displayUrl,
                                    serviceUrls.automation
                                  )} 
                                  alt={`Shared ${photoIndex + 1}`}
                                  className="w-full h-32 object-cover rounded-2xl"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                  📸 {photo.category || 'Photo'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-xs opacity-75 mt-1">
                          {new Date(message.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {(isLoading || isSendingCTA) && (
                  <div className="flex justify-start">
                    <div className="bg-blue-100 dark:bg-blue-900/30 px-4 py-2 rounded-2xl">
                      <div className="text-sm text-blue-900 dark:text-blue-100">
                        {isSendingCTA 
                          ? `${formData.name || 'Bot'} is sending CTA messages...`
                          : `${formData.name || 'Bot'} is typing...`
                        }
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message Count Stats */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{conversationState.stats?.totalMessages || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Messages</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{conversationState.stats?.botMessages || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Bot Messages</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{conversationState.stats?.userMessages || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">User Messages</p>
              </div>
            </div>

            {/* Message Input */}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 form-input"
                disabled={isLoading || isSendingCTA}
              />
              <button
                onClick={sendMessage}
                disabled={!userMessage.trim() || isLoading || isSendingCTA}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Models;